import { Component, HostListener, OnDestroy, OnInit, signal, computed, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PassportDetailsStore, type PassportDetails as PassportDetailsModel } from '../shared/passport-details.store';
import { PassportService } from '../shared/passport.service';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { OfflineHistoryService } from '../shared/offline-history.service';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { HeaderComponent } from '../header/header.component';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { ImageLoadHelperService } from '../shared/image-load-helper.service';
import { AuthorizerSyncService } from '../shared/authorizer-sync.service';
import { ScrollPositionService } from '../shared/scroll-position.service';
import { FilterService } from '../shared/filter.service';
import { SearchService } from '../shared/search.service';
import {
  clearReceiverListRestoreState,
  hasReceiverListSnapshot,
  leaveReceiverPersonaListState,
  parseReceiverListSnapshot,
  RECEIVER_EXPECT_RESTORE_KEY,
  saveReceiverListSnapshot
} from '../receiver/receiver-list-state.storage';
import {
  clearShuntingListRestoreState,
  hasShuntingListSnapshot,
  leaveShuntingPersonaListState,
  SHUNTING_EXPECT_RESTORE_KEY
} from '../shunting/shunting-list-state.storage';
import {
  clearGatehouseListRestoreState,
  GATEHOUSE_EXPECT_RESTORE_KEY,
  hasGatehouseListSnapshot,
  leaveGatehousePersonaListState,
  parseGatehouseListSnapshot,
  saveGatehouseListSnapshot,
  type GatehouseSchedulerTab
} from '../gatehouse/gatehouse-list-state.storage';
import {
  clearSchedulerListRestoreState,
  hasSchedulerListSnapshot,
  leaveSchedulerPersonaListState,
  SCHEDULER_EXPECT_RESTORE_KEY
} from '../scheduler/scheduler-list-state.storage';
import {
  clearTransloadingListRestoreState,
  hasTransloadingListSnapshot,
  leaveTransloadingPersonaListState,
  TRANSLOADING_EXPECT_RESTORE_KEY
} from '../transloading/transloading-list-state.storage';
import {
  clearGrowersListRestoreState,
  GROWERS_EXPECT_RESTORE_KEY,
  hasGrowersListSnapshot,
  leaveGrowersPersonaListState
} from '../growers/growers-list-state.storage';
import {
  AUDIT_EXPECT_RESTORE_KEY,
  clearAuditListRestoreState,
  hasAuditListSnapshot,
  leaveAuditPersonaListState
} from '../audit/audit-list-state.storage';
import { ToastService } from '../shared/toast.service';
import { UsersService } from '../shared/users.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import type { RoleUserMappingsResponse } from '../interfaces/api-responses';
import { PassportHelperService } from '../shared/passport-helper.service';

type OutgoingPassportSubmission = { data: Partial<PassportDetailsModel> };

function isOutgoingPassportSubmission(value: unknown): value is OutgoingPassportSubmission {
  return typeof value === 'object' && value !== null && 'data' in value;
}

type PersonaExcept =
  | 'receiver'
  | 'shunting'
  | 'gatehouse'
  | 'transloading'
  | 'growers'
  | 'audit'
  | 'scheduler';

type PersonaRouteSessionMode = 'back' | 'access-denied' | 'modal-home';

interface TargetRouteSessionEntry {
  except: PersonaExcept;
  storageKey: string;
  clearSelf: () => void;
}

interface PersonaOriginFlags {
  receiver: boolean;
  shunting: boolean;
  gatehouse: boolean;
  grower: boolean;
  transloader: boolean;
  scheduler: boolean;
  audit: boolean;
}

export type OutgoingIndexedDbSaveOutcome = 'saved' | 'read_failed' | 'blocked_no_haccp' | 'error';

@Component({
  selector: 'app-passport-details-page',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingSpinnerComponent, HeaderComponent, DateWithOrdinalPipe],
  templateUrl: './passport-details.page.html',
  styleUrl: './passport-details.page.css'
})
export class PassportDetailsPage implements OnInit, OnDestroy {
  private static readonly PERSONA_ROLE_MAP: Record<string, number> = {
    Transloader: 3, Scheduler: 6, Gatehouse: 7
  };

  /** Order matches the explicit clear sequences used before refactor (deterministic). */
  private static readonly PERSONA_EXCEPT_CLEAR_ORDER: readonly PersonaExcept[] = [
    'receiver',
    'shunting',
    'gatehouse',
    'transloading',
    'growers',
    'audit',
    'scheduler'
  ];

  private static readonly TARGET_ROUTE_SESSION_CONFIG: Record<string, TargetRouteSessionEntry> = {
    '/receiver': {
      except: 'receiver',
      storageKey: RECEIVER_EXPECT_RESTORE_KEY,
      clearSelf: clearReceiverListRestoreState
    },
    '/shunting': {
      except: 'shunting',
      storageKey: SHUNTING_EXPECT_RESTORE_KEY,
      clearSelf: clearShuntingListRestoreState
    },
    '/gatehouse': {
      except: 'gatehouse',
      storageKey: GATEHOUSE_EXPECT_RESTORE_KEY,
      clearSelf: clearGatehouseListRestoreState
    },
    '/scheduler': {
      except: 'scheduler',
      storageKey: SCHEDULER_EXPECT_RESTORE_KEY,
      clearSelf: clearSchedulerListRestoreState
    },
    '/growers': {
      except: 'growers',
      storageKey: GROWERS_EXPECT_RESTORE_KEY,
      clearSelf: clearGrowersListRestoreState
    },
    '/transloading': {
      except: 'transloading',
      storageKey: TRANSLOADING_EXPECT_RESTORE_KEY,
      clearSelf: clearTransloadingListRestoreState
    },
    '/audit': {
      except: 'audit',
      storageKey: AUDIT_EXPECT_RESTORE_KEY,
      clearSelf: clearAuditListRestoreState
    }
  };

  id = '';
  persona: 'Grower' | 'Transloader' | 'Scheduler' | 'Gatehouse' | 'Receiver' | 'Shunting' | 'Audit' = 'Grower';
  submitModalVisible = false;
  private hasDraft = false;
  /** Maps blob: object URLs back to their proxy paths for image deletion. */
  private readonly proxyUrlMap = new Map<string, string>();
  /** Caches raw Blob objects so the preview modal can create fresh blob: URLs after the thumbnails' URLs are revoked. */
  private readonly imageBlobCache = new Map<string, Blob>();
  private imageLoadInProgress = false;
  private lastLoadedLinksKey = '';
  /** Temporary blob: URL for the preview modal image (revoked when preview closes). */
  previewImageSrc: string | null = null;
  schedulerEditEnabled = false;
  private schedulerBaseline: Partial<PassportDetailsModel> | null = null;
  isLoading = signal(false);
  accessDenied = signal(false);
  isAddingAuthorizer = signal(false);
  isSavingDraft = signal(false);
  isCompletingReceiving = signal(false);

  /** Full-screen blocking overlay while loading passport data or saving a draft. */
  blockingOverlayVisible = computed(
    () => this.isLoading() || this.isSavingDraft() || this.isCompletingReceiving()
  );

  /** Overlay caption: draft save takes precedence over generic loading. */
  blockingOverlayText = computed(() => {
    if (this.isSavingDraft()) return 'Saving draft…';
    if (this.isCompletingReceiving()) return 'Completing…';
    return 'Loading…';
  });

  // Flag to prevent re-capturing original snapshot on subsequent updates
  private originalSnapshotCaptured = false;

  // Flag to track if the user explicitly picked an authorizer in this session
  private authorisedByUserPicked = false;

  /**
   * Plant id last used when Scheduler authorizer options were loaded for a transferred load.
   * The vendor number in the store can briefly lag behind the list; caching avoids POSTing
   * { passportId, roleId: 2 } without plant scope (which fails on blank passports).
   */
  private transferredDestinationPlantIdCache = 0;

  // Constant for "No Origin Field" display text
  private readonly NO_ORIGIN_FIELD_TEXT = 'No Origin Field';

  private getPersonaId(persona: string): number {
    const roleMap: Record<string, number> = {
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7,
      'audit': 8,
      'auditor': 8
    };
    const normalized = (persona || '').toLowerCase();
    return roleMap[normalized] || 2; // Default to Grower
  }

  /**
   * Helper to safely get plantID with fallback to PlantID
   */
  private getPlantID(data: any): any {
    return data?.plantID || data?.PlantID;
  }

  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly historyPanelService = inject(HistoryPanelService);
  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly imageLoadHelper = inject(ImageLoadHelperService);
  private readonly offlineHistoryService = inject(OfflineHistoryService);
  private readonly scrollPositionService = inject(ScrollPositionService);
  private readonly filterService = inject(FilterService);
  private readonly searchService = inject(SearchService);
  private readonly toastService = inject(ToastService);
  private readonly helper = inject(PassportHelperService);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public store: PassportDetailsStore,
    private readonly passports: PassportService,
    private readonly ui: UiStateService,
    private readonly http: HttpClient,
    private readonly offlineStorage: OfflineStorageService,
    private readonly authorizerSync: AuthorizerSyncService,
    private readonly usersService: UsersService
  ) {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    // Initialize persona from storage or route state
    try {
      // Check route state first (passed from persona components)
      const state = history?.state as Record<string, unknown> | undefined;
      const fromRoute = (state?.['from'] as string || '').toLowerCase();
      
      // Use route state if available, otherwise fall back to localStorage
      const p = fromRoute || (localStorage.getItem('persona') || '').toLowerCase();
      
      if (p.includes('transload')) this.persona = 'Transloader';
      else if (p.includes('gate')) this.persona = 'Gatehouse';
      else if (p.includes('schedule')) this.persona = 'Scheduler';
      else if (p.includes('audit')) this.persona = 'Audit';
      else if (p.includes('receiv')) this.persona = 'Receiver';
      else if (p.includes('shunting') || p.includes('shunt')) this.persona = 'Shunting';
      else if (p.includes('grower')) this.persona = 'Grower';
      
      // Log for debugging
      console.log(`PassportDetails: Detected persona='${this.persona}' from route='${fromRoute}' or localStorage='${localStorage.getItem('persona')}'`);
    } catch {
      // Ignore errors parsing persona from path
    }
  }

  /**
   * SessionStorage key used to detect returning from mobile Comments/History panel.
   * When set, ngOnInit skips destructive re-initialization so in-progress edits
   * (HACCP images, loading date/time, origin, bulker number, authorised by) are preserved.
   */
  private getMobilePanelNavKey(): string {
    return `mobilePanelNav_${this.id}`;
  }

  /** SessionStorage key for component-local state that is lost when the component is destroyed. */
  private getMobilePanelStateKey(): string {
    return `mobilePanelState_${this.id}`;
  }

  /**
   * Save component-local state to sessionStorage before navigating to mobile Comments/History.
   * The PassportDetailsStore (singleton) preserves store-backed fields automatically,
   * but these component-local fields are lost when Angular destroys the component.
   */
  private saveMobilePanelState(): void {
    const state: Record<string, unknown> = {
      persona: this.persona,
      selectedShuntingYard: this.selectedShuntingYard,
      selectedShuntingYardId: this.selectedShuntingYardId,
      isShuntingRequired: this.isShuntingRequired,
      schedulerEditEnabled: this.schedulerEditEnabled,
      reasonText: this.reasonText
    };
    sessionStorage.setItem(this.getMobilePanelStateKey(), JSON.stringify(state));
  }

  /**
   * Restore component-local state saved before mobile Comments/History navigation.
   * Returns true if state was found and restored, false otherwise.
   */
  private restoreMobilePanelState(): boolean {
    const raw = sessionStorage.getItem(this.getMobilePanelStateKey());
    sessionStorage.removeItem(this.getMobilePanelStateKey());
    if (!raw) return false;
    try {
      const state = JSON.parse(raw) as Record<string, unknown>;
      if (state['persona']) this.persona = state['persona'] as typeof this.persona;
      if (state['selectedShuntingYard'] !== undefined) this.selectedShuntingYard = state['selectedShuntingYard'] as string;
      if (state['selectedShuntingYardId'] !== undefined) this.selectedShuntingYardId = state['selectedShuntingYardId'] as number | null;
      if (state['isShuntingRequired'] !== undefined) this.isShuntingRequired = state['isShuntingRequired'] as boolean;
      if (state['schedulerEditEnabled'] !== undefined) this.schedulerEditEnabled = state['schedulerEditEnabled'] as boolean;
      if (state['reasonText'] !== undefined) this.reasonText = state['reasonText'] as string;
      return true;
    } catch {
      return false;
    }
  }

  ngOnInit(): void {
    const isReturningFromPanel = sessionStorage.getItem(this.getMobilePanelNavKey()) === 'true';
    if (isReturningFromPanel) {
      sessionStorage.removeItem(this.getMobilePanelNavKey());
      // Restore component-local state (persona, shunting yard, scheduler edit mode)
      this.restoreMobilePanelState();
      // Store already has user's in-progress edits -- skip destructive reload.
      // Only reload dropdown option lists (origins, authorizers, shunting yards).
      void this.initializeDropdownOptionsOnly();
      return;
    }
    void this.initializeAfterOutgoingSnapshot();
  }

  /** Restore outgoing/draft IndexedDB snapshot before init so offline images are not wiped. */
  private async initializeAfterOutgoingSnapshot(): Promise<void> {
    await this.loadSnapshotByStatus();
    void this.initializeComponent();
  }

  get isGrower(): boolean {
    if (this.isActualAudit) return false;
    const fromStorage = localStorage.getItem('persona')?.toLowerCase() || '';
    const current = this.persona?.toLowerCase() || '';
    const p = (fromStorage.includes('schedule') || fromStorage.includes('audit')) ? current : (fromStorage || current);
    return p.includes('grower');
  }

  get isTransloader(): boolean {
    if (this.isActualAudit) return false;
    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const current = (this.persona || '').toLowerCase();
    const p = (fromStorage.includes('schedule') || fromStorage.includes('audit')) ? current : (fromStorage || current);
    return p.includes('transload');
  }

  get isGrowerOrTransloader(): boolean {
    return this.isGrower || this.isTransloader;
  }

  /** Logged-in grower/transloader only — not Scheduler/Audit viewing as Grower/Transloader. */
  private get isActualGrowerOrTransloaderUser(): boolean {
    const loggedInPersona = (localStorage.getItem('persona') || '').toLowerCase();
    return loggedInPersona.includes('grower') || loggedInPersona.includes('transload');
  }

  get canAddAuthorizer(): boolean {
    return this.isGrowerOrTransloader || (this.isTransferredLoad && this.persona === 'Scheduler');
  }

  get isDesktopMode(): boolean {
    return globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
  }

  get isScheduler(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('schedule')) return true;

    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const fromRoute = (this.persona || '').toLowerCase();
    return fromStorage.includes('schedule') || fromRoute.includes('schedule');
  }

  private get isActualAudit(): boolean {
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('audit')) return true;
    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    return fromStorage.includes('audit');
  }

  get isAudit(): boolean {
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('audit')) return true;

    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const fromRoute = (this.persona || '').toLowerCase();
    return fromStorage.includes('audit') || fromRoute.includes('audit');
  }

  get isGatehouse(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('gate')) return true;

    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const fromRoute = (this.persona || '').toLowerCase();
    return fromStorage.includes('gate') || fromRoute.includes('gate');
  }

  get isReceiver(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('receiv')) return true;

    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const fromRoute = (this.persona || '').toLowerCase();
    return fromStorage.includes('receiv') || fromRoute.includes('receiv');
  }

  /** Receiver view: show Complete when load is in RECEIVED_BY_RECEIVER and not yet marked receiving-complete. */
  get canCompleteReceiving(): boolean {
    if (this.isActualAudit) return false;
    if (!this.isReceiver || !this.isView) return false;
    const row = this.store.get(this.id);
    if (!row) return false;
    const code = (row.statusCode || '').toUpperCase();
    if (code !== 'RECEIVED_BY_RECEIVER') return false;
    if (row.isReceivingCompleted === true) return false;
    if (row.isCancelled === true) return false;
    const loadStatus = (row.loadStatusName || '').toLowerCase();
    if (loadStatus === 'cancelled') return false;
    return true;
  }

  async onCompleteReceiving(): Promise<void> {
    if (!this.canCompleteReceiving || this.isCompletingReceiving()) {
      return;
    }
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    if (!token) {
      this.toastService.warning('You must be signed in to complete receiving.', 5000);
      return;
    }
    this.isCompletingReceiving.set(true);
    try {
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passportorder/receiving-complete/${this.id}`;
      const headers = {
        Authorization: `${tokenType} ${token}`,
        'Content-Type': 'application/json',
      };
      await firstValueFrom(
        this.http.post<{ success?: boolean }>(apiUrl, {}, { headers, withCredentials: true })
      );
      this.store.update(this.id, { isReceivingCompleted: true });
      this.passports.setStatus(this.id, 'completed', true);
      this.ui.setStatusSelected('completed');
      this.updateReceiverReturnTab('completed');
      this.toastService.success('Receiving marked complete.', 4000);
    } catch (error: unknown) {
      const status =
        error !== null &&
        typeof error === 'object' &&
        'status' in error &&
        typeof (error as { status?: unknown }).status === 'number'
          ? (error as { status: number }).status
          : undefined;
      if (status === 403) {
        this.toastService.error('You are not authorized to complete receiving for this load.', 5000);
      } else if (status === 400) {
        this.toastService.error('This load cannot be marked complete at this time.', 5000);
      } else {
        this.toastService.error('Failed to complete receiving. Please try again.', 5000);
      }
    } finally {
      this.isCompletingReceiving.set(false);
    }
  }

  private updateReceiverReturnTab(target: 'pending' | 'outgoing' | 'completed'): void {
    const snapshot = parseReceiverListSnapshot();
    if (!snapshot) {
      return;
    }
    saveReceiverListSnapshot({ ...snapshot, tab: target });
  }

  get isShunting(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('shunting') || from.toLowerCase().includes('shunt')) return true;

    const fromStorage = (localStorage.getItem('persona') || '').toLowerCase();
    const fromRoute = (this.persona || '').toLowerCase();
    return fromStorage.includes('shunting') || fromStorage.includes('shunt') ||
           fromRoute.includes('shunting') || fromRoute.includes('shunt');
  }

  get isDesktopView(): boolean {
    return globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
  }

  get isReceivingTab(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const fromTab = (state?.['tab'] as string | undefined) || '';
    const uiTab = this.ui.schedulerTabSelected ? this.ui.schedulerTabSelected() : '';
    const tab = (fromTab || uiTab || '').toLowerCase();
    return tab === 'receiving';
  }

  get isTransferredTab(): boolean {
    if (this.isActualAudit) return false;
    const state = history?.state as Record<string, unknown> | undefined;
    const fromTab = (state?.['tab'] as string | undefined) || '';
    const uiTab = this.ui.schedulerTabSelected ? this.ui.schedulerTabSelected() : '';
    const tab = (fromTab || uiTab || '').toLowerCase();
    return tab === 'transferred';
  }

  get isTransferredLoad(): boolean {
    return this.isTransferredTab || !!this.store.get(this.id)?.transferFlag;
  }

  /**
   * Parses a destination plant id from a vendor number string (e.g. "IS0120" -> 120).
   */
  private parseDestinationPlantIdFromVendorNumber(vendorNumber: string | undefined): number {
    if (!vendorNumber?.trim()) return 0;
    const afterIs = /IS\s*(\d+)/i.exec(vendorNumber);
    const digits = afterIs?.[1] ?? vendorNumber.replaceAll(/\D/g, '');
    const plantId = Number.parseInt(digits, 10);
    return Number.isFinite(plantId) && plantId > 0 ? plantId : 0;
  }

  /**
   * Pure resolution for transferred-load destination plant (no cache writes).
   * Order: vendor number, passport store plantID, then session cache from the last
   * successful authorizer-options load.
   */
  private resolveTransferredDestinationPlantId(): number {
    const row = this.store.get(this.id);
    const fromVendor = this.parseDestinationPlantIdFromVendorNumber(row?.vendorNumber);
    if (fromVendor > 0) return fromVendor;

    const fromStore = row?.plantID;
    if (typeof fromStore === 'number' && Number.isFinite(fromStore) && fromStore > 0) {
      return fromStore;
    }

    return Math.max(0, this.transferredDestinationPlantIdCache);
  }

  /**
   * Destination plant id for transferred-load Scheduler authorizer APIs (plant-scoped add).
   */
  private getTransferredPlantId(): number {
    return this.resolveTransferredDestinationPlantId();
  }

  get isShuntingYardLocked(): boolean {
    return this.isDesktopView && (this.isGatehouse || this.isScheduler) && this.isReceivingTab;
  }

  get isTransferredReadonly(): boolean {
    return this.isTransferredLoad && this.persona === 'Scheduler' && !this.schedulerEditEnabled;
  }

  get isOriginLockedForTransfer(): boolean {
    return this.isTransferredLoad;
  }

  /**
   * Get the effective image count based on current editing state
   * Online: imageCount (backend) - deleted images + added images
   * Offline: displayed images + placeholders (added images already in displayed)
   */
  private getEffectiveImageCount(): number {
    const data = this.store.get(this.id);
    if (!data) return 0;

    const isOffline = !navigator.onLine;
    
    if (isOffline) {
      // OFFLINE: Count what's visually present
      // Note: Newly added images are already in haccpImages as dataURLs, 
      // so we DON'T need to count haccpImageFiles separately
      const images = data.haccpImages || [];
      
      // Count real images (not placeholders) - includes both backend images AND newly added dataURLs
      const realImageCount = images.filter((img: string) => 
        img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')
      ).length;
      
      // Count placeholders (represent backend images when offline)
      const placeholderCount = images.filter((img: string) => 
        img?.startsWith('PLACEHOLDER:')
      ).length;
      
      return realImageCount + placeholderCount;
    } else {
      // ONLINE: Calculate from backend imageCount + edits
      const backendImageCount = data.imageCount || 0;
      const deletedCount = (data.deletedImageFilenames || []).length;
      const fileCount = data.haccpImageFiles?.length ?? 0;
      // Outgoing IndexedDB snapshots keep data URLs without File objects (iOS)
      const dataUrlCount = (data.haccpImages ?? []).filter(
        (img: string) => img?.startsWith('data:')
      ).length;
      const addedCount = fileCount > 0 ? fileCount : dataUrlCount;
      const calculatedCount = backendImageCount - deletedCount + addedCount;
      
      // Fallback: If calculated count is 0 but we have loaded images, use actual image count
      // This handles cases where imageCount wasn't properly set in the store
      if (calculatedCount === 0 && (data.haccpImages || []).length > 0) {
        const actualImages = (data.haccpImages || []).filter((img: string) => 
          img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')
        );
        return actualImages.length;
      }
      
      return calculatedCount;
    }
  }

  /**
   * Dynamically calculated HACCP check value based on current editing state
   * Online: imageCount (backend) - deleted images + added images
   * Offline: displayed images + placeholders + added images
   */
  get computedHaccpCheck(): 'Yes' | 'No' {
    return this.getEffectiveImageCount() > 0 ? 'Yes' : 'No';
  }

  /**
   * Check if maximum images (3) are reached
   * Used to disable camera icon when limit is reached
   */
  get isUploadLimitReached(): boolean {
    return this.getEffectiveImageCount() >= 3;
  }

  // Display images from backend links only
  get displayedHaccpImages(): string[] {
    const data = this.store.get(this.id);
    const images = data?.haccpImages || [];
    // Filter out placeholders (empty strings or PLACEHOLDER: prefixed strings) - they will be shown as empty boxes in template
    return images.filter((img: string) => img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:'));
  }

  /**
   * Get count of empty image placeholders (for drafts when offline)
   * Only returns count when offline, otherwise returns 0
   */
  get emptyHaccpImagePlaceholders(): number {
    // Only show placeholders when offline
    if (navigator.onLine) {
      return 0;
    }
    
    const data = this.store.get(this.id);
    const images = data?.haccpImages || [];
    // Count empty strings or PLACEHOLDER: prefixed strings as placeholders
    return images.filter((img: string) => !img || img.trim().length === 0 || img.startsWith('PLACEHOLDER:')).length;
  }

  /**
   * Get array for ngFor to render empty placeholders
   */
  getEmptyPlaceholderArray(): number[] {
    const count = this.emptyHaccpImagePlaceholders;
    return new Array(count).fill(0).map((_, i) => i);
  }

  get formattedApn(): string {
    const details = this.store.get(this.id);
    if (!details) return '';
    
    return details.apn || '';
  }

  get mode(): 'view' | 'edit' {
    if (this.isActualAudit) return 'view';
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from.toLowerCase().includes('shunting') || from.toLowerCase().includes('shunt')) {
      return 'view';
    }
    if (from.toLowerCase().includes('audit')) {
      return 'view';
    }

    const currentPersona = (localStorage.getItem('persona') || this.persona || '').toLowerCase();
    const rec = this.passports.getById(this.id);
    const isPending = rec?.status === 'pending';
    
    return this.getModeForPersona(currentPersona, isPending, rec);
  }

  private getModeForPersona(persona: string, isPending: boolean, rec: any): 'view' | 'edit' {
    // Receiver and Shunting are always view-only
    if (persona.includes('receiv') || persona.includes('shunting') || persona.includes('shunt')) {
      return 'view';
    }
    // Gatehouse is always view-only
    if (persona.includes('gate')) {
      return 'view';
    }
    // Audit is always view-only (read-only access)
    if (persona.includes('audit')) {
      return 'view';
    }
    // Scheduler
    if (persona.includes('schedule')) {
      return isPending && this.schedulerEditEnabled ? 'edit' : 'view';
    }
    // Transloader
    if (persona.includes('transload')) {
      return this.getTransloaderMode(rec, isPending);
    }
    // Grower
    if (persona.includes('grower')) {
      return isPending ? 'edit' : 'view';
    }
    
    return 'view';
  }

  private getTransloaderMode(rec: any, isPending: boolean): 'view' | 'edit' {
    // If record is in completed tab, always view mode
    if (!isPending) {
      return 'view';
    }
    
    // If record is in pending tab, always edit mode
    return 'edit';
  }

  get isView(): boolean { return this.mode === 'view'; }
  get isEdit(): boolean { return this.mode === 'edit'; }

  /**
   * Helper to get route for persona string
   */
  private getRouteForPersona(persona: string): string {
    const p = (persona || '').toLowerCase();
    if (p.includes('transload')) return '/transloading';
    if (p.includes('grower')) return '/growers';
    if (p.includes('schedule')) return '/scheduler';
    if (p.includes('gate')) return '/gatehouse';
    if (p.includes('audit')) return '/audit';
    if (p.includes('receiv')) return '/receiver';
    if (p.includes('shunting') || p.includes('shunt')) return '/shunting';
    return '/profile';
  }

  private static readonly ALLOWED_BACK_ROUTES = new Set([
    '/transloading',
    '/growers',
    '/scheduler',
    '/gatehouse',
    '/audit',
    '/receiver',
    '/shunting',
    '/profile'
  ]);

  private getSafeBackRoute(route: string): string {
    if (PassportDetailsPage.ALLOWED_BACK_ROUTES.has(route)) {
      return route;
    }
    return '/profile';
  }

  private getPassportBackNavigationTarget(): { route: string; fromHint: string } {
    const state = history?.state as Record<string, unknown> | undefined;
    const from = (state?.['from'] as string | undefined) || '';
    if (from) {
      const route = this.getRouteForPersona(from);
      if (route !== '/profile') {
        return { route, fromHint: from };
      }
    }
    const p = localStorage.getItem('persona') || this.persona || '';
    return { route: this.getRouteForPersona(p), fromHint: p };
  }

  private clearAllPersonaListsExcept(except: PersonaExcept): void {
    const clears: Record<PersonaExcept, () => void> = {
      receiver: clearReceiverListRestoreState,
      shunting: clearShuntingListRestoreState,
      gatehouse: clearGatehouseListRestoreState,
      transloading: clearTransloadingListRestoreState,
      growers: clearGrowersListRestoreState,
      audit: clearAuditListRestoreState,
      scheduler: clearSchedulerListRestoreState
    };
    for (const key of PassportDetailsPage.PERSONA_EXCEPT_CLEAR_ORDER) {
      if (key !== except) {
        clears[key]();
      }
    }
  }

  private clearAllPersonaListRestoreStates(): void {
    clearReceiverListRestoreState();
    clearShuntingListRestoreState();
    clearGatehouseListRestoreState();
    clearTransloadingListRestoreState();
    clearGrowersListRestoreState();
    clearAuditListRestoreState();
    clearSchedulerListRestoreState();
  }

  private applyPersonaRouteSessionStorage(
    mode: PersonaRouteSessionMode,
    storageKey: string,
    clearSelf: () => void
  ): void {
    if (mode === 'modal-home') {
      clearSelf();
      this.scrollPositionService.setReturningFromDetail(false);
      return;
    }
    try {
      globalThis.sessionStorage?.setItem(storageKey, '1');
    } catch {
      // sessionStorage may be unavailable
    }
    if (mode === 'back') {
      this.scrollPositionService.setReturningFromDetail(false);
    }
  }

  private computePersonaOrigins(fromPersonaHint: string): PersonaOriginFlags {
    const h = fromPersonaHint.toLowerCase();
    return {
      receiver: h.includes('receiv') || hasReceiverListSnapshot(),
      shunting: h.includes('shunt') || hasShuntingListSnapshot(),
      gatehouse: h.includes('gatehouse') || hasGatehouseListSnapshot(),
      audit: h.includes('audit') || hasAuditListSnapshot(),
      grower: h.includes('grower') || hasGrowersListSnapshot(),
      transloader: h.includes('transload') || hasTransloadingListSnapshot(),
      scheduler: h.includes('schedule') || hasSchedulerListSnapshot()
    };
  }

  private leavePersonaStatesFromOrigins(origins: PersonaOriginFlags): void {
    const ctx = {
      filterService: this.filterService,
      searchService: this.searchService,
      ui: this.ui,
      scrollPosition: this.scrollPositionService
    };
    if (origins.receiver) {
      leaveReceiverPersonaListState(ctx);
    }
    if (origins.shunting) {
      leaveShuntingPersonaListState(ctx);
    }
    if (origins.gatehouse) {
      leaveGatehousePersonaListState(ctx);
    }
    if (origins.grower) {
      leaveGrowersPersonaListState(ctx);
    }
    if (origins.transloader) {
      leaveTransloadingPersonaListState(ctx);
    }
    if (origins.scheduler) {
      leaveSchedulerPersonaListState(ctx);
    }
    if (origins.audit) {
      leaveAuditPersonaListState(ctx);
    }
  }

  private applyKnownTargetRouteSession(
    targetRoute: string,
    mode: PersonaRouteSessionMode
  ): boolean {
    const config = PassportDetailsPage.TARGET_ROUTE_SESSION_CONFIG[targetRoute];
    if (!config) {
      return false;
    }
    this.clearAllPersonaListsExcept(config.except);
    this.applyPersonaRouteSessionStorage(mode, config.storageKey, config.clearSelf);
    return true;
  }

  private applyReceiverSessionForTargetRoute(
    targetRoute: string,
    fromPersonaHint: string,
    mode: PersonaRouteSessionMode
  ): void {
    if (this.applyKnownTargetRouteSession(targetRoute, mode)) {
      return;
    }

    if (mode === 'back' || mode === 'modal-home') {
      this.scrollPositionService.setReturningFromDetail(true);
    }

    this.clearAllPersonaListRestoreStates();
    this.leavePersonaStatesFromOrigins(this.computePersonaOrigins(fromPersonaHint));
  }

  // Alias properties/methods for template compatibility
  get previewImageIndex(): number | null {
    return this.haccpImagePreviewVisible ? this.haccpImagePreviewIndex : null;
  }

  prevPreviewImage(event: Event): void {
    this.previousHaccpImage(event);
  }

  nextPreviewImage(event: Event): void {
    this.nextHaccpImage(event);
  }

  closeSubmitModal(): void {
    this.submitModalVisible = false;
    void this.navigateToPersonaHome();
  }

  async back(): Promise<void> {
    await this.revertUnsavedImageChanges();

    const { route, fromHint } = this.getPassportBackNavigationTarget();
    const safeRoute = this.getSafeBackRoute(route);
    this.applyReceiverSessionForTargetRoute(safeRoute, fromHint, 'back');
    await this.router.navigateByUrl(safeRoute);
  }

  /**
   * Navigate back to the originating persona page after an access denial (403).
   * Reuses the same from-state logic as back() but without scroll position preservation.
   */
  private async navigateBackToOrigin(): Promise<void> {
    await this.revertUnsavedImageChanges();

    const { route, fromHint } = this.getPassportBackNavigationTarget();
    const safeRoute = this.getSafeBackRoute(route);
    this.applyReceiverSessionForTargetRoute(safeRoute, fromHint, 'access-denied');
    await this.router.navigateByUrl(safeRoute);
  }

  // Origin dropdown options - will be loaded from API
  originOptions: string[] = [];
  originMap: Map<string, { originName: string, rawSourceType: string }> = new Map(); // Map display name to origin data

  originOpen = false;
  haccpOpen = false;
  authorOpen = false;
  haccpChoiceOpen = false;
  
  personaOpen = false;
  yardOpen = false;
  dropdownSearch = '';
  authorSearch = '';
  authorisedByOptions: string[] = [];
  authorisedByMap: Map<string, number> = new Map(); // Map name to user ID
  private _authLoadToken = 0;
  haccpInfoVisible = false;
  haccpMaxImagesError = false; // Flag to show max images error
  haccpInvalidFileTypeError = false; // Flag to show invalid file type error
  haccpFileSizeError = false; // Flag to show file size error
  private haccpErrorTimer: any; // Timer to auto-hide error message
  
  // File validation constants
  private readonly ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
  private readonly MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB per image
  haccpImagePreviewVisible = false; // Flag for fullscreen image preview
  haccpImagePreviewIndex = 0; // Current image index in preview

  @ViewChild('haccpCameraInput') haccpCameraInput!: ElementRef<HTMLInputElement>;
  @ViewChild('haccpFileInput') haccpFileInput!: ElementRef<HTMLInputElement>;

  private async initializeComponent(): Promise<void> {
    this.authorisedByUserPicked = false;
    this.transferredDestinationPlantIdCache = 0;
    void (async () => {
      this.isLoading.set(true);
      try {
        // Immediately clear any stale/revoked blob URLs from a previous component instance.
        // The store is a singleton and retains haccpImages across navigations, but
        // ngOnDestroy revokes the blob URLs. Clearing here — before any await — prevents
        // Angular from rendering revoked URLs during later change detection cycles.
        const staleEntry = this.store.get(this.id);
        if (staleEntry?.haccpImages?.some((img: string) => img?.startsWith('blob:'))) {
          this.store.update(this.id, { haccpImages: [] });
        }

        this.authorizerSync.setAuthorisedByMap(this.authorisedByMap);
        
        const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
        const isReceiver = currentPersona.includes('receiver');
        const isShunter = currentPersona.includes('shunt');
        const isScheduler = currentPersona.includes('schedule');
        const isGatehouse = currentPersona.includes('gate');
        const isAudit = currentPersona.includes('audit');
        
        // Load origin options for personas that need them
        // Skip for: Receiver, Shunter, Transloader (origin is read-only, already in passport data),
        // and completed Grower passports (view-only mode, dropdown not needed)
        const rec = this.passports.getById(this.id);
        const isPendingOrOutgoing = !rec || rec.status === 'pending' || rec.status === 'outgoing';
        const isGrowerPersona = currentPersona.includes('grower');
        const isTransloaderPersona = currentPersona.includes('transload');

        if (!isReceiver && !isShunter && !isTransloaderPersona) {
          if (!(isGrowerPersona && !isPendingOrOutgoing)) {
            this.loadOriginOptions();
          }
        }
        
        // Load shunting yards for Gatehouse, Scheduler, and Audit
        if (isGatehouse || isScheduler || isAudit) {
          await this.loadShuntingYardsForPassport();
        }
        
        if (isScheduler) {
          await this.initializeForScheduler();
        } else if (isAudit) {
          // Audit uses same initialization as Scheduler (load data for Grower/Transloader toggle)
          await this.initializeForScheduler();
        } else {
          await this.initializeForNonScheduler(isReceiver, isShunter, isGatehouse);
        }

        this.initializeLoadingDateTimeIfNeeded();
        
        // Auto-populate bulkerNumber from bulkerMain if bulkerNumber is empty
        // This ensures the fallback value is shown and editable in the form
        this.autoPopulateBulkerNumberIfNeeded();
        
        // Capture original snapshot for offline history comparison
        // This must be done AFTER all data is loaded (from store or API)
        this.captureOriginalSnapshotIfNeeded();
      } finally {
        this.isLoading.set(false);
      }
    })();
  }
  
  /**
   * Capture the original snapshot for offline history comparison.
   * This should be called once after initial data is loaded.
   * The snapshot represents the state BEFORE any user edits.
   */
  private captureOriginalSnapshotIfNeeded(): void {
    // Only capture once per page load
    if (this.originalSnapshotCaptured) {
      return;
    }
    
    // Only capture for personas that can make offline edits (Grower, Transloader on mobile)
    // IMPORTANT: Don't check navigator.onLine here - we need to capture the snapshot
    // BEFORE the user goes offline so we have baseline data for comparison
    const storedPersona = (localStorage.getItem('persona') || '').toLowerCase();
    
    // Scheduler should never create offline history
    if (storedPersona.includes('schedule')) {
      return;
    }
    
    const isEligibleRole = storedPersona.includes('grower') || storedPersona.includes('transloader');
    const isMobileView = !this.isDesktopMode;
    
    // Only capture for eligible roles on mobile view
    if (!isEligibleRole || !isMobileView) {
      return;
    }
    
    const currentData = this.store.get(this.id);
    if (currentData && this.id) {
      this.offlineHistoryService.setOriginalSnapshot(this.id, currentData);
      this.originalSnapshotCaptured = true;
      console.log(`📸 Captured original snapshot in passport-details for passport ${this.id}`);
    }
  }

  /**
   * Auto-populate bulkerNumber from bulkerMain if bulkerNumber is empty.
   * This ensures the fallback value is displayed and editable in the form.
   * Only applies to editable personas (Grower, Transloader).
   */
  private autoPopulateBulkerNumberIfNeeded(): void {
    const data = this.store.get(this.id);
    if (!data) return;
    
    // Only auto-populate if bulkerNumber is empty but bulkerMain has a value
    const bulkerNumberEmpty = !data.bulkerNumber?.trim();
    const bulkerMainHasValue = !!data.bulkerMain?.trim();
    
    if (bulkerNumberEmpty && bulkerMainHasValue) {
      this.store.update(this.id, { bulkerNumber: data.bulkerMain });
      console.log(`📋 Auto-populated bulkerNumber from bulkerMain: ${data.bulkerMain}`);
    }
  }

  /**
   * Lightweight initialization used when returning from mobile Comments/History panel.
   * Only reloads dropdown option lists (origins, authorizers, shunting yards) without
   * overwriting the user's in-progress form edits that are already in the PassportDetailsStore.
   * Component-local state (persona, shunting yard selection, scheduler edit mode) is
   * restored from sessionStorage by restoreMobilePanelState() before this method is called.
   */
  private async initializeDropdownOptionsOnly(): Promise<void> {
    this.isLoading.set(true);
    try {
      this.authorizerSync.setAuthorisedByMap(this.authorisedByMap);

      const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
      const isReceiver = currentPersona.includes('receiver');
      const isShunter = currentPersona.includes('shunt');
      const isScheduler = currentPersona.includes('schedule');
      const isGatehouse = currentPersona.includes('gate');
      const isAudit = currentPersona.includes('audit');
      const isTransloaderPersona = currentPersona.includes('transload');
      const isGrowerPersona = currentPersona.includes('grower');
      const rec = this.passports.getById(this.id);
      const isPendingOrOutgoing = !rec || rec.status === 'pending' || rec.status === 'outgoing';

      // Load origin dropdown options (not the selected value)
      if (!isReceiver && !isShunter && !isTransloaderPersona) {
        if (!(isGrowerPersona && !isPendingOrOutgoing)) {
          this.loadOriginOptions();
        }
      }

      // Load shunting yard dropdown options for Gatehouse, Scheduler, and Audit
      // (the selected value was restored from sessionStorage by restoreMobilePanelState)
      if (isGatehouse || isScheduler || isAudit) {
        await this.loadShuntingYardsForPassport();
      }

      // Load authorizer dropdown options (not the selected value)
      if (!isReceiver && !isShunter) {
        if (!((isGrowerPersona || isTransloaderPersona) && !isPendingOrOutgoing)) {
          await this.loadAuthorisedByOptions();
        }
      }

      // Mark original snapshot as already captured to prevent re-capture with edited data
      this.originalSnapshotCaptured = true;
    } finally {
      this.isLoading.set(false);
    }
  }

  private async initializeForScheduler(): Promise<void> {
    const preloaded = this.store.get(this.id);
    const isTransferred = preloaded?.transferFlag || this.isTransferredTab;
    const tsID = preloaded?.transloadingStationID;
    const hasPreloadedRouting = preloaded?.transferFlag !== undefined;

    // Path C: Known transloading station — load Transloader data directly.
    // Only when we already know from pre-populated store data that the load
    // has a transloading station AND it is not a transferred load.
    if (!isTransferred && tsID != null && tsID !== 0) {
      console.log(`Scheduler: transloadingStationID=${tsID}, using Transloader persona`);
      this.persona = 'Transloader';
      await this.loadPassportDataForPersona('Transloader');
      await this.loadAuthorisedByOptions();
      return;
    }

    // Path A/B: Routing is known from store (Scheduler list) or navigation state.
    // Load Grower data directly — no Transloader probe needed.
    if (isTransferred || hasPreloadedRouting) {
      this.persona = isTransferred ? 'Scheduler' : 'Grower';
      console.log(`Scheduler: ${isTransferred ? 'Transferred load' : 'No transloading station'}, loading Grower data as ${this.persona}`);
      await this.loadPassportDataForPersona('Grower');
      if (isTransferred) {
        await this.loadOriginOptions();
        await this.loadAuthorisedByOptions();
      }
      return;
    }

    // Fallback: no pre-populated routing info (direct URL / bookmark).
    // Load Grower metadata first to discover transferFlag + transloadingStationID,
    // then branch accordingly.
    console.log('Scheduler: No pre-populated routing info, loading Grower metadata first');
    await this.loadPassportDataForPersona('Grower', true); // skipImages
    const data = this.store.get(this.id);

    if (data?.transferFlag || this.isTransferredTab) {
      console.log('Scheduler: Transferred load detected from Grower API');
      this.persona = 'Scheduler';
      await this.loadImagesIfNeeded();
      await this.loadOriginOptions();
      await this.loadAuthorisedByOptions();
    } else if (data?.transloadingStationID != null && data.transloadingStationID !== 0) {
      console.log(`Scheduler: transloadingStationID=${data.transloadingStationID} from Grower API, switching to Transloader`);
      this.persona = 'Transloader';
      await this.loadPassportDataForPersona('Transloader');
      await this.loadAuthorisedByOptions();
    } else {
      console.log('Scheduler: No transloading station from Grower API, using Grower persona');
      this.persona = 'Grower';
      await this.loadImagesIfNeeded();
    }
  }

  private async initializeForNonScheduler(isReceiver: boolean, isShunter: boolean, isGatehouse: boolean): Promise<void> {
    let personaDataLoaded = false;
    if (!this.store.get(this.id)) {
      await this.loadPassportDataForPersona(this.persona || 'Grower');
      personaDataLoaded = true;
    }

    if (!isReceiver && !isShunter) {
      const rec = this.passports.getById(this.id);
      const isPendingOrOutgoing = !rec || rec.status === 'pending' || rec.status === 'outgoing';
      const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
      const isGrowerOrTransloader = currentPersona.includes('grower') || currentPersona.includes('transload');

      if (!(isGrowerOrTransloader && !isPendingOrOutgoing)) {
        await this.loadAuthorisedByOptions();
      }
    }
    if (!personaDataLoaded) {
      await this.loadImagesIfNeeded();
    }
    
    if (isGatehouse) {
      this.initializeGatehouseShuntingYard();
    }
  }

  private initializeGatehouseShuntingYard(): void {
    const data = this.store.get(this.id);
    if (data) {
      this.selectedShuntingYardId = data.shuntingYardID ?? null;
      this.selectedShuntingYard = data.shuntingYardName || (this.selectedShuntingYardId ? '' : 'No Shunting Required');
      this.isShuntingRequired = data.isShuntingRequired ?? true;
    }
  }

  private initializeLoadingDateTimeIfNeeded(): void {
    // Only initialize for personas that can edit: Grower, Transloader, Scheduler
    const shouldInitialize = this.isGrowerOrTransloader || this.isScheduler;
    
    if (!shouldInitialize) {
      return;
    }

    const currentData = this.store.get(this.id);
    
    // Check if loadingDateTime is missing or empty
    if (!currentData?.loadingDateTime || currentData.loadingDateTime.length < 16) {
      // Before setting default, check if original snapshot has a value
      // This prevents overwriting backend data that wasn't loaded into the store yet
      const originalSnapshot = this.offlineHistoryService.getOriginalSnapshot(this.id) as any;
      if (originalSnapshot?.loadingDateTime && originalSnapshot.loadingDateTime.length >= 16) {
        // Use the value from original snapshot instead of generating new time
        this.store.update(this.id, { loadingDateTime: originalSnapshot.loadingDateTime });
        return;
      }
      
      // Generate current date/time in local time format (YYYY-MM-DDTHH:mm)
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const isoLocal = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      
      // Convert local time to UTC before storing (so convertUtcToLocal works correctly later)
      const utcValue = this.dateTimeHelper.convertLocalToUtc(isoLocal);
      
      // Store as UTC timestamp
      this.store.update(this.id, { loadingDateTime: utcValue });
    }
  }

  async startSchedulerEdit(): Promise<void> {
    if (this.isActualAudit) return;
    if (!this.isScheduler) return;
    await this.regenerateImageBlobUrls();
    const current = this.store.get(this.id);
    this.schedulerBaseline = current ? structuredClone(current) : null;
    this.schedulerEditEnabled = true;
    // Always reload authorizers to ensure the correct list is shown based on persona/transfer status
    await this.loadAuthorisedByOptions();
  }

  cancelSchedulerEdit(): void {
    if (!this.isScheduler) return;
    if (this.schedulerBaseline) {
      const b = this.schedulerBaseline;
      this.store.update(this.id, {
        origin: b.origin,
        loadingDateTime: b.loadingDateTime,
        bulkerNumber: b.bulkerNumber,
        haccpCheck: b.haccpCheck,
        haccpImages: b.haccpImages || [],
        authorisedBy: b.authorisedBy
      });
    }
    this.schedulerEditEnabled = false;
    this.reasonText = '';
    this.haccpOpen = false;
    this.authorOpen = false;
    this.originOpen = false;
    this.haccpChoiceOpen = false;
  }

  /**
   * Regenerate blob URLs from cached Blob objects. Acts as a safety net
   * for edge cases where blob URLs may have been invalidated (e.g., browser
   * garbage collection). Called by startSchedulerEdit to ensure fresh URLs
   * before entering edit mode.
   */
  private async regenerateImageBlobUrls(): Promise<void> {
    const currentData = this.store.get(this.id);
    const currentImages = currentData?.haccpImages || [];

    let changed = false;
    const needsRefetch: string[] = [];
    const newImages = currentImages.map((img: string) => {
      if (!img?.startsWith('blob:')) return img;
      const blob = this.imageBlobCache.get(img);
      if (!blob) {
        const proxyUrl = this.proxyUrlMap.get(img);
        if (proxyUrl) needsRefetch.push(proxyUrl);
        this.proxyUrlMap.delete(img);
        changed = true;
        return '';
      }

      const newBlobUrl = URL.createObjectURL(blob);
      const proxyUrl = this.proxyUrlMap.get(img);

      this.proxyUrlMap.delete(img);
      this.imageBlobCache.delete(img);
      if (proxyUrl) this.proxyUrlMap.set(newBlobUrl, proxyUrl);
      this.imageBlobCache.set(newBlobUrl, blob);

      changed = true;
      return newBlobUrl;
    });

    if (needsRefetch.length > 0) {
      const refetched = await this.fetchProxyImages(needsRefetch);
      const validRefetched = refetched.filter(u => !!u);
      const finalImages = newImages.filter(img => !!img).concat(validRefetched);
      this.store.update(this.id, { haccpImages: finalImages });
    } else if (changed) {
      this.store.update(this.id, { haccpImages: newImages });
    }
  }

  /**
   * Helper method to format origin display name with prefix based on rawSourceType
   * @param originName The raw origin name
   * @param rawSourceType The raw source type ('1' = Storage, '2' = Field, '4' = Plant)
   * @returns Formatted display name with prefix
   */
  private formatOriginDisplayName(originName: string, rawSourceType: string): string {
    if (!originName) return '';
    
    let prefix: string;
    if (rawSourceType === '2') {
      prefix = 'Field: ';
    } else if (rawSourceType === '1') {
      prefix = 'Storage: ';
    } else if (rawSourceType === '4') {
      prefix = 'Plant: ';
    } else {
      prefix = '';
    }
    
    return prefix + originName;
  }

  private async loadOriginOptions(): Promise<void> {
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      
      if (!token) {
        this.originOptions = [];
        this.originMap.clear();
        return;
      }
      
      // Get roleId from persona
      const persona = (localStorage.getItem('persona') || this.persona || 'grower').toLowerCase();
      const roleId = this.getPersonaId(persona);
      
      const isOnline = navigator.onLine;
      
      // Try to load from API if online
      if (isOnline && token) {
        const loaded = await this.tryLoadOriginsFromAPI(roleId, token, tokenType);
        if (loaded) return;
      }
      
      // Fallback to offline storage
      await this.loadOriginsFromOffline(roleId);
    } catch (err) {
      console.warn('Failed to load origins:', err);
      // Clear options if API fails
      this.originOptions = [];
      this.originMap.clear();
    }
  }

  private async tryLoadOriginsFromAPI(roleId: number, token: string, tokenType: string): Promise<boolean> {
    try {
      // Build API URL with roleId and passportId (if available)
      let apiUrl = `${environment.apiBaseUrl}/api/v1/origins?roleId=${roleId}`;
      if (this.id) {
        apiUrl += `&passportId=${this.id}`;
      }
      
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };
      
      const resp: any = await firstValueFrom(this.http.get(apiUrl, { headers, withCredentials: true }));
      
      this.originOptions = [];
      this.originMap.clear();
      
      if (Array.isArray(resp?.origins)) {
        // Process and cache the origins
        this.processOrigins(resp.origins);
        
        // Cache origins for offline use (mobile only - skip IndexedDB writes on desktop)
        if (!this.isDesktopMode) {
          const currentData = this.store.get(this.id || '');
          const storageKey = currentData?.originGroupKey || this.id || undefined;
          await this.offlineStorage.saveOrigins(roleId, storageKey, resp.origins);
        }
        
        return true;
      }
      return false;
    } catch (apiError) {
      console.warn('Failed to load origins from API, trying offline storage:', apiError);
      return false;
    }
  }

  private async loadOriginsFromOffline(roleId: number): Promise<void> {
    // Use group key for lookup when available, fall back to passport ID
    const currentData = this.store.get(this.id || '');
    const storageKey = currentData?.originGroupKey || this.id || undefined;
    const cachedOrigins = await this.offlineStorage.getOrigins(roleId, storageKey);
    
    this.originOptions = [];
    this.originMap.clear();
    
    if (cachedOrigins && Array.isArray(cachedOrigins)) {
      this.processOrigins(cachedOrigins);
    } else {
      console.log('No cached origins found');
      // Even without cached origins, add "No Origin Field" option
      this.originOptions = [this.NO_ORIGIN_FIELD_TEXT];
    }
  }

  private processOrigins(origins: any[]): void {
    // Add "No Origin Field" as the first option for all personas
    // This allows users to explicitly select no origin, which sends null values to the API
    this.originOptions.push(this.NO_ORIGIN_FIELD_TEXT);
    
    // Check the ACTUAL logged-in user's persona from localStorage, not this.persona
    // This is important because Schedulers have this.persona changed to 'Grower' or 'Transloader'
    // when viewing those passports, but they should still see all origin options including Plants
    const actualUserPersona = (localStorage.getItem('persona') || '').toLowerCase();
    const isActualScheduler = actualUserPersona.includes('schedule');
    const isActualGatehouse = actualUserPersona.includes('gate');
    
    // Specific Plant origins to hide when Scheduler edits Grower/Transloader data
    // These plants should never appear in the dropdown for Scheduler editing on behalf of Grower/Transloader
    const excludedPlantOriginsForScheduler = new Set(['Grantham', 'Hull', 'Scarborough', 'Whittlesey', 'Wombourne']);
    
    for (const origin of origins) {
      const originName = origin.originName || origin.OriginName || origin.name || '';
      const rawSourceType = origin.rawSourceType || origin.RawSourceType || '';
      
      // Skip Plant origins (rawSourceType = 4) for actual Grower and Transloader users only
      // Schedulers and Gatehouse users viewing Grower/Transloader passports should see all origins
      if (rawSourceType === '4' && !isActualScheduler && !isActualGatehouse && 
          (this.persona === 'Grower' || this.persona === 'Transloader')) {
        continue;
      }
      
      // Skip specific Plant origins for Schedulers when editing Grower/Transloader data
      // Also check this.persona === 'Scheduler' because during initial load, this.persona
      // has not yet been switched to 'Grower'/'Transloader' by initializeForScheduler()
      if (isActualScheduler && rawSourceType === '4' && 
          (this.persona === 'Grower' || this.persona === 'Transloader' || this.persona === 'Scheduler') &&
          excludedPlantOriginsForScheduler.has(originName)) {
        continue;
      }
      
      if (originName && rawSourceType) {
        const displayName = this.formatOriginDisplayName(originName, rawSourceType);
        this.originOptions.push(displayName);
        this.originMap.set(displayName, { originName, rawSourceType });
      }
    }
  }

  /**
   * Merges Scheduler/Receiver role-mapping names into authorisedByOptions for a plant,
   * after plant-scoped authorizers were loaded (see loadAuthorisedByOptions comments).
   */
  private mergeSchedulerReceiverAuthorizerSuggestions(
    plantId: number,
    schedulers: RoleUserMappingsResponse,
    receivers: RoleUserMappingsResponse
  ): void {
    const eligible = [...schedulers.users, ...receivers.users].filter(
      u => u.mappingID === plantId && u.isActive
    );

    const suggestionNames = Array.from(
      new Set(
        eligible
          .map(u => u.employeeName)
          .filter((name): name is string => typeof name === 'string' && name.length > 0)
      )
    );

    const existing = new Set(this.authorisedByOptions);
    for (const name of suggestionNames) {
      if (!existing.has(name)) {
        this.authorisedByOptions.push(name);
        existing.add(name);
      }
    }
  }

  /**
   * Plant-scoped authorizer list for Scheduler on a transferred load (destination plant known).
   */
  private async loadAuthorisedByOptionsForTransferredSchedulerPlant(token: number): Promise<void> {
    const plantId = this.resolveTransferredDestinationPlantId();
    this.transferredDestinationPlantIdCache = Math.max(0, plantId);

    if (plantId <= 0) {
      this.authorisedByOptions = [];
      return;
    }

    try {
      await this.loadAuthorisedByOptionsForPlant(plantId, token);

      const [schedulers, receivers] = await Promise.all([
        this.usersService.getUsersByRoleMappings('Scheduler'),
        this.usersService.getUsersByRoleMappings('Receiver')
      ]);

      this.mergeSchedulerReceiverAuthorizerSuggestions(plantId, schedulers, receivers);
    } catch (error) {
      console.warn('Failed to load plant-scoped authorizers / schedulers / receivers', error);
      this.authorisedByOptions = [];
    }
  }

  private async loadAuthorisedByOptions(): Promise<void> {
    const token = ++this._authLoadToken;
    const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
    const isScheduler = currentPersona.includes('schedule');
    const isAudit = currentPersona.includes('audit');
    const isTransloader = currentPersona.includes('transload');
    const isGrower = currentPersona.includes('grower');

    // Plant-scoped list only on Scheduler persona + transferred surface (not when
    // Scheduler user is editing Grower/Transloader Authorised By — those use passport below).
    if (isScheduler && this.isTransferredLoad && this.persona === 'Scheduler') {
      await this.loadAuthorisedByOptionsForTransferredSchedulerPlant(token);
      return;
    }

    // Determine if current view is for a Grower or Transloader persona (including Scheduler viewing them)
    const isTransloaderView = ((isScheduler || isAudit) && this.persona === 'Transloader' && this.id) || (isTransloader && this.id);
    const isGrowerView = ((isScheduler || isAudit) && (this.persona === 'Grower' || this.persona === 'Scheduler') && this.id) || (isGrower && this.id);

    if (isTransloaderView) {
      await this.loadAuthorisedByOptionsForPassport(Number.parseInt(this.id, 10), token);
    } else if (isGrowerView) {
      await this.loadAuthorisedByOptionsForPassport(Number.parseInt(this.id, 10), token, 2);
    } else {
      const userId = Number.parseInt(localStorage.getItem('userID') || '0', 10);
      if (userId) {
        await this.loadAuthorisedByOptionsForUser(userId, token);
      }
    }
  }

  private async loadAuthorisedByOptionsForUser(userId: number, loadToken?: number): Promise<void> {
    const myToken = loadToken ?? ++this._authLoadToken;
    try {
      if (!userId) {
        this.clearAuthorisedByData();
        return;
      }

      const isOnline = navigator.onLine;
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';

      // Try to load from API if online
      if (isOnline && token) {
        const loaded = await this.tryLoadAuthorisedUsersFromAPI(userId, token, tokenType);
        if (this._authLoadToken !== myToken) return;
        if (loaded) return;
      }

      // Fallback to offline storage
      if (this._authLoadToken !== myToken) return;
      await this.loadAuthorisedUsersFromOffline(userId);
    } catch (err) {
      console.warn('Failed to load authorised users:', err);
      if (this._authLoadToken === myToken) {
        this.clearAuthorisedByData();
      }
    }
  }

  /**
   * Loads plant-scoped authorizers (Passport.Authorizer rows with PlantID set).
   * Used by Schedulers on transferred loads whose destination plant is known via
   * vendorNumber but which don't yet have a GrowerAccount or TransloadingStation.
   */
  private async loadAuthorisedByOptionsForPlant(plantId: number, loadToken?: number): Promise<void> {
    const myToken = loadToken ?? ++this._authLoadToken;
    try {
      if (!plantId || plantId <= 0) {
        this.clearAuthorisedByData();
        return;
      }

      const isOnline = navigator.onLine;
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';

      if (isOnline && token) {
        const loaded = await this.tryLoadAuthorisedUsersFromAPI(null, token, tokenType, undefined, 4, plantId);
        if (this._authLoadToken !== myToken) return;
        if (loaded) return;
      }

      // No offline cache key for plant-scoped lists yet — clear to an empty state
      // rather than falling back to the user's personal authorizer list, which is
      // a different scope and would be misleading.
      if (this._authLoadToken === myToken) {
        this.clearAuthorisedByData();
      }
    } catch (err) {
      console.warn('Failed to load plant-scoped authorised users:', err);
      if (this._authLoadToken === myToken) {
        this.clearAuthorisedByData();
      }
    }
  }

  private async loadAuthorisedByOptionsForPassport(passportId: number, loadToken?: number, roleId?: number): Promise<void> {
    const myToken = loadToken ?? ++this._authLoadToken;
    try {
      if (!passportId) {
        this.clearAuthorisedByData();
        return;
      }

      const isOnline = navigator.onLine;
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';

      if (isOnline && token) {
        const loaded = await this.tryLoadAuthorisedUsersFromAPI(null, token, tokenType, passportId, roleId);
        if (this._authLoadToken !== myToken) return;
        if (loaded) return;
      }

      if (this._authLoadToken !== myToken) return;
      const userId = localStorage.getItem('userID');
      if (userId) {
        await this.loadAuthorisedUsersFromOffline(Number.parseInt(userId, 10));
      } else {
        console.log('No offline storage available - no userId found');
        this.clearAuthorisedByData();
      }
    } catch (err) {
      console.warn('Failed to load authorised users by passportId:', err);
      if (this._authLoadToken === myToken) {
        this.clearAuthorisedByData();
      }
    }
  }

  private async tryLoadAuthorisedUsersFromAPI(
    userId: number | null,
    token: string,
    tokenType: string,
    passportId?: number,
    roleId?: number,
    plantId?: number
  ): Promise<boolean> {
    try {
      let apiUrl: string;
      if (plantId && plantId > 0) {
        // Plant-scoped listing for transferred loads (no Grower/Station yet).
        apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?plantId=${plantId}&roleId=4&usePlantAuthorizerScope=true`;
      } else if (passportId) {
        apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?passportId=${passportId}`;
        if (roleId) {
          apiUrl += `&roleId=${roleId}`;
        }
      } else if (userId) {
        apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?userId=${userId}`;
      } else {
        console.warn('No userId, passportId, or plantId provided for loading authorizers');
        return false;
      }
      
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };
      const resp: any = await firstValueFrom(this.http.get(apiUrl, { headers, withCredentials: true }));
      
      this.clearAuthorisedByData();
      
      if (Array.isArray(resp?.authorisedUsers)) {
        this.processAuthorisedUsers(resp.authorisedUsers);
        
        // Cache to IndexedDB for offline use (mobile only - skip IndexedDB writes on desktop)
        // For Transloader, authorizers are same for all their passports (same TransloadingStation)
        if (!this.isDesktopMode) {
          const storageUserId = userId || Number.parseInt(localStorage.getItem('userID') || '0', 10);
          if (storageUserId > 0) {
            await this.offlineStorage.saveAuthorisedUsers(storageUserId, resp.authorisedUsers);
            console.log(`📦 Cached ${resp.authorisedUsers.length} authorizers for offline use`);
          }
        }
      }
      return true;
    } catch (apiError) {
      console.warn('Failed to load from API, trying offline storage:', apiError);
      return false;
    }
  }

  private async loadAuthorisedUsersFromOffline(userId: number): Promise<void> {
    const cachedUsers = await this.offlineStorage.getAuthorisedUsers(userId);
    
    this.clearAuthorisedByData();
    
    if (cachedUsers && Array.isArray(cachedUsers)) {
      this.processAuthorisedUsers(cachedUsers);
    } else {
      console.log('No cached authorised users found');
    }
  }

  private processAuthorisedUsers(users: any[]): void {
    for (const u of users) {
      const name = u.authorizerName || u.name;
      const id = u.authorizerId || u.id;
      if (name) {
        this.authorisedByOptions.push(name);
        // Handle pending authorizers (negative ID) and real IDs
        if (id !== undefined && id !== null) {
          this.authorisedByMap.set(name, Number.parseInt(String(id), 10));
        } else {
          // If no ID, use -1 to indicate pending (shouldn't happen normally)
          this.authorisedByMap.set(name, -1);
        }
      }
    }
  }

  private clearAuthorisedByData(): void {
    this.authorisedByOptions = [];
    this.authorisedByMap.clear();
  }

  /**
   * Load shunting yards for the current passport (Gatehouse and Scheduler only)
   * Uses the new API endpoint that filters by passport's plant
   */
  private async loadShuntingYardsForPassport(): Promise<void> {
    try {
      if (!this.id) {
        console.warn('No passport ID available to load shunting yards');
        return;
      }

      const isOnline = navigator.onLine;
      const token = localStorage.getItem('accessToken');

      // Try to load from API if online
      if (isOnline && token) {
        const loaded = await this.tryLoadShuntingYardsFromAPI(token);
        if (loaded) return;
      }

      // Fallback to hardcoded values (cache removed)
      this.setFallbackShuntingYards();
    } catch (err) {
      console.warn('Failed to load shunting yards:', err);
      this.setFallbackShuntingYards();
    }
  }

  private async tryLoadShuntingYardsFromAPI(token: string): Promise<boolean> {
    try {
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      const apiUrl = `${environment.apiBaseUrl}/api/v1/ShuntingYards/passport/${this.id}`;
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };
      const resp: any = await firstValueFrom(this.http.get(apiUrl, { headers, withCredentials: true }));
      
      console.log('Shunting yards for passport API response:', resp);
      
      if (Array.isArray(resp?.shuntingYards)) {
        this.processShuntingYardsResponse(resp.shuntingYards);
        console.log(`Loaded ${this.shuntingYards.length} shunting yards for passport ${this.id} from API`);
        return true;
      }
      return false;
    } catch (apiError) {
      console.warn('Failed to load shunting yards from API, using fallback:', apiError);
      return false;
    }
  }

  private processShuntingYardsResponse(yards: any[]): void {
    this.shuntingYards = [
      { id: null, name: 'No Shunting Required' },
      ...yards
        .filter((y: any) => y && (y.shuntingYardName || y.name))
        .map((y: any) => ({
          id: y.shuntingYardID || y.id || 0,
          name: y.shuntingYardName || y.name || ''
        }))
    ];
    this.shuntingYardsDisplay = this.shuntingYards.map(y => y.name);
  }

  private setFallbackShuntingYards(): void {
    console.log('No cached shunting yards found, using fallback');
    this.shuntingYards = [
      { id: null, name: 'No Shunting Required' },
      { id: 1, name: 'Yard A' },
      { id: 2, name: 'Yard B' },
      { id: 3, name: 'Yard C' }
    ];
    this.shuntingYardsDisplay = this.shuntingYards.map(y => y.name);
  }

  /**
   * data: URLs for locally added images that have not yet been uploaded.
   * Outgoing IndexedDB snapshots store data URLs without File objects (iOS), so preserve them.
   * For pending/completed, skip data URLs when haccpImageFiles is empty (post-upload stale previews).
   */
  private getUnsavedLocalDataUrls(
    haccpImages: string[] | undefined,
    haccpImageFiles: File[] | undefined,
    preserveDataUrlsWithoutFiles = false
  ): string[] {
    const dataUrls = (haccpImages ?? []).filter((img: string) => img?.startsWith('data:'));
    if (preserveDataUrlsWithoutFiles) {
      return dataUrls;
    }
    if ((haccpImageFiles?.length ?? 0) === 0) {
      return [];
    }
    return dataUrls;
  }

  /**
   * Initialize image arrays from backend data.
   * Backend returns proxy URLs; we fetch each through HttpClient with the auth
   * header and convert to blob: URLs so <img> can render them without exposing
   * storage credentials to the browser.
   */
  private async loadImagesIfNeeded(): Promise<void> {
    if (this.imageLoadInProgress) return;
    this.imageLoadInProgress = true;
    try {
      await this.loadBackendImagesIntoStore();
    } catch (error) {
      console.warn('Failed to load images:', error);
      this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, 0);
    } finally {
      this.imageLoadInProgress = false;
    }
  }

  private shouldKeepOutgoingOfflineImages(
    status: string | undefined,
    haccpImages: string[] | undefined
  ): boolean {
    const offlineDataUrls = (haccpImages ?? []).filter((img: string) => img?.startsWith('data:'));
    if (status !== 'outgoing' || offlineDataUrls.length === 0) {
      return false;
    }
    console.log(`📤 Outgoing passport: keeping ${offlineDataUrls.length} offline data URL image(s)`);
    return true;
  }

  private pruneStaleBlobUrlsFromStore(haccpImages: string[] | undefined): void {
    const currentImages = haccpImages ?? [];
    const hasStaleBlobUrls = currentImages.some(
      (img: string) => img?.startsWith('blob:') && !this.imageBlobCache.has(img)
    );
    if (!hasStaleBlobUrls) {
      return;
    }
    const nonBlobImages = currentImages.filter(
      (img: string) => !img?.startsWith('blob:') || this.imageBlobCache.has(img)
    );
    this.store.update(this.id, { haccpImages: nonBlobImages });
  }

  private getValidImageLinks(links: string[] | undefined): string[] {
    return (links ?? []).filter((s: string) => typeof s === 'string' && s.trim().length > 0);
  }

  private applyNoBackendImagesState(existingDataUrls: string[]): void {
    if (existingDataUrls.length > 0) {
      this.store.update(this.id, {
        haccpImages: [...existingDataUrls],
        imageCount: existingDataUrls.length
      });
    } else {
      this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, 0);
      this.store.update(this.id, {
        haccpImages: [],
        imageCount: 0
      });
    }
    this.lastLoadedLinksKey = '';
  }

  private shouldSkipImageReload(haccpImages: string[] | undefined, linksKey: string): boolean {
    const currentBlobImages = (haccpImages ?? []).filter((img: string) => img?.startsWith('blob:'));
    return this.lastLoadedLinksKey === linksKey && currentBlobImages.length > 0;
  }

  private applyOfflineImagePlaceholders(
    linksToLoad: string[],
    existingDataUrls: string[],
    totalImageCount: number
  ): void {
    const message = totalImageCount === 1
      ? 'HACCP Check image is only available when online.'
      : 'HACCP Check images are only available when online.';
    this.toastService.warning(message, 5000);

    const placeholders = linksToLoad.map((link: string) => {
      const parts = link.split('/');
      const filename = parts.at(-1) || 'image';
      return `PLACEHOLDER:${filename}`;
    });

    this.store.update(this.id, {
      haccpImages: [...placeholders, ...existingDataUrls],
      imageCount: totalImageCount
    });
    this.lastLoadedLinksKey = '';
  }

  private revokeOldBlobUrls(haccpImages: string[] | undefined): void {
    for (const oldImg of haccpImages ?? []) {
      if (!oldImg?.startsWith('blob:')) {
        continue;
      }
      URL.revokeObjectURL(oldImg);
      this.proxyUrlMap.delete(oldImg);
      this.imageBlobCache.delete(oldImg);
    }
  }

  private async fetchAndStoreProxyImages(
    linksToLoad: string[],
    existingDataUrls: string[],
    existingFiles: File[],
    linksKey: string,
    totalLinkCount: number
  ): Promise<void> {
    const displayImages = await this.fetchProxyImages(linksToLoad);
    const validImages = displayImages.filter(u => !!u);

    this.store.update(this.id, {
      haccpImages: [...validImages, ...existingDataUrls],
      imageCount: validImages.length + existingDataUrls.length,
      haccpImageFiles: existingFiles
    });

    this.lastLoadedLinksKey = linksKey;
    console.log(`Loaded ${validImages.length}/${totalLinkCount} backend images via proxy`);

    if (validImages.length === 0 && totalLinkCount > 0) {
      this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, 0);
    }
  }

  private async loadBackendImagesIntoStore(): Promise<void> {
    const currentData = this.store.get(this.id);
    const status = this.passports.getById(this.id)?.status;
    if (this.shouldKeepOutgoingOfflineImages(status, currentData?.haccpImages)) {
      return;
    }

    this.pruneStaleBlobUrlsFromStore(currentData?.haccpImages);

    const freshData = this.store.get(this.id);
    const existingFiles = freshData?.haccpImageFiles || [];
    const existingDataUrls = this.getUnsavedLocalDataUrls(
      freshData?.haccpImages,
      existingFiles,
      status === 'outgoing'
    );
    const imageLinks = this.getValidImageLinks(freshData?.haccpImageLinks);

    if (imageLinks.length === 0) {
      console.log('No backend images to load');
      this.applyNoBackendImagesState(existingDataUrls);
      return;
    }

    console.log(`Found ${imageLinks.length} backend images`);
    const linksToLoad = status === 'completed' ? imageLinks : imageLinks.slice(0, 3);
    const linksKey = linksToLoad.join('|');

    if (this.shouldSkipImageReload(freshData?.haccpImages, linksKey)) {
      return;
    }

    if (!navigator.onLine) {
      this.applyOfflineImagePlaceholders(linksToLoad, existingDataUrls, imageLinks.length);
      return;
    }

    this.revokeOldBlobUrls(freshData?.haccpImages);
    await this.fetchAndStoreProxyImages(
      linksToLoad,
      existingDataUrls,
      existingFiles,
      linksKey,
      imageLinks.length
    );
  }

  /**
   * Fetch images from the backend proxy endpoint with authentication,
   * returning blob: URLs for thumbnail display. Blob URLs are kept
   * alive for the component's lifetime and revoked in ngOnDestroy
   * (or when replaced by a subsequent loadImagesIfNeeded call).
   * Raw Blob objects are cached in imageBlobCache for the preview modal.
   */
  private async fetchProxyImages(proxyUrls: string[]): Promise<string[]> {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const headers: Record<string, string> = {
      'Authorization': `${tokenType} ${token}`,
      'ngsw-bypass': 'true'
    };

    const fetches = proxyUrls.map(async (proxyUrl) => {
      const fullUrl = `${environment.apiBaseUrl}${proxyUrl}`;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          let url = fullUrl;
          if (attempt > 0) {
            const separator = fullUrl.includes('?') ? '&' : '?';
            url = `${fullUrl}${separator}_t=${Date.now()}`;
          }
          const blob = await firstValueFrom(
            this.http.get(url, { headers, responseType: 'blob' })
          );
          const blobUrl = URL.createObjectURL(blob);
          this.proxyUrlMap.set(blobUrl, proxyUrl);
          this.imageBlobCache.set(blobUrl, blob);
          return blobUrl;
        } catch (err: any) {
          const status = err?.status || err?.error?.status;
          if (status === 404) {
            console.warn('Image not found (404), skipping retry:', proxyUrl);
            return '';
          }
          if (attempt === 0) {
            await new Promise(r => setTimeout(r, 500));
          } else {
            console.warn('Failed to fetch proxy image after retry:', proxyUrl, err);
            return '';
          }
        }
      }
      return '';
    });

    return Promise.all(fetches);
  }


  get filteredAuthorisedByOptions(): string[] {
    const term = (this.authorSearch || '').trim().toLowerCase();
    if (!term) return this.authorisedByOptions;
    return this.authorisedByOptions.filter(o => o.toLowerCase().includes(term));
  }

  get canAddAuthorSearch(): boolean {
    const term = (this.authorSearch || '').trim();
    if (!term) return false;
    const lower = term.toLowerCase();
    return !this.authorisedByOptions.some(o => o.toLowerCase() === lower);
  }
  private haccpUploadRevertTimer: number | undefined;

  // Persona selection and optional fields
  personaOptions: Array<'Grower' | 'Transloader'> = ['Grower', 'Transloader'];
  schedulerPersonaOptions: Array<'Grower' | 'Transloader'> = ['Transloader', 'Grower'];
  
  /**
   * Filtered scheduler persona options based on tab context and transloadingStationID
   * - Transferred loads: always show 'Scheduler'; add 'Transloader' only if transloadingStationID is set
   * - Other tabs: if transloadingStationID is null, only 'Grower'; otherwise both
   */
  get filteredSchedulerPersonaOptions(): Array<'Grower' | 'Transloader' | 'Scheduler'> {
    const passportData = this.store.get(this.id);

    if (this.isTransferredTab || passportData?.transferFlag) {
      const transloadingStationID = passportData?.transloadingStationID;
      if (transloadingStationID != null && transloadingStationID !== 0) {
        return ['Scheduler', 'Transloader'];
      }
      return ['Scheduler'];
    }

    const transloadingStationID = passportData?.transloadingStationID;
    
    if (transloadingStationID == null || transloadingStationID === 0) {
      return ['Grower'];
    }
    
    return this.schedulerPersonaOptions;
  }
  
  shuntingYards: Array<{ id: number | null; name: string }> = [];
  shuntingYardsDisplay: string[] = []; // For dropdown display
  selectedShuntingYard = 'No Shunting Required'; // Display name - default value
  selectedShuntingYardId: number | null = null; // ID for API - default NULL
  isShuntingRequired = true; // Whether shunting yard section should be shown (based on backend passport data)
  reasonText = '';

  @HostListener('document:click')
  onDocClick(): void {
    this.originOpen = false;
    this.haccpOpen = false;
    this.authorOpen = false;
    this.haccpChoiceOpen = false;
    this.personaOpen = false;
    this.yardOpen = false;
    // Note: haccpCheck value comes from backend and should not be modified client-side
    // Even if images fail to load, we keep the backend's haccpCheck value
    // and show a toast notification to inform the user (handled by ImageLoadHelperService)
  }

  toggleOrigin(event: Event): void {
    event.stopPropagation();
    this.originOpen = !this.originOpen;
  }

  toggleAuthor(event: Event): void {
    // Only toggle if the event target is the button itself, not a child element (like the search input)
    if (event.target === event.currentTarget) {
      event.stopPropagation();
      event.preventDefault();
      this.authorOpen = !this.authorOpen;
    }
  }

  setOrigin(value: string): void {
    const trimmed = (value || '').trim();
    // If "No Origin Field" is selected, store empty string (to send null/blank to API)
    const storeValue = trimmed === this.NO_ORIGIN_FIELD_TEXT ? '' : trimmed;
    this.store.update(this.id, { origin: storeValue });
    this.originOpen = false;
    this.dropdownSearch = '';
  }

  setLoadingDateTime(valueIso: string): void {
    // If value is cleared, store empty string
    if (!valueIso || valueIso.trim() === '') {
      this.store.update(this.id, { loadingDateTime: '' });
      return;
    }
    
    // Convert local time (from datetime-local input) to UTC before storing
    // datetime-local input gives us YYYY-MM-DDTHH:mm in local time
    const utcValue = this.dateTimeHelper.convertLocalToUtc(valueIso);
    this.store.update(this.id, { loadingDateTime: utcValue });
  }

  setBulkerNumber(value: string): void {
    this.store.update(this.id, { bulkerNumber: value });
  }

  async setHaccpDesktop(value: 'Yes' | 'No'): Promise<void> {
    if (value === 'Yes') {
      const effectiveCount = this.getEffectiveImageCount();
      if (effectiveCount >= 3) {
        this.showMaxImagesError(effectiveCount);
        return;
      }
      this.haccpOpen = false;
      // On desktop, automatically trigger file upload when 'Yes' is selected
      // We use a short timeout to ensure UI updates first
      setTimeout(() => {
        if (this.haccpCameraInput?.nativeElement && this.haccpFileInput?.nativeElement) {
          this.chooseHaccpCapture('file', this.haccpCameraInput.nativeElement, this.haccpFileInput.nativeElement);
        }
      }, 100);
    } else {
      this.setHaccp(value);
    }
  }

  setHaccp(value: 'Yes' | 'No'): void {
    if (value === 'Yes') {
      this.handleHaccpYes();
    } else {
      this.handleHaccpNo();
    }
  }

  private handleHaccpYes(): void {
    const effectiveCount = this.getEffectiveImageCount();
    
    if (effectiveCount >= 3) {
      this.showMaxImagesError(effectiveCount);
      return;
    }
    
    // Normal flow: less than 3 images, allow upload
    this.haccpOpen = false;
    this.haccpChoiceOpen = true;
  }

  private showMaxImagesError(effectiveCount: number): void {
    this.showHaccpError('maxImages');
    console.log(`⚠️ Maximum HACCP images (3) already reached. Current effective count: ${effectiveCount}`);
  }

  /**
   * Shows a HACCP-related error message and auto-hides it after 3 seconds
   * @param errorType - Type of error: 'maxImages', 'invalidFileType', or 'fileSize'
   */
  private showHaccpError(errorType: 'maxImages' | 'invalidFileType' | 'fileSize'): void {
    // Clear all error flags first
    this.haccpMaxImagesError = false;
    this.haccpInvalidFileTypeError = false;
    this.haccpFileSizeError = false;
    
    // Set the appropriate error flag
    switch (errorType) {
      case 'maxImages':
        this.haccpMaxImagesError = true;
        break;
      case 'invalidFileType':
        this.haccpInvalidFileTypeError = true;
        break;
      case 'fileSize':
        this.haccpFileSizeError = true;
        break;
    }
    
    this.haccpOpen = false;
    this.haccpChoiceOpen = false;
    
    // Clear existing timer and set new one to auto-hide error after 3 seconds
    if (this.haccpErrorTimer) {
      clearTimeout(this.haccpErrorTimer);
    }
    this.haccpErrorTimer = setTimeout(() => {
      this.haccpMaxImagesError = false;
      this.haccpInvalidFileTypeError = false;
      this.haccpFileSizeError = false;
    }, 3000);
  }

  private handleHaccpNo(): void {
    const deletedImages = this.markAllBackendImagesForDeletion();
    
    // Clear all image-related state
    this.store.update(this.id, { 
      haccpImages: [],
      haccpImageFiles: [],
      deletedImageFilenames: deletedImages
    });
    this.haccpOpen = false;
    this.haccpChoiceOpen = false;
  }

  private markAllBackendImagesForDeletion(): string[] {
    const data = this.store.get(this.id);
    const backendImageCount = data?.imageCount || 0;
    const haccpImageLinks = data?.haccpImageLinks || [];
    const deletedImages: string[] = [];
    
    if (!navigator.onLine || backendImageCount === 0 || haccpImageLinks.length === 0) {
      return deletedImages;
    }
    
    for (const imageUrl of haccpImageLinks) {
      if (imageUrl) {
        const blobPath = this.extractBlobPathFromUrl(imageUrl);
        if (blobPath && !deletedImages.includes(blobPath)) {
          deletedImages.push(blobPath);
        }
      }
    }
    
    for (const img of data?.haccpImages || []) {
      if (img?.startsWith('blob:')) {
        this.proxyUrlMap.delete(img);
        this.imageBlobCache.delete(img);
      }
    }
    
    console.log(`User selected HACCP "No" - marking ${deletedImages.length} backend images for deletion`);
    return deletedImages;
  }

  private extractBlobPathFromUrl(imageUrl: string): string | null {
    try {
      const proxyPrefix = '/api/v1/passports/images/';
      if (imageUrl.startsWith(proxyPrefix)) {
        return imageUrl.substring(proxyPrefix.length);
      }
      return null;
    } catch (err) {
      console.warn('Failed to parse image URL for deletion:', err);
      return null;
    }
  }

  addHaccpImage(file: File | null): void {
    if (!file) return;
    
    // Validate file type
    if (!this.ALLOWED_IMAGE_TYPES.includes(file.type)) {
      console.warn(`⚠️ Invalid file type: ${file.type}. Only images (JPEG, PNG, GIF, WebP, BMP) are allowed.`);
      this.showHaccpError('invalidFileType');
      return;
    }
    
    // Validate file size (max 15MB per image)
    if (file.size > this.MAX_IMAGE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      console.warn(`⚠️ File too large: ${fileSizeMB}MB. Maximum allowed is 15MB per image.`);
      this.showHaccpError('fileSize');
      return;
    }
    
    // Check if adding this image would exceed the limit
    const effectiveCount = this.getEffectiveImageCount();
    if (effectiveCount >= 3) {
      // Show error message
      this.showHaccpError('maxImages');
      console.log(`⚠️ Cannot add image - maximum (3) already reached. Current effective count: ${effectiveCount}`);
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const current = this.store.get(this.id)?.haccpImages || [];
      
      console.log('➕ [HACCP] Adding image to uploads:', file.name, 'passport:', this.id);

      const result = reader.result;
      const dataUrl = typeof result === 'string' ? result : '';

      const nonPlaceholderImages: string[] = [];
      const placeholderImages: string[] = [];
      
      for (const img of current) {
        if (img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')) {
          nonPlaceholderImages.push(img);
        } else {
          placeholderImages.push(img);
        }
      }

      if (!dataUrl.startsWith('data:')) {
        console.warn(
          `Offline HACCP: invalid read result for passport ${this.id} (${file.name}, ${file.type}, ${file.size} bytes)`
        );
        this.toastService.warning(
          'Could not read this photo. Try again or choose a different image (JPEG or PNG).',
          6000
        );
        this.haccpOpen = false;
        this.haccpChoiceOpen = false;
        return;
      }
      
      const newImages = [...nonPlaceholderImages, dataUrl, ...placeholderImages];
      
      this.store.update(this.id, { 
        haccpImages: newImages,
        haccpImageFiles: [...(this.store.get(this.id)?.haccpImageFiles || []), file]
      });
      
      console.log('📝 [HACCP] Current new upload files:', this.store.get(this.id)?.haccpImageFiles?.length || 0)

      if (this.haccpUploadRevertTimer !== undefined) { 
        clearTimeout(this.haccpUploadRevertTimer); 
        this.haccpUploadRevertTimer = undefined; 
      }
      this.haccpOpen = false;
      this.haccpChoiceOpen = false;
    };
    reader.onerror = () => {
      const reason = reader.error?.message ?? 'Failed to read image file';
      console.warn(
        `Offline HACCP: failed to read image for passport ${this.id} (${file.name}, ${file.type}, ${file.size} bytes): ${reason}`
      );
      this.toastService.warning(
        'Could not read this photo. Try again or choose a different image (JPEG or PNG).',
        6000
      );
      this.haccpOpen = false;
      this.haccpChoiceOpen = false;
    };
    reader.onabort = () => {
      console.warn(`Offline HACCP: FileReader aborted for passport ${this.id} (${file.name})`);
    };
    reader.readAsDataURL(file);
  }

  private findActualImageIndex(displayedIndex: number, currentImages: string[]): number {
    let actualIndex = -1;
    let displayedCount = 0;
    for (let i = 0; i < currentImages.length; i++) {
      const img = currentImages[i];
      if (img?.trim().length > 0 && !img.startsWith('PLACEHOLDER:')) {
        if (displayedCount === displayedIndex) {
          actualIndex = i;
          break;
        }
        displayedCount++;
      }
    }
    return actualIndex;
  }

  private markBackendImageForDeletion(imageUrl: string, deletedImages: string[]): void {
    try {
      // Resolve blob: URLs to their original proxy path via the map
      let resolvedUrl = imageUrl;
      if (imageUrl.startsWith('blob:')) {
        resolvedUrl = this.proxyUrlMap.get(imageUrl) || imageUrl;
      }

      const blobPath = this.extractBlobPathFromUrl(resolvedUrl);
      if (!blobPath) {
        console.warn('Could not extract blob path from image URL:', imageUrl);
        return;
      }
      if (deletedImages.includes(blobPath)) {
        console.log(`Backend image already in deletion list: ${blobPath}`);
        return;
      }
      deletedImages.push(blobPath);
      console.log(`Marked backend image for deletion: ${blobPath}`);
    } catch (err) {
      console.warn('Failed to parse image URL:', err);
    }
  }

  private calculateHasRealImages(images: string[], imageFiles: File[]): boolean {
    const isOffline = navigator.onLine === false;
    const hasRealImages = images.some((img: string) => img?.trim().length > 0 && !img.startsWith('PLACEHOLDER:'));
    const hasImageFiles = imageFiles.length > 0;
    const hasPlaceholders = isOffline && images.some((img: string) => img?.startsWith('PLACEHOLDER:'));
    return hasRealImages || hasImageFiles || hasPlaceholders;
  }

  removeHaccpImage(index: number): void {
    const currentImages = this.store.get(this.id)?.haccpImages || [];
    const currentFiles = this.store.get(this.id)?.haccpImageFiles || [];
    const deletedImages = this.store.get(this.id)?.deletedImageFilenames || [];

    const actualIndex = this.findActualImageIndex(index, currentImages);
    if (actualIndex === -1) {
      console.warn('🧾 [HACCP] Could not find image at displayed index:', index);
      return;
    }

    const imageUrl = currentImages[actualIndex];
    
    if (!imageUrl?.trim() || imageUrl.startsWith('PLACEHOLDER:')) {
      this.removeEmptyPlaceholder(actualIndex);
      return;
    }
    
    const isBackend = imageUrl.startsWith('blob:') || imageUrl.startsWith('http://') || imageUrl.startsWith('https://');
    const nextImages = currentImages.filter((_, i) => i !== actualIndex);

    if (isBackend) {
      this.markBackendImageForDeletion(imageUrl, deletedImages);
      if (imageUrl.startsWith('blob:')) {
        this.proxyUrlMap.delete(imageUrl);
        this.imageBlobCache.delete(imageUrl);
      }
      const hasRealImages = this.calculateHasRealImages(nextImages, currentFiles);
      this.store.update(this.id, {
        haccpImages: nextImages,
        deletedImageFilenames: deletedImages,
        haccpCheck: hasRealImages ? 'Yes' : 'No'
      });
      return;
    }

    const isLocal = (val: string) => val?.startsWith('data:');
    const localIndex = currentImages.slice(0, actualIndex).filter(isLocal).length;
    const nextFiles = currentFiles.filter((_, i) => i !== localIndex);

    const hasRealImages = this.calculateHasRealImages(nextImages, nextFiles);
    this.store.update(this.id, {
      haccpImages: nextImages,
      haccpImageFiles: nextFiles,
      deletedImageFilenames: deletedImages,
      haccpCheck: hasRealImages ? 'Yes' : 'No'
    });

    console.log('🧾 [HACCP] Removed image at displayed index:', index, 'actual index:', actualIndex);
  }

  /**
   * Remove empty placeholder at the given index (for drafts)
   * This receives the actual index in currentImages array (not displayed index)
   * Placeholders now contain the image name in format "PLACEHOLDER:filename.jpg"
   */
  removeEmptyPlaceholder(index: number): void {
    const currentImages = this.store.get(this.id)?.haccpImages || [];
    const currentFiles = this.store.get(this.id)?.haccpImageFiles || [];
    const deletedImages = this.store.get(this.id)?.deletedImageFilenames || [];
    
    // Validate index
    if (index < 0 || index >= currentImages.length) {
      console.warn('🧾 [HACCP] Invalid placeholder index:', index);
      return;
    }
    
    // Get the placeholder string
    const placeholderStr = currentImages[index];
    
    // Check if it's a placeholder (empty string or PLACEHOLDER: prefixed)
    const isPlaceholder = !placeholderStr || placeholderStr.trim().length === 0 || placeholderStr.startsWith('PLACEHOLDER:');
    
    if (!isPlaceholder) {
      // Not a placeholder - use regular removal logic
      this.removeHaccpImage(index);
      return;
    }
    
    // Extract image name from placeholder (format: "PLACEHOLDER:filename.jpg")
    let filenameToDelete: string | null = null;
    if (placeholderStr?.startsWith('PLACEHOLDER:')) {
      filenameToDelete = placeholderStr.substring('PLACEHOLDER:'.length);
    }
    
    // Add to deletion list if we found a filename
    if (filenameToDelete && !deletedImages.includes(filenameToDelete)) {
      deletedImages.push(filenameToDelete);
      console.log(`✅ Marked backend image for deletion (from placeholder): ${filenameToDelete}`);
      console.log(`📋 Current deletedImageFilenames:`, [...deletedImages]);
    } else if (filenameToDelete) {
      console.log(`⚠️ Filename already in deletion list: ${filenameToDelete}`);
    }
    
    // Remove the placeholder directly
    const nextImages = currentImages.filter((_, i) => i !== index);
    // Check for real images OR image files OR remaining placeholders (when offline, placeholders represent existing backend images)
    const isOfflineCheck = navigator.onLine === false;
    const hasRealImages = nextImages.some((img: string) => img?.trim().length > 0 && !img.startsWith('PLACEHOLDER:')) || currentFiles.length || (isOfflineCheck && nextImages.some((img: string) => img?.startsWith('PLACEHOLDER:')));
    this.store.update(this.id, {
      haccpImages: nextImages,
      deletedImageFilenames: deletedImages,
      haccpCheck: hasRealImages ? 'Yes' as const : 'No' as const
    });
    console.log('🧾 [HACCP] Removed empty placeholder at actual index:', index, filenameToDelete ? `(marked ${filenameToDelete} for deletion)` : '');
    // Note: Draft will be saved when user explicitly clicks "Save Draft"
    // This ensures all changes (including multiple deletions) are captured in a single history entry
  }

  onHaccpImageLoad(_index: number): void {
    // No-op: blob URLs are revoked in ngOnDestroy and when replaced by loadImagesIfNeeded.
    // Early revocation causes errors when Angular re-renders the template (especially in
    // Scheduler persona where *ngIf blocks toggle frequently during initialization).
  }

  onHaccpImageError(index: number): void {
    try {
      const failedUrl = this.displayedHaccpImages[index];

      // Blob URL not in our imageBlobCache = stale leftover from a previous
      // component instance (revoked in ngOnDestroy).  It will be cleared by
      // initializeForScheduler or loadImagesIfNeeded — don't fire a toast.
      if (failedUrl?.startsWith('blob:') && !this.imageBlobCache.has(failedUrl)) {
        console.log('🔄 [HACCP] Ignoring stale blob URL error at index:', index);
        this.removeFailedImageFromDisplay(index);
        return;
      }

      console.warn('🚫 [HACCP] Image failed to load at index:', index);
      
      const currentData = this.store.get(this.id);
      const expectedImageCount = currentData?.imageCount || 0;
      
      if (navigator.onLine && expectedImageCount > 0 && !this.imageLoadInProgress) {
        const currentLoadedCount = (currentData?.haccpImages || []).length;
        this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, currentLoadedCount - 1);
      }
      
      this.removeFailedImageFromDisplay(index);
    } catch (e) {
      console.warn('Failed to handle broken image removal:', e);
    }
  }
  
  /**
   * Remove failed-to-load image from display WITHOUT marking it for backend deletion
   * This is different from removeHaccpImage which marks images for deletion
   */
  private removeFailedImageFromDisplay(index: number): void {
    const currentImages = this.store.get(this.id)?.haccpImages || [];
    const currentFiles = this.store.get(this.id)?.haccpImageFiles || [];
    
    const actualIndex = this.findActualImageIndex(index, currentImages);
    if (actualIndex === -1) {
      console.warn('🧾 [HACCP] Could not find failed image at displayed index:', index);
      return;
    }
    
    // Just remove from display array - don't add to deletedImageFilenames
    const nextImages = currentImages.filter((_, i) => i !== actualIndex);
    
    this.store.update(this.id, {
      haccpImages: nextImages,
      haccpImageFiles: currentFiles
    });
    
    console.log('🧾 [HACCP] Removed failed image from display (not marked for deletion) at index:', actualIndex);
  }

  openHaccpImagePreview(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.haccpImagePreviewIndex = index;
    this.haccpImagePreviewVisible = true;
    this.updatePreviewImageSrc(index);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    // Add keyboard listener for Escape key
    document.addEventListener('keydown', this.handlePreviewKeydown);
  }

  private readonly handlePreviewKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.haccpImagePreviewVisible) {
      this.closeHaccpImagePreview();
    } else if (event.key === 'ArrowLeft' && this.haccpImagePreviewVisible) {
      this.previousHaccpImage();
    } else if (event.key === 'ArrowRight' && this.haccpImagePreviewVisible) {
      this.nextHaccpImage();
    }
  }

  closeHaccpImagePreview(): void {
    this.haccpImagePreviewVisible = false;
    if (this.previewImageSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewImageSrc);
    }
    this.previewImageSrc = null;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handlePreviewKeydown);
  }

  nextHaccpImage(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const images = this.displayedHaccpImages;
    if (images.length > 0) {
      const nextIndex = (this.haccpImagePreviewIndex + 1) % images.length;
      this.haccpImagePreviewIndex = nextIndex;
      this.updatePreviewImageSrc(nextIndex);
    }
  }

  previousHaccpImage(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const images = this.displayedHaccpImages;
    if (images.length > 0) {
      const prevIndex = (this.haccpImagePreviewIndex - 1 + images.length) % images.length;
      this.haccpImagePreviewIndex = prevIndex;
      this.updatePreviewImageSrc(prevIndex);
    }
  }

  private updatePreviewImageSrc(index: number): void {
    if (this.previewImageSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewImageSrc);
    }
    const url = this.displayedHaccpImages[index];
    const cachedBlob = this.imageBlobCache.get(url);
    if (cachedBlob) {
      this.previewImageSrc = URL.createObjectURL(cachedBlob);
    } else {
      this.previewImageSrc = url;
    }
  }

  get currentPreviewImage(): string {
    const images = this.displayedHaccpImages;
    return images[this.haccpImagePreviewIndex] || '';
  }

  get hasMultipleImages(): boolean {
    return this.displayedHaccpImages.length > 1;
  }

  async setAuthorisedBy(value: string): Promise<void> {
    await this.setAuthorisedByInternal(value, { toggleLoading: true });
  }

  private closeAuthorPicker(): void {
    this.authorOpen = false;
    this.authorSearch = '';
  }

  /**
   * POST plant-scoped authorizer for transferred Scheduler when the name is not in authorisedByMap.
   * Transferred loads often have no GrowerAccount yet; scope by destination plant (roleId 4).
   */
  private async resolveAuthorisedByViaTransferredSchedulerApi(
    trimmed: string,
    opts: { toggleLoading: boolean }
  ): Promise<void> {
    const plantId = this.resolveTransferredDestinationPlantId();
    if (plantId <= 0) {
      globalThis.alert(
        'Cannot determine the destination plant for this transferred load. ' +
          'Ensure the vendor number includes the plant code (for example IS0120), ' +
          'reload the passport, then pick Authorised By again.'
      );
      this.closeAuthorPicker();
      return;
    }

    try {
      if (opts.toggleLoading) {
        this.isLoading.set(true);
      }
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers/add`;
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      const body = {
        authorizerName: trimmed,
        plantId,
        roleId: 4,
        usePlantAuthorizerScope: true
      };

      const response = await firstValueFrom(
        this.http.post<any>(apiUrl, body, { headers, withCredentials: true })
      );

      const realId =
        response.authorizerId ||
        response.id ||
        response.authorizerID ||
        response.newAuthorizerID;

      if (realId) {
        this.authorisedByMap.set(trimmed, realId);
        this.store.update(this.id, {
          authorisedBy: trimmed,
          authorisedByUserID: realId
        });
      } else {
        this.store.update(this.id, { authorisedBy: trimmed });
      }
    } catch (error) {
      console.warn('Failed to auto-create authorizer', error);
      globalThis.alert(
        `Failed to save authorizer "${trimmed}": ${this.extractAuthorizerErrorMessage(error)}`
      );
      // Do NOT write the name into the store — onSubmit relies on the store
      // reflecting the successful add to decide whether to proceed.
    } finally {
      if (opts.toggleLoading) {
        this.isLoading.set(false);
      }
    }
  }

  /**
   * Internal variant of setAuthorisedBy. When `toggleLoading` is false the
   * outer caller (e.g. onSubmit) owns the isLoading signal and this method
   * will not flip it — preventing the submit flow from briefly showing the
   * "not loading" state and avoiding recursive isLoading toggles.
   */
  private async setAuthorisedByInternal(
    value: string,
    opts: { toggleLoading: boolean }
  ): Promise<void> {
    const trimmed = (value || '').trim();

    this.authorisedByUserPicked = true;

    if (!trimmed) {
      this.store.update(this.id, {
        authorisedBy: '',
        authorisedByUserID: undefined
      });
      this.closeAuthorPicker();
      return;
    }

    const mappedId = this.authorisedByMap.get(trimmed);

    if (mappedId && mappedId > 0) {
      this.store.update(this.id, {
        authorisedBy: trimmed,
        authorisedByUserID: mappedId
      });
      this.closeAuthorPicker();
      return;
    }

    const isTransferredScheduler = this.isTransferredLoad && this.persona === 'Scheduler';
    if (isTransferredScheduler) {
      await this.resolveAuthorisedByViaTransferredSchedulerApi(trimmed, opts);
    } else {
      this.store.update(this.id, {
        authorisedBy: trimmed,
        authorisedByUserID: undefined
      });
    }

    this.closeAuthorPicker();
  }

  /**
   * Extracts a user-facing message from any error thrown by an authorizer API call.
   * Priority order:
   *  1. Known backend error codes mapped to friendly strings
   *  2. Backend-provided message field (body.message)
   *  3. HTTP status text
   *  4. Error.message (for true Error instances)
   *  5. Generic fallback
   *
   * Needed because Angular's HttpErrorResponse is NOT an instance of Error,
   * so `error instanceof Error` is false and the raw backend message was
   * being hidden behind a generic "Unknown error" string.
   */
  private extractAuthorizerErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { error?: string; message?: string } | null | undefined;
      const backendCode = body?.error;
      const backendMessage = body?.message;

      const friendlyByCode: Record<string, string> = {
        no_grower_on_passport: 'This passport has no grower assigned yet. Please assign a grower before adding an authorizer.',
        no_station_on_passport: 'This passport has no transloading station assigned yet. Please assign one before adding an authorizer.',
        no_plant_context: 'This transferred load has no destination plant recorded yet. Please check the vendor number before adding an authorizer.',
        insert_failed: backendMessage ?? 'The authorizer could not be saved. Please check that the passport has a grower, transloading station, or plant context assigned.',
        invalid_request: backendMessage ?? 'The authorizer request was invalid.',
        internal_error: backendMessage ?? 'A server error occurred while saving the authorizer.',
      };

      if (backendCode && friendlyByCode[backendCode]) {
        return friendlyByCode[backendCode];
      }
      if (backendMessage) return backendMessage;
      if (error.statusText) return `${error.status} ${error.statusText}`;
      return `HTTP ${error.status}`;
    }

    if (error instanceof Error) return error.message;
    return 'Unknown error';
  }

  async addAuthorisedBy(value: string): Promise<void> {
    if (this.isLoading() || this.isAddingAuthorizer()) {
      return;
    }
    await this.addAuthorisedByInternal(value, { toggleLoading: true });
  }

  /**
   * Internal helper that performs the authorizer add without the outer `isLoading()` guard.
   * Use this when the caller (e.g. `onSubmit`) has already set `isLoading=true` and still
   * needs the add to execute. Set `toggleLoading=false` to leave `isLoading` untouched so
   * the outer caller can manage it.
   */
  private async addAuthorisedByInternal(
    value: string,
    opts: { toggleLoading: boolean }
  ): Promise<void> {
    const trimmed = (value || '').trim();
    if (!trimmed) return;

    const nameExists = this.authorisedByOptions.some(o => o.toLowerCase() === trimmed.toLowerCase());
    if (nameExists) {
      globalThis.alert(`Authorizer "${trimmed}" already exists in the list.`);
      this.authorSearch = '';
      return;
    }

    this.isAddingAuthorizer.set(true);
    if (opts.toggleLoading) {
      this.isLoading.set(true);
    }

    const userId = localStorage.getItem('userID');
    if (!userId) {
      console.warn('User ID not found in localStorage');
      this.isAddingAuthorizer.set(false);
      if (opts.toggleLoading) {
        this.isLoading.set(false);
      }
      return;
    }

    const userIdNum = Number.parseInt(String(userId), 10);
    const isMobileMode = globalThis.window !== undefined && globalThis.window.innerWidth < 768;
    const isOffline = !navigator.onLine;

    const canUseOfflineMode = isMobileMode && this.isGrowerOrTransloader && isOffline;

    try {
      if (canUseOfflineMode) {
        await this.addAuthoriserOffline(trimmed, userIdNum);
      } else {
        await this.addAuthoriserOnline(trimmed, userIdNum);
      }
    } catch (error) {
      if (!isOffline && isMobileMode && this.isGrowerOrTransloader && this.isOfflineError(error)) {
        console.log('📴 API call failed, falling back to offline mode');
        await this.addAuthoriserOffline(trimmed, userIdNum);
        return;
      }

      console.warn('Failed to add authoriser:', error);
      globalThis.alert(`Failed to add authorizer: ${this.extractAuthorizerErrorMessage(error)}`);
      this.authorSearch = '';
    } finally {
      this.isAddingAuthorizer.set(false);
      if (opts.toggleLoading) {
        this.isLoading.set(false);
      }
    }
  }

  /**
   * Adds an authorizer in offline mode (Grower/Transloader mobile only)
   * - For Grower: stores userId → will sync with RoleID = 2
   * - For Transloader: stores passportId → will sync with RoleID = 3
   */
  private async addAuthoriserOffline(trimmed: string, userIdNum: number): Promise<void> {
    console.log('📴 Offline mode: Storing authorizer locally');
    
    // Add to current list immediately with temporary ID
    this.authorisedByOptions.push(trimmed);
    this.authorisedByMap.set(trimmed, -1); // -1 indicates pending sync
    
    // Get current cached list
    const currentList = await this.offlineStorage.getAuthorisedUsers(userIdNum) || [];
    
    // Create new authorizer object
    // For Transloader: include passportId so sync knows to use passportId-based endpoint
    const newAuthorizer: any = {
      authorizerId: -1,              // Temporary ID
      authorizerName: trimmed,       // The name
      userId: userIdNum,
      isPendingSync: true,           // Flag: needs sync when online
      addedOffline: true,            // Track: added while offline
      timestamp: Date.now()          // When it was added
    };
    
    if (this.isTransloader && this.id) {
      newAuthorizer.passportId = Number.parseInt(this.id, 10);
      console.log(`📴 Offline Transloader authorizer "${trimmed}" with passportId: ${this.id}`);
    } else if (this.isGrower && this.id) {
      newAuthorizer.passportId = Number.parseInt(this.id, 10);
      newAuthorizer.roleId = 2;
      console.log(`📴 Offline Grower account-level authorizer "${trimmed}" with passportId: ${this.id}`);
    } else {
      console.log(`📴 Offline authorizer "${trimmed}" with userId: ${userIdNum}`);
    }
    
    // Add to list (handles multiple authorizers)
    currentList.push(newAuthorizer);
    
    // Save to IndexedDB
    await this.offlineStorage.saveAuthorisedUsers(userIdNum, currentList);

    // Set as selected and close dropdown. Await the internal setter so the
    // store is updated synchronously with the rest of the add flow.
    await this.setAuthorisedByInternal(trimmed, { toggleLoading: false });
    this.authorSearch = '';
  }

  /**
   * Adds an authorizer in online mode (API call)
   * - For Grower: sends userId → backend adds with RoleID = 2
   * - For Transloader: sends passportId → backend adds with RoleID = 3 for all transloaders at station
   */
  private async addAuthoriserOnline(trimmed: string, userIdNum: number): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers/add`;
    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };
    
    let body: {
      authorizerName: string;
      userId?: number;
      passportId?: number;
      roleId?: number;
      plantId?: number;
      usePlantAuthorizerScope?: boolean;
    };

    const transferredPlantId =
      this.isTransferredLoad && this.isScheduler && this.persona === 'Scheduler'
        ? this.getTransferredPlantId()
        : 0;

    if (transferredPlantId > 0) {
      // Transferred load, Scheduler persona. No GrowerAccount/Station on the passport
      // yet — scope the new Authorizer row to the destination Plant instead.
      body = {
        authorizerName: trimmed,
        plantId: transferredPlantId,
        roleId: 4,
        usePlantAuthorizerScope: true
      };
      console.log(`📤 Adding plant-scoped authorizer "${trimmed}" via plantId: ${transferredPlantId}`);
    } else if (this.isTransferredLoad && this.isScheduler && this.persona === 'Scheduler') {
      globalThis.alert(
        'Cannot determine the destination plant for this transferred load. ' +
          'Ensure the vendor number includes the plant code (for example IS0120), reload the passport, then add the authorizer again.'
      );
      return;
    } else if (this.isTransloader && this.id) {
      body = {
        authorizerName: trimmed,
        passportId: Number.parseInt(this.id, 10)
      };
      console.log(`📤 Adding Transloader authorizer "${trimmed}" via passportId: ${this.id}`);
    } else if ((this.isGrower || (this.isScheduler && (this.persona === 'Grower' || this.persona === 'Scheduler'))) && this.id) {
      body = {
        authorizerName: trimmed,
        passportId: Number.parseInt(this.id, 10),
        roleId: 2
      };
      console.log(`📤 Adding Grower account-level authorizer "${trimmed}" via passportId: ${this.id}`);
    } else {
      body = {
        authorizerName: trimmed,
        userId: userIdNum
      };
      console.log(`📤 Adding authorizer "${trimmed}" via userId: ${userIdNum}`);
    }
    
    const response = await firstValueFrom(this.http.post<any>(apiUrl, body, { headers, withCredentials: true }));
    
    // Update map with real ID from response
    const realId = response.authorizerId || response.id || response.authorizerID;
    if (realId) {
      this.authorisedByMap.set(trimmed, realId);
    }
    
    // Reload the authorizer list from backend to ensure consistency
    await this.loadAuthorisedByOptions();
    
    // Restore the newly added entry if reload cleared it
    // (the add may not yet be reflected in the GET response)
    if (realId && !this.authorisedByMap.has(trimmed)) {
      this.authorisedByMap.set(trimmed, realId);
    }
    if (!this.authorisedByOptions.includes(trimmed)) {
      this.authorisedByOptions.push(trimmed);
    }

    // Await the internal setter so the store is updated before the caller
    // (e.g. onSubmit) reads authorisedBy/authorisedByUserID off the store.
    // Pass toggleLoading=false: whoever owns this call stack (addAuthorisedByInternal
    // or the public addAuthorisedBy) already manages isLoading.
    await this.setAuthorisedByInternal(trimmed, { toggleLoading: false });
    this.authorSearch = '';
  }

  /**
   * Syncs all pending authorizers and updates the authorisedByMap with real IDs
   * Returns true if all pending authorizers were synced successfully
   */
  async syncPendingAuthorizers(): Promise<boolean> {
    return await this.authorizerSync.syncPendingAuthorizers();
  }

  get loadingValue(): string {
    const v = this.store.get(this.id)?.loadingDateTime;
    // Convert UTC to local time for datetime-local input
    if (v && v.length >= 16) {
      return this.dateTimeHelper.convertUtcToLocal(v);
    }
    
    // Fallback: generate current date/time (should rarely be needed now)
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const isoLocal = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return isoLocal;
  }

  get loadingFormatted(): string {
    // Get UTC value from store and convert to local time
    const v = this.store.get(this.id)?.loadingDateTime;
    
    // If no value, return empty string (will show "Select" placeholder in desktop view)
    if (!v || v.trim() === '') {
      return '';
    }
    
    const iso = this.dateTimeHelper.convertUtcToLocal(v); // yyyy-MM-ddTHH:mm (local time)
    
    // Validate the ISO string format
    if (!iso?.includes('T')) {
      return '';
    }
    
    const [datePart, timePart] = iso.split('T');
    const [yyyy, mm, dd] = datePart.split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const yy = String(yyyy).slice(-2);
    const mon = months[(mm || 1) - 1] || '';
    // Add ordinal suffix to day
    const dayWithOrdinal = this.getDayWithOrdinal(dd);
    return `${dayWithOrdinal}-${mon}-${yy} | ${timePart}`;
  }

  private getDayWithOrdinal(day: number): string {
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    const lastDigit = day % 10;
    let suffix: string;
    if (lastDigit === 1) {
      suffix = 'st';
    } else if (lastDigit === 2) {
      suffix = 'nd';
    } else if (lastDigit === 3) {
      suffix = 'rd';
    } else {
      suffix = 'th';
    }
    return `${day}${suffix}`;
  }

  openPicker(input: HTMLInputElement): void {
    input.showPicker?.();
  }

  onHaccpCameraClick(input: HTMLInputElement): void {
    const imgs = this.store.get(this.id)?.haccpImages || [];
    const realImagesCount = imgs.filter((img: string) => img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')).length;
    if (realImagesCount >= 3) return;
    // Note: HACCP check is now computed dynamically based on image count
    // Adding an image will automatically set computedHaccpCheck to 'Yes'
    this.haccpChoiceOpen = true;
  }

  onHaccpImageSelected(ev: Event): void {
    const file = (ev as unknown as { target?: { files?: FileList } })?.target?.files?.[0] || null;
    this.addHaccpImage(file);
    const inputEl = (ev as unknown as { target?: HTMLInputElement })?.target;
    if (inputEl?.type === 'file') { inputEl.value = ''; }
  }

  onHaccpCameraIconClick(event: Event): void {
    event.stopPropagation();
    this.onHaccpCameraClick(document.getElementById('haccp-camera') as HTMLInputElement);
  }

  chooseHaccpCapture(which: 'camera' | 'file', cameraInput: HTMLInputElement, fileInput: HTMLInputElement): void {
    this.triggerFileInput(which, cameraInput, fileInput);
  }

  /**
   * Generic helper to trigger camera or file input clicks
   */
  private triggerFileInput(which: 'camera' | 'file', cameraInput: HTMLInputElement, fileInput: HTMLInputElement): void {
    if (which === 'camera') {
      cameraInput.click();
    } else {
      fileInput.click();
    }
  }

  showHaccpInfo(event: Event): void {
    event.stopPropagation();
    this.haccpInfoVisible = true;
    globalThis.setTimeout(() => { this.haccpInfoVisible = false; }, 4000);
  }

  get canSubmit(): boolean {
    const s = this.store.get(this.id);
    if (!s) return false;
    
    if (this.isGatehouse) {
      return true; // Proceed always enabled for Gatehouse
    }
    
    if (this.isScheduler) {
      return this.canSchedulerSubmit(s);
    }
    
    // For Grower/Transloader in edit mode
    return this.canGrowerTransloaderSubmit(s);
  }

  private canSchedulerSubmit(s: any): boolean {
      const yardOk = !!this.selectedShuntingYard;
    
      // In view mode (before clicking Edit), only yard is required
      if (!this.schedulerEditEnabled) {
        return yardOk;
      }
    
      // In edit mode, full validation based on chosen persona
      const personaChosen = ['grower','transloader','scheduler'].some(p => (this.persona || '').toLowerCase().includes(p));
      const reasonOk = !!(this.reasonText?.trim());
    const commonFields = this.validateCommonFields(s);
    
      // Origin field is optional for all personas now - "No Origin Field" is a valid selection
      // This applies to both Grower and Transloader when Scheduler is filling in on their behalf
    return commonFields && personaChosen && yardOk && reasonOk;
      }

  private canGrowerTransloaderSubmit(s: any): boolean {
    const commonFields = this.validateCommonFields(s);
    
    // Origin field is now optional for all personas - "No Origin Field" is a valid selection
    // This applies to both Grower and Transloader personas
    return commonFields;
  }
    
  private validateCommonFields(s: any): boolean {
    const hasLoading = (s?.loadingDateTime?.length ?? 0) >= 16;
    const hasBulker = !!(s?.bulkerNumber?.trim());
    const hasAuthorised = !!(s?.authorisedBy?.trim());
    
    // Use computed HACCP check which handles online/offline logic
    const haccpCheck = this.computedHaccpCheck;
    
    // HACCP must be "Yes" (which requires images to be attached)
    // "No" is not a valid entry for submission
    let haccpOk = haccpCheck === 'Yes';
    
    // Option C: Block submit unless real files exist
    if (haccpOk) {
      const hasLocalFiles = (s?.haccpImageFiles?.length ?? 0) > 0;
      const hasBackendImages = (s?.imageCount || 0) > 0 && !hasLocalFiles;
      
      // Also allow if we have data URLs that will be rebuilt in prepareSubmissionData (Option B)
      const hasDataUrls = Boolean(s?.haccpImages?.some((img: string) => img.startsWith('data:')));
      
      haccpOk = hasLocalFiles || hasBackendImages || hasDataUrls;
    }
    
    return hasLoading && hasBulker && hasAuthorised && haccpOk;
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit) return;
    this.isLoading.set(true);
    try {
      if (!(await this.autoResolveAuthorizerFromSearchBeforeSubmit())) {
        return;
      }
      await this.submitPassportFromStore();
    } catch (error: any) {
      await this.handleSubmissionError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * If a new name was typed into the search box but never picked/added, auto-resolve it
   * before submit. Uses internal helpers with toggleLoading=false because onSubmit owns
   * the loading signal.
   */
  private async autoResolveAuthorizerFromSearchBeforeSubmit(): Promise<boolean> {
    if (!this.canAddAuthorizer || !this.authorSearch?.trim()) {
      return true;
    }

    const trimmed = this.authorSearch.trim();
    const currentAuthorisedBy = this.store.get(this.id)?.authorisedBy?.trim() || '';
    if (trimmed === currentAuthorisedBy) {
      return true;
    }

    const exactMatch = this.authorisedByOptions.find(
      o => o.toLowerCase() === trimmed.toLowerCase()
    );

    if (exactMatch) {
      console.log(`Auto-selecting existing authorizer "${exactMatch}" before submit`);
      await this.setAuthorisedByInternal(exactMatch, { toggleLoading: false });
      return true;
    }

    console.log(`Auto-adding authorizer "${trimmed}" before submit`);
    await this.addAuthorisedByInternal(trimmed, { toggleLoading: false });

    // Abort if add failed (e.g. backend rejected due to missing GrowerAccountID).
    return this.store.get(this.id)?.authorisedBy === trimmed;
  }

  private async submitPassportFromStore(): Promise<void> {
    const data = this.store.get(this.id);
    if (!data) {
      return;
    }

    if (!(await this.ensureAuthorisedByUserIdBeforeOnlineSubmit(data))) {
      return;
    }

    const submissionData = this.store.get(this.id);
    if (!submissionData) {
      return;
    }

    this.prepareSubmissionData(submissionData);
    await this.submitToBackend(submissionData);
    await this.handleSuccessfulSubmission();
  }

  private hasValidAuthorizerId(id: number | undefined | null): id is number {
    return id !== undefined && id !== null && id > 0;
  }

  /**
   * When submitting online with an authorizer name but no valid ID, resolve via cache/API
   * (same path as offline sync) before building the submit payload.
   */
  private async ensureAuthorisedByUserIdBeforeOnlineSubmit(data: PassportDetailsModel): Promise<boolean> {
    if (!navigator.onLine) {
      return true;
    }

    const name = data?.authorisedBy?.trim();
    if (!name) {
      return true;
    }

    const currentData = this.store.get(this.id);
    const existingId = this.resolveAuthorisedByUserID(data, currentData);
    if (this.hasValidAuthorizerId(existingId)) {
      if (!this.hasValidAuthorizerId(data.authorisedByUserID)) {
        this.store.update(this.id, { authorisedByUserID: existingId });
      }
      return true;
    }

    this.authorizerSync.setAuthorisedByMap(this.authorisedByMap);
    const { roleId } = this.getAuthenticationData();
    const resolvedId = await this.authorizerSync.resolveAuthorizerIdFromCacheOrApi(name, {
      passportId: this.id,
      roleId,
    });

    if (!this.hasValidAuthorizerId(resolvedId)) {
      this.toastService.warning(
        `Could not resolve authorizer "${name}". Please try adding the authorizer again or check your connection.`,
        7000,
      );
      return false;
    }

    this.authorisedByMap.set(name, resolvedId);
    this.store.update(this.id, {
      authorisedBy: name,
      authorisedByUserID: resolvedId,
    });
    console.log(`✅ Resolved authorizer "${name}" → ID ${resolvedId} before online submit`);
    return true;
  }

  private prepareSubmissionData(data: any): void {
    // Ensure plannedDateTime is preserved from the store (important for Grower persona)
    console.log('📋 Submitting passport with plannedDateTime:', data.plannedDateTime);
    
    // Ensure shunting yard is stored even if empty for Gatehouse
    if (this.isGatehouse) {
      this.store.update(this.id, { destination: this.store.get(this.id)?.destination });
    }

    // Rebuild files from data URLs on submit if haccpImageFiles is empty
    const haccpImageFiles = data.haccpImageFiles || [];
    const haccpImages = data.haccpImages || [];
    
    if (haccpImageFiles.length === 0 && haccpImages.length > 0) {
      const dataUrls = haccpImages.filter((img: string) => img.startsWith('data:'));
      if (dataUrls.length > 0) {
        const newFiles = dataUrls.map((dataUrl: string, index: number) => {
          return this.dataURLtoFile(dataUrl, `offline_image_${index}.jpg`);
        });
        
        // Update the data object directly so submitToBackend uses it
        data.haccpImageFiles = newFiles;
        
        // Also update the store to keep it consistent
        this.store.update(this.id, { haccpImageFiles: newFiles });
        
        console.log(`🔄 Rebuilt ${newFiles.length} File objects from data URLs for submission`);
      }
    }
  }

  private getDataUrlMimeType(dataHeader: string): string | null {
    const colonIndex = dataHeader.indexOf(':');
    if (colonIndex === -1) {
      return null;
    }

    const semicolonIndex = dataHeader.indexOf(';', colonIndex + 1);
    if (semicolonIndex === -1) {
      return null;
    }

    return dataHeader.slice(colonIndex + 1, semicolonIndex);
  }

  private dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = this.getDataUrlMimeType(arr[0]) ?? 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.codePointAt(n) ?? 0;
    }
    return new File([u8arr], filename, { type: mime });
  }

  private async handleSuccessfulSubmission(): Promise<void> {
    this.authorisedByUserPicked = false;
    // Mark as completed and synced
    this.passports.setStatus(this.id, 'completed', true);
    this.saveSubmittedSnapshot();
    
    // Clear any draft once submitted
    await this.clearDraft();
    
    // Clear reason field after successful submission
    this.reasonText = '';
    
    // Show confirmation modal
    this.submitModalVisible = true;
  }

  private async handleSubmissionError(error: any): Promise<void> {
    console.warn('Failed to submit passport to backend:', error);
    
    const isOffline = this.isOfflineError(error);
    
    if (isOffline) {
      await this.handleOfflineSubmission();
      return;
    }

    const errorCode = error?.error?.error;

    if (errorCode === 'stale_invoiced_load') {
      this.toastService.warning(
        error?.error?.message ??
          'This load has been invoiced. Refresh the page to load the latest data; your changes were not saved.',
        7000,
      );
      return;
    }

    if (errorCode === 'images_required') {
      this.toastService.warning(
        error?.error?.message
          ?? 'At least one HACCP image is required before you can submit this passport.',
        6000,
      );
      return;
    }

    if (errorCode === 'workflow_trigger_failed' || errorCode === 'image_processing_failed') {
      this.toastService.warning(
        'Connection to the processing service failed. Your submission has been reverted to its original state. Please try again later.',
        8000
      );
      return;
    }

    // Extract error message from response
    let errorMessage = 'Failed to submit passport. Please try again.';
    if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    alert(errorMessage);
  }

  private isOfflineError(error: any): boolean {
    return !navigator.onLine || 
      error?.message?.toLowerCase().includes('network') ||
      error?.message?.toLowerCase().includes('failed to fetch') ||
      error?.status === 0;
  }

  private async handleOfflineSubmission(): Promise<void> {
    // Desktop mode: Don't save offline, just show alert
    const isDesktopMode = window.innerWidth >= 768;
    if (isDesktopMode) {
      alert('No internet connection. Please connect to the internet to submit your passport.');
      return;
    }

    // Mobile mode: Save offline for later sync
    const saveOutcome = await this.saveOutgoingPassportToIndexedDB();
    if (saveOutcome !== 'saved') {
      this.toastService.error(this.getOutgoingIndexedDbSaveFailureMessage(saveOutcome), 7000);
      return;
    }

    this.markPassportAsOutgoing();
    
    console.log('📴 Device is offline - passport marked as outgoing for resync');
    console.log('ℹ️ Draft will be synced (non-image fields only) and outgoing submission will handle images');
    
    // Navigate to persona home and switch to outgoing tab
    this.ui.setStatusSelected('outgoing');
    await this.navigateToPersonaHome();
    
    this.toastService.warning('Device is offline. Passport will be submitted when you click Resync after coming back online.', 7000);
  }

  private getOutgoingIndexedDbSaveFailureMessage(outcome: OutgoingIndexedDbSaveOutcome): string {
    if (outcome === 'read_failed') {
      return 'Could not read one or more HACCP photos. Try again or use JPEG/PNG before saving offline.';
    }
    if (outcome === 'blocked_no_haccp') {
      return 'Add at least one HACCP photo before saving this submission offline.';
    }
    return 'Could not save the submission offline. Please try again.';
  }

  private countHaccpDataUrlImages(haccpImages: string[] | undefined): number {
    return (haccpImages ?? []).filter(
      (img: string) => typeof img === 'string' && img.startsWith('data:')
    ).length;
  }

  private mergeHaccpImagesWithEncodedFiles(currentImages: string[], encodedDataUrls: string[]): string[] {
    const nonPlaceholderImages: string[] = [];
    const placeholderImages: string[] = [];
    for (const img of currentImages) {
      if (img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')) {
        nonPlaceholderImages.push(img);
      } else {
        placeholderImages.push(img);
      }
    }
    const backendOrRemote = nonPlaceholderImages.filter(
      (img: string) => !img.startsWith('data:') && !img.startsWith('blob:')
    );
    return [...backendOrRemote, ...encodedDataUrls, ...placeholderImages];
  }

  private async encodeHaccpImageFilesToDataUrls(files: File[]): Promise<string[] | null> {
    const dataUrls: string[] = [];
    for (const file of files.slice(0, 3)) {
      try {
        const dataUrl = await this.fileToDataUrl(file);
        if (!dataUrl.startsWith('data:')) {
          console.warn(
            `Offline HACCP: empty data URL after read for passport ${this.id} (${file.name}, ${file.type}, ${file.size} bytes)`
          );
          return null;
        }
        dataUrls.push(dataUrl);
      } catch (error: unknown) {
        const reason = error instanceof Error ? error.message : String(error);
        console.warn(
          `Offline HACCP: failed to read image for passport ${this.id} (${file.name}, ${file.type}, ${file.size} bytes): ${reason}`
        );
        return null;
      }
    }
    return dataUrls;
  }

  private outgoingHaccpPayloadIsValid(
    haccpImages: string[],
    passportData: PassportDetailsModel | undefined
  ): boolean {
    if (this.countHaccpDataUrlImages(haccpImages) > 0) {
      return true;
    }
    const backendImageCount = passportData?.imageCount ?? 0;
    const deletedCount = passportData?.deletedImageFilenames?.length ?? 0;
    return backendImageCount - deletedCount > 0;
  }

  private async saveOutgoingPassportToIndexedDB(): Promise<OutgoingIndexedDbSaveOutcome> {
    try {
      let passportData = this.store.get(this.id);
      
      // Prefer stored authorisedByUserID from API (preserves original Grower/Transloader's authorizer)
      // Fall back to authorisedByMap lookup for new selections made by current user
      let authorisedByUserID: number | undefined = passportData?.authorisedByUserID;
      let hasPendingAuthorizer = false;
      
      // Only look up from map if no stored ID exists (for new selections)
      if (!authorisedByUserID && passportData?.authorisedBy?.trim()) {
        const mappedId = this.authorisedByMap.get(passportData.authorisedBy.trim());
        // Only use ID if it's a valid positive number (not pending sync)
        if (mappedId && mappedId > 0) {
          authorisedByUserID = mappedId;
        } else if (mappedId === -1) {
          // Pending sync - will be resolved when authorizers are synced
          hasPendingAuthorizer = true;
          authorisedByUserID = undefined;
        }
      }
      
      // Log what we're saving for debugging
      const deletedFilenames = passportData?.deletedImageFilenames || [];
      let haccpImages = passportData?.haccpImages || [];
      const haccpFiles = passportData?.haccpImageFiles ?? [];

      if (haccpFiles.length > 0) {
        const encoded = await this.encodeHaccpImageFilesToDataUrls(haccpFiles);
        if (encoded === null) {
          return 'read_failed';
        }
        haccpImages = this.mergeHaccpImagesWithEncodedFiles(haccpImages, encoded);
        this.store.update(this.id, { haccpImages });
        passportData = this.store.get(this.id);
      }

      const placeholders = haccpImages.filter((img: string) => img?.startsWith('PLACEHOLDER:'));
      const haccpImageLinks = passportData?.haccpImageLinks || [];
      const dataUrlCount = this.countHaccpDataUrlImages(haccpImages);
      
      // IMPORTANT: Ensure deletedImageFilenames only contains filenames that were explicitly deleted
      const finalDeletedFilenames = [...deletedFilenames];

      if (!this.outgoingHaccpPayloadIsValid(haccpImages, passportData)) {
        console.warn(
          `Offline HACCP: save blocked for passport ${this.id} — no data URLs after encoding (files=${haccpFiles.length}, dataUrls=${dataUrlCount})`
        );
        return 'blocked_no_haccp';
      }
      
      console.log(`📤 [OFFLINE SUBMIT] Saving outgoing passport:`, {
        deletedImageFilenames: finalDeletedFilenames,
        placeholdersCount: placeholders.length,
        haccpImagesCount: haccpImages.length,
        dataUrlCount,
        haccpImageLinksCount: haccpImageLinks.length,
        haccpImageFilesCount: haccpFiles.length
      });
      
      // Omit haccpImageFiles — resync sends images as base64 from haccpImages data URLs only.
      // Raw File objects break IndexedDB put on iOS Safari. Persist data URLs + placeholders only.
      const persistedHaccpImages = haccpImages.filter(
        (img: string) =>
          img?.startsWith('data:') ||
          img?.startsWith('PLACEHOLDER:') ||
          (img?.trim().length > 0 && !img.startsWith('blob:'))
      );
      const dataToSave: Partial<PassportDetailsModel> = {
        ...passportData,
        haccpImages: persistedHaccpImages,
        deletedImageFilenames: finalDeletedFilenames,
        haccpCheck: this.computedHaccpCheck
      };
      delete dataToSave.haccpImageFiles;
      
      const submissionData = {
        passportId: this.id,
        data: dataToSave,
        roleId: this.getAuthenticationData().roleId,
        submittedByRoleId: this.getAuthenticationData().submittedByRoleId,
        isSchedulerViewMode: this.isScheduler && !this.schedulerEditEnabled,
        authorisedByUserID: authorisedByUserID,
        hasPendingAuthorizer: hasPendingAuthorizer
      };
      
      await this.offlineStorage.saveOutgoingPassport(this.id, submissionData);
      console.log('✅ Saved outgoing passport to IndexedDB', authorisedByUserID ? `with authorisedByUserID: ${authorisedByUserID}` : 'without authorisedByUserID');
      return 'saved';
    } catch (saveError: unknown) {
      const reason = saveError instanceof Error ? saveError.message : String(saveError);
      console.warn(`Offline HACCP: IndexedDB save failed for passport ${this.id}: ${reason}`);
      return 'error';
    }
  }

  private markPassportAsOutgoing(): void {
    // Get existing record or create new one with basic data
    const existingRecord = this.passports.getById(this.id);
    const passportData = this.store.get(this.id);
    
    if (existingRecord) {
      // Update existing record
      this.passports.setStatus(this.id, 'outgoing', false);
    } else {
      // Create new record with available data
      this.passports.upsert({
        id: this.id,
        status: 'outgoing',
        isSynced: false,
        loadNumber: passportData?.loadNumber || this.id,
        variety: passportData?.variety,
        haulier: passportData?.haulier,
        origin: passportData?.origin,
        destination: passportData?.destination,
        apnName: passportData?.apnName,
        vendorName: passportData?.vendorName,
        vendorNumber: passportData?.vendorNumber
      });
    }
  }

  private getPersonaLabelForLoadPassportData(): string {
    const p = (localStorage.getItem('persona') || this.persona || 'grower').toLowerCase();
    if (p.includes('transload')) return 'Transloader';
    if (p.includes('schedule')) return 'Scheduler';
    if (p.includes('gate')) return 'Gatehouse';
    return 'Grower';
  }

  /** After API rejects draft/submit because the load was invoiced: clear local cache and reload from server. */
  private async refreshAfterInvoicedRejection(): Promise<void> {
    try {
      await this.offlineStorage.deleteAllDataForPassport(Number.parseInt(this.id, 10));
    } catch {
      /* ignore */
    }
    await this.clearDraft();
    await this.refreshPassportsForCurrentPersona();
    const personaLower = (localStorage.getItem('persona') || this.persona || 'grower').toLowerCase();
    if (personaLower.includes('schedule')) {
      const reloadPersona = this.isTransferredLoad ? 'Grower' : 'Scheduler';
      await this.loadPassportDataForPersona(reloadPersona).catch(err => {
        console.warn('Failed to reload passport after invoiced rejection:', err);
      });
      if (this.isTransferredLoad) {
        await this.loadAuthorisedByOptions();
      }
    } else {
      await this.loadPassportDataForPersona(this.getPersonaLabelForLoadPassportData()).catch(err => {
        console.warn('Failed to reload passport after invoiced rejection:', err);
      });
    }
  }

  private async refreshPassportsForCurrentPersona(): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const userID = localStorage.getItem('userID');
    const persona = (localStorage.getItem('persona') || this.persona || 'grower').toLowerCase();
    if (!token || !userID || !navigator.onLine) return;

    // Audit is read-only; it uses a dedicated criteria-based search endpoint,
    // not the generic user+role endpoint which applies plant-based filtering.
    // Calling the generic endpoint here would overwrite the audit's cross-plant
    // search results with an empty or plant-scoped set, so bail out.
    if (persona.includes('audit')) {
      console.log('🔃 Skipping passport list refresh for Audit persona (read-only, uses dedicated search)');
      return;
    }

    const roleId = this.getPersonaId(persona);
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
    const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };

    console.log('🔃 Refreshing passports after submit from:', apiUrl);
    const passports = await firstValueFrom(this.http.get<any[]>(apiUrl, { headers }));

    // Map to PassportService records based on persona
    const isTransloader = persona.includes('transload');
    const toStatus = (statusCode?: string): 'pending' | 'completed' => {
      const code = (statusCode || '').toUpperCase();
      if (isTransloader) return code === 'IN_TRANSLOADING_PROCESS' ? 'pending' : 'completed';
      return code === 'AWAITING_GROWER_ACTION' ? 'pending' : 'completed';
    };

    const records = passports.map(p => ({ id: String(p.passportID), status: toStatus(p.statusCode), isSynced: true }));
    this.passports.records.set(records);

    // Update key details for cells
    for (const p of passports) {
      this.store.upsert({
        id: String(p.passportID),
        loadNumber: p.loadNumber || String(p.passportID),
        vendorName: p.vendorName || '',
        vendorNumber: p.vendorNumber || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        variety: p.variety || '',
        destination: p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        origin: (p.transferFlag && p.originName)
          ? p.originName
          : this.formatOriginDisplayName(p.originName || '', p.rawSourceType || ''),
        bulkerNumber: p.trailerNumber || '',
        bulkerMain: p.trailerMain || '',
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
        loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
        haccpCheck: (p.haccpCheck === true ? 'Yes' : 'No'),
        haccpImages: [],
        haccpImageLinks: p.haccpImageLinks || [],
        imageCount: p.imageCount || 0,  // Store image count from backend
        deletedImageFilenames: [],  // Clear old editing state when refreshing from API
        haccpImageFiles: [],  // Clear old editing state when refreshing from API
        authorisedBy: p.authorizerName || '',
        authorisedByUserID: p.authorisedByUserID || p.authorizerId || undefined, // Store authorizer ID from API
        plantID: this.getPlantID(p),
        plannedDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.plannedDateTime || ''),
        loadStatusName: p.loadStatusName || '',
        hasSeenPcrChanges: p.hasSeenPcrChanges,
        statusCode: p.statusCode || '',
        transferFlag: p.transferFlag ?? false
      });

    }
  }

  private async submitToBackend(data: PassportDetailsModel): Promise<void> {
    const { token, tokenType, userID, roleId, submittedByRoleId } = this.getAuthenticationData();
    
    if (!token || !userID) {
      throw new Error('No authentication token or user ID found');
    }
    
    // Backend expects path parameters
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/submit/${this.id}/role/${roleId}/submittedBy/${submittedByRoleId}`;
    const formData = new FormData();
    const isSchedulerViewMode = this.isScheduler && !this.schedulerEditEnabled;
    
    // Build form data based on mode
    if (isSchedulerViewMode) {
      this.buildSchedulerViewModeFormData(formData);
    } else {
      this.buildNormalSubmissionFormData(formData, data);
    }

    // Process images (async: images are read into base64 text fields, not file parts)
    const { deletedImageFilenames, filesToUpload } = await this.processImages(formData, data, isSchedulerViewMode);

    // Submit to API
    const headers = { 'Authorization': `${tokenType} ${token}` };
    this.logSubmissionDetails(data, isSchedulerViewMode, deletedImageFilenames, filesToUpload, apiUrl);
    
    const response = await firstValueFrom(this.http.post<any>(apiUrl, formData, { headers, withCredentials: true }));
    
    // Check for errors in the response body
    if (response?.draft?.status === 'Error' || response?.error) {
      const errorMessage = response?.draft?.message || response?.message || response?.error || 'Unknown error occurred';
      console.warn('❌ Submission failed:', errorMessage);
      throw new Error(errorMessage);
    }
    
    console.log('Passport submitted successfully:', response);
    
    // Clear deletion list and file uploads after successful submission
    // AND update imageCount with the new count from backend
    const newImageCount = response.imageCount ?? response.draft?.imageCount ?? 0;
    this.store.update(this.id, {
      deletedImageFilenames: [],
      haccpImageFiles: [],
      haccpImages: [],
      imageCount: newImageCount,  // Update with actual backend count
      haccpImageLinks: response.haccpImageLinks || []  // Update image links from backend
    });
    
    console.log('📊 Backend response: Deleted=' + (response.deletedImages?.length || 0) + ', Added=' + (response.addedImages?.length || 0) + ', Total=' + newImageCount);
    
    this.lastLoadedLinksKey = '';
    await this.loadImagesIfNeeded();
  }

  private getAuthenticationData() {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const userID = localStorage.getItem('userID');
    // For transferred loads in Scheduler edit mode, submit as Grower (roleId=2)
    // to match how data is loaded and mimic Grower editing behavior
    const roleId = (this.isTransferredLoad && this.persona === 'Scheduler' && this.schedulerEditEnabled)
        ? 2
        : this.getPersonaId(this.persona);
    const loggedInPersona = localStorage.getItem('persona') || 'grower';
    const submittedByRoleId = this.getPersonaId(loggedInPersona);
    
    return { token, tokenType, userID, roleId, submittedByRoleId };
  }

  private buildSchedulerViewModeFormData(formData: FormData): void {
      // Always send ShuntingYardID - empty string for "No Shunting Required"
      if (this.selectedShuntingYardId !== null && this.selectedShuntingYardId !== undefined) {
        formData.append('Request.ShuntingYardID', this.selectedShuntingYardId.toString()); // Backend expects Request.ShuntingYardID
        console.log(`✅ Scheduler view mode: Sending shuntingYardID: ${this.selectedShuntingYardId}`);
      } else {
        formData.append('Request.ShuntingYardID', '');
        console.log(`✅ Scheduler view mode: Sending No Shunting Required (clearing shunting yard)`);
      }
      if (this.reasonText?.trim()) {
        formData.append('Request.Remarks', this.reasonText.trim()); // Backend expects Request.Remarks
        formData.append('Request.Reason', this.reasonText.trim()); // Backend expects Request.Reason
      }
  }

  private buildNormalSubmissionFormData(formData: FormData, data: PassportDetailsModel): void {
    this.appendBasicFields(formData, data);
    this.appendDateTimeFields(formData, data);
    this.appendOriginField(formData, data);
    this.appendShuntingYardFields(formData);
  }

  /**
   * Resolves which AuthorisedByUserID to send: explicit pick, transferred-scheduler list match, or unchanged store value.
   */
  private resolveAuthorisedByUserIdForFormSubmit(
    mapId: number | undefined,
    storedData: PassportDetailsModel | undefined,
    isTransferredScheduler: boolean
  ): number | undefined {
    const positiveMapId = mapId !== undefined && mapId > 0 ? mapId : undefined;
    if (positiveMapId === undefined) {
      return storedData?.authorisedByUserID;
    }
    if (this.authorisedByUserPicked || isTransferredScheduler) {
      return positiveMapId;
    }
    return storedData?.authorisedByUserID;
  }

  /**
   * Generic helper to append basic fields to FormData
   */
  private appendBasicFieldsGeneric(formData: FormData, data: PassportDetailsModel, prefix: string = 'Request.'): void {
    if (data?.bulkerNumber?.trim()) {
      formData.append(`${prefix}${prefix ? 'TrailerNumber' : 'trailerNumber'}`, data.bulkerNumber.trim());
    }
    
    // Use computed HACCP check which reflects current editing state
    const haccpCheck = this.computedHaccpCheck;
    formData.append(`${prefix}${prefix ? 'HaccpCheck' : 'haccpCheck'}`, haccpCheck === 'Yes' ? 'true' : 'false');
    
    const storedData = this.store.get(this.id);
    const mapId = data?.authorisedBy?.trim() ? this.authorisedByMap.get(data.authorisedBy.trim()) : undefined;
    const isTransferredScheduler = this.isTransferredLoad && this.persona === 'Scheduler';

    const authorisedByUserId = this.resolveAuthorisedByUserIdForFormSubmit(
      mapId,
      storedData,
      isTransferredScheduler
    );
    
    if (authorisedByUserId && authorisedByUserId > 0) {
      formData.append(`${prefix}${prefix ? 'AuthorisedByUserID' : 'authorisedByUserID'}`, authorisedByUserId.toString());
    }
  }

  /**
   * Generic helper to append date/time fields to FormData
   */
  private appendDateTimeFieldsGeneric(formData: FormData, data: PassportDetailsModel, prefix: string = 'Request.', mode: string = 'submit'): void {
    if (data?.loadingDateTime) {
      // Ensure UTC timestamp has 'Z' suffix so backend parses it as UTC (not Unspecified)
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.loadingDateTime);
      formData.append(`${prefix}${prefix ? 'LoadingDateTime' : 'loadingDateTime'}`, utcValue);
    }
    
    if (data?.plannedDateTime) {
      console.log(`✅ Including plannedDateTime in ${mode}:`, data.plannedDateTime);
      // Ensure UTC timestamp has 'Z' suffix so backend parses it as UTC (not Unspecified)
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.plannedDateTime);
      formData.append(`${prefix}${prefix ? 'PlannedDateTime' : 'plannedDateTime'}`, utcValue);
    } else {
      console.log(`⚠️ No plannedDateTime found in store for ${mode}`);
    }
  }

  /**
   * Get the correct field name based on prefix (handles capitalization)
   */
  private getOriginFieldName(prefix: string, fieldType: 'OriginName' | 'RawSourceType'): string {
    if (prefix) {
      return `${prefix}${fieldType}`;
    }
    return fieldType === 'OriginName' ? 'originName' : 'rawSourceType';
  }

  /**
   * Parse origin data from a prefixed display name (e.g., "Field: X", "Storage: Y", or "Plant: Z")
   */
  private parseOriginFromDisplayName(displayName: string): { originName: string; rawSourceType: string } | null {
    if (displayName.startsWith('Field: ')) {
      return {
        originName: displayName.substring(7),
        rawSourceType: '2'
      };
    }
    
    if (displayName.startsWith('Storage: ')) {
      return {
        originName: displayName.substring(9),
        rawSourceType: '1'
      };
    }
    
    if (displayName.startsWith('Plant: ')) {
      return {
        originName: displayName.substring(7),
        rawSourceType: '4'
      };
    }
    
    return null;
  }

  /**
   * Append origin name and raw source type to FormData
   */
  private appendOriginData(formData: FormData, prefix: string, originName: string, rawSourceType: string): void {
    formData.append(this.getOriginFieldName(prefix, 'OriginName'), originName);
    formData.append(this.getOriginFieldName(prefix, 'RawSourceType'), rawSourceType);
  }

  /**
   * Generic helper to append origin field to FormData
   * When origin is empty (i.e., "No Origin Field" selected), does NOT append the fields
   * so the backend interprets them as null values for both originName and rawSourceType
   */
  private appendOriginFieldGeneric(formData: FormData, data: PassportDetailsModel, prefix: string = 'Request.', mode: string = 'Submit'): void {
    // For transferred loads, origin is locked/readonly and the SP preserves it via TransferFlag check
    if (this.isOriginLockedForTransfer) {
      console.log(`✅ [${mode}] Origin locked for transferred load - not appending (backend preserves via TransferFlag)`);
      return;
    }

    const originValue = data?.origin?.trim();
    
    // If origin is empty (i.e., "No Origin Field" selected), do not append fields
    // The backend will interpret missing fields as null for both originName and rawSourceType
    if (!originValue) {
      console.log(`✅ [${mode}] No origin selected ("No Origin Field") - not appending origin fields (backend interprets as null)`);
      return;
    }

    // Try to find in originMap (for dropdown selections)
    const originData = this.originMap.get(originValue);
    if (originData) {
      this.appendOriginData(formData, prefix, originData.originName, originData.rawSourceType);
      return;
    }

    // Fallback: Extract from prefixed display name
    const parsedOrigin = this.parseOriginFromDisplayName(originValue);
    if (parsedOrigin) {
      this.appendOriginData(formData, prefix, parsedOrigin.originName, parsedOrigin.rawSourceType);
      console.log(`✅ [${mode}] Extracted origin from display name: ${parsedOrigin.originName} (type: ${parsedOrigin.rawSourceType})`);
    } else {
      console.warn(`⚠️ [${mode}] Could not extract origin data from: "${originValue}"`);
    }
  }

  // Wrappers for backward compatibility
  private appendBasicFields(formData: FormData, data: PassportDetailsModel): void {
    this.appendBasicFieldsGeneric(formData, data, 'Request.');
  }

  private appendDateTimeFields(formData: FormData, data: PassportDetailsModel): void {
    this.appendDateTimeFieldsGeneric(formData, data, 'Request.', 'submission');
  }

  private appendOriginField(formData: FormData, data: PassportDetailsModel): void {
    this.appendOriginFieldGeneric(formData, data, 'Request.', 'Submit');
  }

  private appendShuntingYardFields(formData: FormData): void {
    if (this.isGatehouse || this.isScheduler) {
      // Always send ShuntingYardID - empty string for "No Shunting Required"
      if (this.selectedShuntingYardId !== null && this.selectedShuntingYardId !== undefined) {
        formData.append('Request.ShuntingYardID', this.selectedShuntingYardId.toString()); // Backend expects Request.ShuntingYardID
        console.log(`✅ Including shuntingYardID in submission: ${this.selectedShuntingYardId}`);
      } else {
        formData.append('Request.ShuntingYardID', '');
        console.log(`✅ No shunting yard required - sending empty string to clear`);
      }
      if (this.reasonText?.trim()) {
        formData.append('Request.Remarks', this.reasonText.trim()); // Backend expects Request.Remarks
        // Also add as Reason if not already set
        formData.append('Request.Reason', this.reasonText.trim()); // Backend expects Request.Reason
      }
    }
  }

  /**
   * Reads a File into a base64 data URL. Used to send HACCP images as text fields instead of
   * multipart file parts (works around an iOS 26.5.2 WebKit regression that drops the body of any
   * multipart request containing a file part).
   */
  private fileToDataUrl(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string' && result.startsWith('data:')) {
          resolve(result);
          return;
        }
        reject(new Error('FileReader did not produce a data URL'));
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file'));
      reader.readAsDataURL(file);
    });
  }

  private async processImages(formData: FormData, data: PassportDetailsModel, isSchedulerViewMode: boolean): Promise<{ deletedImageFilenames: string[], filesToUpload: File[] }> {
    if (isSchedulerViewMode) {
      console.log('📤 Scheduler view mode: No image processing, only shunting yard assignment');
      return { deletedImageFilenames: [], filesToUpload: [] };
    }

    const deletedImageFilenames = data.deletedImageFilenames || [];
    this.appendDeletedImages(formData, deletedImageFilenames);

    // iOS 26.5.2 WebKit regression: a multipart body containing a file part is transmitted with an
    // empty body (Content-Length 0). Send each HACCP image as a base64 TEXT field (Request.Image1/2/3)
    // instead; the backend decodes these back into files.
    const filesToUpload = (data.haccpImageFiles || []).slice(0, 3);
    for (const [index, file] of filesToUpload.entries()) {
      const dataUrl = await this.fileToDataUrl(file);
      formData.append(`Request.Image${index + 1}`, dataUrl);
      console.log(`  ✅ Added Request.Image${index + 1} as base64 (${file.name}, ${file.size} bytes)`);
    }

    if (data.imageCount !== undefined) {
      formData.append('Request.ImageCount', data.imageCount.toString());
    }
    
    console.log('📤 Total operations: Delete=' + deletedImageFilenames.length + ', Upload=' + filesToUpload.length);
    return { deletedImageFilenames, filesToUpload };
  }

  /**
   * Generic helper to append deleted images to FormData
   */
  private appendDeletedImagesGeneric(formData: FormData, deletedImageFilenames: string[], prefix: string = ''): void {
    if (deletedImageFilenames.length > 0) {
      console.log(`🗑️ ${prefix}Images to delete:`, deletedImageFilenames);
      for (const filename of deletedImageFilenames) {
        formData.append('deleteImages', filename);
      }
    }
  }

  /**
   * Generic helper to append new images to FormData
   */
  private appendNewImagesGeneric(formData: FormData, haccpImageFiles: File[], prefix: string = ''): File[] {
    console.log(`🖼️ ${prefix}Image submission debug:`);
    console.log('  - New files to upload:', haccpImageFiles.length);
    
    const filesToUpload = haccpImageFiles.slice(0, 3);
    if (filesToUpload[0]) {
      formData.append('file1', filesToUpload[0]);
      console.log(`  ✅ Added file1:`, filesToUpload[0].name, filesToUpload[0].size, 'bytes');
    }
    if (filesToUpload[1]) {
      formData.append('file2', filesToUpload[1]);
      console.log(`  ✅ Added file2:`, filesToUpload[1].name, filesToUpload[1].size, 'bytes');
    }
    if (filesToUpload[2]) {
      formData.append('file3', filesToUpload[2]);
      console.log(`  ✅ Added file3:`, filesToUpload[2].name, filesToUpload[2].size, 'bytes');
    }
    return filesToUpload;
  }

  private appendDeletedImages(formData: FormData, deletedImageFilenames: string[]): void {
    this.appendDeletedImagesGeneric(formData, deletedImageFilenames);
  }

  private appendNewImages(formData: FormData, haccpImageFiles: File[]): File[] {
    return this.appendNewImagesGeneric(formData, haccpImageFiles);
  }

  private logSubmissionDetails(data: PassportDetailsModel, isSchedulerViewMode: boolean, deletedImageFilenames: string[], filesToUpload: File[], apiUrl: string): void {
    console.log('Submitting passport via API to:', apiUrl);
    console.log('Submission fields:', {
      bulkerNumber: isSchedulerViewMode ? 'N/A (view mode)' : data.bulkerNumber,
      haccpCheck: isSchedulerViewMode ? 'N/A (view mode)' : data.haccpCheck,
      loadingDateTime: isSchedulerViewMode ? 'N/A (view mode)' : data.loadingDateTime,
      origin: isSchedulerViewMode ? 'N/A (view mode)' : data.origin,
      imagesToDelete: deletedImageFilenames.length,
      newImagesToUpload: filesToUpload.length,
      shuntingYardID: this.selectedShuntingYardId,
      shuntingYardName: this.selectedShuntingYard,
      remarks: this.reasonText,
      mode: isSchedulerViewMode ? 'VIEW MODE (assignment-only)' : 'EDIT MODE (full submission)'
    });
  }

  confirmSubmitModal(): void {
    this.submitModalVisible = false;
    const rec = this.passports.getById(this.id);
    const target: 'pending' | 'outgoing' | 'completed' = rec?.status || 'pending';
    this.ui.setStatusSelected(target);
    void this.navigateToPersonaHome();
  }

  async saveDraft(): Promise<void> {
    const data = this.store.get(this.id);
    if (!data) return;
    
    // Prevent double-clicks by setting saving state
    if (this.isSavingDraft()) return;
    this.isSavingDraft.set(true);
    
    console.log('📝 Saving draft with plannedDateTime:', data.plannedDateTime);
    
    let shouldNavigateHome = true;
    try {
      await this.saveDraftToBackend(data);
    } catch (error: any) {
      console.warn('Failed to save draft via API:', error);
      shouldNavigateHome = await this.handleDraftSaveError(error, data);
    } finally {
      this.isSavingDraft.set(false);
    }
    
    if (shouldNavigateHome) {
      await this.navigateToPersonaHome();
    }
  }

  private async handleDraftSaveError(error: any, data: PassportDetailsModel): Promise<boolean> {
    const isOffline = this.isOfflineError(error);
    
    if (isOffline) {
      return await this.handleOfflineDraftError(data);
    }
    await this.handleOnlineDraftError(error, data);
    return true;
  }

  private async handleOfflineDraftError(data: PassportDetailsModel): Promise<boolean> {
    // Desktop mode: Don't save offline, just show alert
    const isDesktopMode = window.innerWidth >= 768;
    if (isDesktopMode) {
      alert('No internet connection. Please connect to the internet to save your draft.');
      return false;
    }

    const saved = await this.saveDraftLocallyAsFallback(data);
    if (!saved) {
      this.toastService.error(
        'Could not save the draft offline. Please try again.',
        7000
      );
      return false;
    }

    console.log('📴 Device is offline - draft saved locally for resync');
    alert('Device is offline. Draft will be saved when you click Resync after coming back online.');
    return true;
  }

  private async handleOnlineDraftError(error: any, data: PassportDetailsModel): Promise<void> {
    if (error?.error?.error === 'stale_invoiced_load') {
      this.toastService.warning(
        error?.error?.message ??
          'This load has been invoiced. Refresh the page to load the latest data; your changes were not saved.',
        7000,
      );
      return;
    }

    const errorMessage = this.extractErrorMessage(error);
    alert(errorMessage);
    
    // DO NOT save when online save fails - the backend has rolled back
    // Reload fresh data from backend to sync with rollback
    console.warn('❌ Draft save failed while online. Reloading fresh data from backend. Backend has rolled back changes.');
    
    // Clear IndexedDB cache for this passport
    try {
      await this.offlineStorage.deleteAllDataForPassport(Number(this.id));
      console.log('🗑️ Cleared cached passport data from IndexedDB for PassportID:', this.id);
    } catch (clearError) {
      console.warn('Failed to clear IndexedDB cache:', clearError);
    }
    
    // Reload the passport data from API to get the rolled-back state
    // This ensures the in-memory store has the correct data after rollback
    const persona = localStorage.getItem('persona') || '';
    if (persona.toLowerCase().includes('schedule')) {
      // For transferred loads, Scheduler edits Grower data (roleId=2)
      const reloadPersona = this.isTransferredLoad ? 'Grower' : 'Scheduler';
      await this.loadPassportDataForPersona(reloadPersona).catch(err => {
        console.warn('Failed to reload passport data after error:', err);
      });
      if (this.isTransferredLoad) {
        await this.loadAuthorisedByOptions();
      }
    }
    // For other personas, the data will be refreshed when user returns to list and clicks passport again
    // We could trigger a sync here, but it's simpler to let the natural flow handle it
  }

  private extractErrorMessage(error: any): string {
    if (error?.error?.message) return error.error.message;
    if (error?.message) return error.message;
    if (typeof error === 'string') return error;
    return 'Failed to save draft. Please try again.';
  }

  private async saveDraftToBackend(data: PassportDetailsModel): Promise<void> {
    const authData = this.getDraftAuthenticationData();
    if (!authData) {
      await this.router.navigateByUrl('/growers');
      return;
    }

    const { token, tokenType, roleId } = authData;
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/update/${this.id}/role/${roleId}`;
    const formData = await this.buildDraftFormData(data);
    
    this.logDraftSubmission(data, apiUrl, formData);

    const headers = { 'Authorization': `${tokenType} ${token}` };
    const response = await firstValueFrom(
      this.http.put<any>(apiUrl, formData, { headers, withCredentials: true })
    );
    
    // Check for errors in the response body
    if (response?.draft?.status === 'Error' || response?.error) {
      const errorMessage = response?.draft?.message || response?.message || response?.error || 'Unknown error occurred';
      console.warn('❌ Draft save failed:', errorMessage);
      throw new Error(errorMessage);
    }

    console.log('Draft saved successfully:', response);
    
    // Clear deletion list and file uploads after successful save
    // AND update imageCount with the new count from backend
    const newImageCount = response.imageCount ?? response.draft?.imageCount ?? 0;
    console.log('📊 [SAVE DRAFT] Backend response: Deleted=' + (response.deletedImages?.length || 0) + ', Added=' + (response.addedImages?.length || 0) + ', Total=' + newImageCount);
    
    this.store.update(this.id, {
      deletedImageFilenames: [],
      haccpImageFiles: [],
      haccpImages: [],
      imageCount: newImageCount,
      haccpImageLinks: response.haccpImageLinks || []
    });
    
    this.lastLoadedLinksKey = '';
    await this.loadImagesIfNeeded();
    
    await this.clearDraft();
  }

  private getDraftAuthenticationData(): { token: string; tokenType: string; roleId: number } | null {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const userID = localStorage.getItem('userID');
    
    if (!token || !userID) {
      console.warn('No authentication token or user ID found');
      return null;
    }

    // For transferred loads with Scheduler persona, save as Grower (roleId=2)
    // Draft saves only occur in edit mode, so no need to check schedulerEditEnabled
    const roleId = (this.isTransferredLoad && this.persona === 'Scheduler')
        ? 2
        : this.getPersonaId(this.persona);
    return { token, tokenType, roleId };
  }

  private async buildDraftFormData(data: PassportDetailsModel): Promise<FormData> {
    const formData = new FormData();
    
    this.appendDraftBasicFields(formData, data);
    this.appendDraftDateTimeFields(formData, data);
    this.appendDraftOriginField(formData, data);
    await this.appendDraftImages(formData, data);
    
    return formData;
  }

  private appendDraftBasicFields(formData: FormData, data: PassportDetailsModel): void {
    this.appendBasicFieldsGeneric(formData, data, '');
  }

  private appendDraftDateTimeFields(formData: FormData, data: PassportDetailsModel): void {
    this.appendDateTimeFieldsGeneric(formData, data, '', 'draft');
  }

  private appendDraftOriginField(formData: FormData, data: PassportDetailsModel): void {
    this.appendOriginFieldGeneric(formData, data, '', 'Draft');
  }

  private async appendDraftImages(formData: FormData, data: PassportDetailsModel): Promise<void> {
    const deletedImageFilenames = data.deletedImageFilenames || [];
    const haccpImageFiles = (data.haccpImageFiles || []).slice(0, 3);
    
    console.log('  - Images to delete:', deletedImageFilenames.length);
    
    this.appendDeletedImagesGeneric(formData, deletedImageFilenames, '[SAVE DRAFT] ');

    // iOS 26.5.2 WebKit regression: send images as base64 TEXT fields (image1/2/3), not file parts.
    // The draft endpoint binds fields without the Request. prefix, so use lowercase field names.
    for (const [index, file] of haccpImageFiles.entries()) {
      const dataUrl = await this.fileToDataUrl(file);
      formData.append(`image${index + 1}`, dataUrl);
      console.log(`  ✅ [SAVE DRAFT] Added image${index + 1} as base64 (${file.name}, ${file.size} bytes)`);
    }
    
    console.log('📤 [SAVE DRAFT] Total operations: Delete=' + deletedImageFilenames.length + ', Upload=' + haccpImageFiles.length);
  }

  private logDraftSubmission(data: PassportDetailsModel, apiUrl: string, formData: FormData): void {
    const deletedImageFilenames = data.deletedImageFilenames || [];
    const haccpImageFiles = data.haccpImageFiles || [];
    const filesToUpload = haccpImageFiles.slice(0, 3);

    console.log('Saving draft via API to:', apiUrl);
    console.log('Draft fields being saved:', {
      bulkerNumber: data.bulkerNumber,
      haccpCheck: data.haccpCheck,
      loadingDateTime: data.loadingDateTime,
      plannedDateTime: data.plannedDateTime,
      origin: data.origin,
      imagesToDelete: deletedImageFilenames.length,
      newImagesToUpload: filesToUpload.length
    });
  }

  private extractFilenameFromUrl(url: string, index: number): string {
    try {
      // Proxy URL format: /api/v1/passports/images/Passports/123/Grower/image.jpg
      if (url.startsWith('/api/')) {
        const parts = url.split('/');
        return parts.at(-1) || `image_${index + 1}.jpg`;
      }
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return pathParts.at(-1) || `image_${index + 1}.jpg`;
    } catch {
      return `image_${index + 1}.jpg`;
    }
  }

  private extractImageNamesFromUrls(urls: string[]): string[] {
    return urls.map((url, index) => this.extractFilenameFromUrl(url, index));
  }

  private extractBackendImageNames(data: PassportDetailsModel, lastReceivedImageLinks: string[]): string[] {
    const isBackendImage = (img: string) => img?.startsWith('http') || img?.startsWith('blob:');
    const hasBackendImageUrls = data.haccpImages?.some(isBackendImage) ?? false;
    
    if (hasBackendImageUrls && data.haccpImages) {
      // Prefer extracting names from proxy URLs in haccpImageLinks (they contain the real filename)
      if (lastReceivedImageLinks.length > 0) {
        return this.extractImageNamesFromUrls(lastReceivedImageLinks);
      }
      const backendUrls = data.haccpImages.filter(isBackendImage);
      return this.extractImageNamesFromUrls(backendUrls);
    }
    
    if (lastReceivedImageLinks.length > 0) {
      return this.extractImageNamesFromUrls(lastReceivedImageLinks);
    }
    
    return [];
  }

  private async saveDraftLocallyAsFallback(data: PassportDetailsModel): Promise<boolean> {
    // Capture old data BEFORE saving the new draft (for offline history)
    // Pass current form data so we can extract backend history as baseline
    const oldDataForHistory = await this.offlineHistoryService.getOldDataForHistory(this.id, data);
    
    // For drafts: 
    // - If images are added offline (File objects), keep them
    // - If images are from backend URLs, store count/names only (no URLs)
    const currentData = this.store.get(this.id);
    const lastReceivedImageLinks = currentData?.haccpImageLinks || [];
    
    const hasOfflineImages = (data.haccpImageFiles?.length ?? 0) > 0;
    const imageNames = this.extractBackendImageNames(data, lastReceivedImageLinks);
    
    // Create draft snapshot
    // If images are added offline, keep the File objects
    // If images are from backend, store count/names only
    const snapshot: any = {
      ...data,
      savedAt: Date.now()
    };
    
    // Resolve authorisedByUserID using helper method
    const authorisedByUserID = this.resolveAuthorisedByUserID(data, currentData);
    
    // IMPORTANT: Always preserve the authorizer name in the snapshot
    // This ensures the name is available when syncing the draft later
    if (data?.authorisedBy?.trim()) {
      snapshot.authorisedBy = data.authorisedBy.trim();
    }
    
    if (authorisedByUserID) {
      snapshot.authorisedByUserID = authorisedByUserID; // Store user ID for resync
      console.log(`✅ Saving draft with authorisedByUserID: ${authorisedByUserID}`);
    } else if (data?.authorisedBy?.trim()) {
      // Preserve the authorizer name even when ID is pending
      console.log(`✅ Saving draft with pending authorizer name: "${data.authorisedBy}"`);
    }
    
    // Filter out deleted images from imageNames before saving
    const deletedFilenames = data.deletedImageFilenames || [];
    const filteredImageNames = imageNames.filter((name: string) => !deletedFilenames.includes(name));
    const filteredImageCount = filteredImageNames.length;
    
    if (hasOfflineImages) {
      // Keep data URLs for offline-added images; resync sends base64 from haccpImages only.
      const offlineImageUrls = (data.haccpImages || []).filter((img: string) => 
        img && img.trim().length > 0 && img.startsWith('data:') && !img.startsWith('PLACEHOLDER:')
      );
      snapshot.haccpImages = offlineImageUrls;
      snapshot.haccpImageCount = filteredImageCount;
      snapshot.haccpImageNames = filteredImageNames;
      snapshot.deletedImageFilenames = deletedFilenames;
      console.log(`✅ Saving draft with ${offlineImageUrls.length} offline data URL images and ${filteredImageCount} backend image placeholders (${deletedFilenames.length} deleted)`);
    } else {
      // Save current haccpImages state (which already has deleted placeholders removed)
      const currentImages = data.haccpImages || [];
      const preservedImages = currentImages.filter((img: string) => {
        if (img?.startsWith('PLACEHOLDER:')) {
          const filename = img.substring('PLACEHOLDER:'.length);
          return !deletedFilenames.includes(filename);
        }
        return true;
      });

      snapshot.haccpImageCount = filteredImageCount;
      snapshot.haccpImageNames = filteredImageNames;
      snapshot.haccpImages = preservedImages;
      snapshot.deletedImageFilenames = deletedFilenames;
      console.log(`✅ Saving draft with ${preservedImages.length} images/placeholders (${deletedFilenames.length} deleted, no files)`);
    }

    // Omit haccpImageFiles — resync sends images as base64 from haccpImages data URLs only.
    // Raw File/Blob objects break IndexedDB put on iOS Safari.
    delete snapshot.haccpImageFiles;
    
    try {
      await this.offlineStorage.saveDraft(this.id, snapshot);
      console.log(`✅ Saved draft to IndexedDB`);
      
      // Create offline history entry (only for grower/transloader in mobile view)
      if (this.shouldLogOfflineHistory()) {
        try {
          const historyEntry = this.offlineHistoryService.createDraftHistoryEntry(
            this.id,
            oldDataForHistory,
            data
          );
          
          if (historyEntry) {
            await this.offlineHistoryService.saveHistoryEntry(this.id, historyEntry);
            console.log(`📝 Created offline history entry for draft save`);
          }
        } catch (historyError) {
          console.warn('Failed to create offline history entry:', historyError);
          // Don't fail the save if history fails
        }
      }
      return true;
    } catch (error) {
      console.warn('Failed to save draft to IndexedDB:', error);
      return false;
    }
  }

  /**
   * Resolve the authorisedByUserID from data or lookup from authorisedByMap
   * Prefers stored authorisedByUserID from API (preserves original Grower/Transloader's authorizer)
   * Falls back to authorisedByMap lookup for new selections made by current user
   */
  private resolveAuthorisedByUserID(
    data: PassportDetailsModel,
    currentData: PassportDetailsModel | undefined
  ): number | undefined {
    const trimmed = data?.authorisedBy?.trim();
    const mapId = trimmed ? this.authorisedByMap.get(trimmed) : undefined;

    if (this.authorisedByUserPicked && mapId !== undefined && mapId > 0) {
      return mapId;
    }

    if (this.isTransferredLoad && this.persona === 'Scheduler' && mapId !== undefined && mapId > 0) {
      return mapId;
    }

    const dataId = data?.authorisedByUserID;
    if (dataId !== undefined && dataId !== null && dataId > 0) {
      return dataId;
    }

    const currentId = currentData?.authorisedByUserID;
    if (currentId !== undefined && currentId !== null && currentId > 0) {
      return currentId;
    }

    if (mapId !== undefined && mapId > 0) {
      return mapId;
    }

    return undefined;
  }

  /**
   * Determine if offline history should be logged
   * Only log for grower/transloader in mobile view when OFFLINE
   * IMPORTANT: Use localStorage persona (the REAL user role), not this.persona
   * which may be set to Grower/Transloader when Scheduler is viewing
   */
  private shouldLogOfflineHistory(): boolean {
    // Only log offline history when actually offline
    if (navigator.onLine) {
      return false;
    }
    
    // Check the REAL persona from localStorage, not the viewing persona
    const storedPersona = (localStorage.getItem('persona') || '').toLowerCase();
    
    // Scheduler should NEVER log offline history, even when viewing as Grower/Transloader
    if (storedPersona.includes('schedule')) {
      return false;
    }
    
    const isEligibleRole = storedPersona.includes('grower') || storedPersona.includes('transloader');
    const isMobileView = !this.isDesktopMode;
    return isEligibleRole && isMobileView;
  }
  
  private async navigateToPersonaHome(): Promise<void> {
    const p = localStorage.getItem('persona') || this.persona || '';
    const route = this.getRouteForPersona(p);
    const safeRoute = this.getSafeBackRoute(route);
    this.applyReceiverSessionForTargetRoute(safeRoute, p, 'modal-home');
    await this.router.navigateByUrl(safeRoute);
  }

  private submittedKey(): string { return `passport_submitted_${this.id}`; }

  private async loadSnapshotByStatus(): Promise<void> {
    const status = this.passports.getById(this.id)?.status;
    const isOnline = navigator.onLine;
    
    if (status === 'outgoing') {
      // For outgoing passports: Always load from IndexedDB (user's pending submission)
      const outgoingData = await this.loadOutgoingPassportSubmissionData();
      if (isOutgoingPassportSubmission(outgoingData)) {
        const payload = outgoingData.data;
        console.log('📤 Found outgoing passport submission data, restoring modified images');
        
        // Calculate correct imageCount for offline submissions
        // When offline, images are stored as dataURLs in haccpImages (not as Files in haccpImageFiles)
        const haccpImages = payload.haccpImages ?? [];
        const deletedImageFilenames = payload.deletedImageFilenames ?? [];
        const haccpImageLinks = payload.haccpImageLinks ?? [];
        
        // Count real images (not placeholders)
        const realImages = haccpImages.filter((img: string) => 
          img && img.trim().length > 0 && !img.startsWith('PLACEHOLDER:')
        );
        
        // For outgoing passports: imageCount should reflect actual images that will be sent
        // = original backend images (haccpImageLinks) - deleted + newly added (realImages that are dataURLs)
        const originalBackendImageCount = haccpImageLinks.length;
        const newlyAddedImages = realImages.filter((img: string) => img.startsWith('data:'));
        const correctedImageCount = originalBackendImageCount - deletedImageFilenames.length + newlyAddedImages.length;
        
        console.log('📤 Outgoing passport image calculation:', {
          originalBackendImages: originalBackendImageCount,
          deletedCount: deletedImageFilenames.length,
          newlyAddedCount: newlyAddedImages.length,
          correctedImageCount
        });
        
        // Outgoing data should be self-contained with all necessary fields
        this.store.update(this.id, {
          ...payload,
          // Ensure images are restored correctly
          haccpImages: haccpImages,
          haccpImageFiles: payload.haccpImageFiles ?? [],
          deletedImageFilenames: deletedImageFilenames,
          imageCount: correctedImageCount, // Use calculated count instead of potentially incorrect backend count
          haccpImageLinks: haccpImageLinks,
          haccpCheck: correctedImageCount > 0 ? 'Yes' : 'No'
        });
      }
    } else if (status === 'completed' && !isOnline) {
      // For completed passports in OFFLINE mode: Load from snapshot/cache
      this.loadSubmittedIfAny();
    } else if (status === 'completed' && isOnline) {
      // For completed passports in ONLINE mode: Use fresh API data from list component
      // Do not load old snapshots - the data is already in the store from the list component
      console.log('✅ Using fresh API data for completed passport (online mode)');
    } else {
      // For pending passports: Load draft if exists
      await this.loadDraftIfAny();
    }
  }

  /**
   * Load outgoing passport submission data from IndexedDB if it exists
   */
  private async loadOutgoingPassportSubmissionData(): Promise<unknown> {
    try {
      return await this.offlineStorage.getOutgoingPassport(this.id);
    } catch (error) {
      console.warn('Failed to load outgoing passport submission data:', error);
      return null;
    }
  }

  private loadOfflineImagesFromDraft(haccpImageFiles: File[], haccpImages: string[]): { images: string[], files: File[] } {
    const files = haccpImageFiles;
    let images: string[] = [];
    
    if (haccpImages?.length > 0) {
      // Only keep data URLs (offline-added images) - filter out backend URLs (blob: and http:) and placeholders
      images = haccpImages.filter((img: string) => 
        img?.trim().length > 0 && 
        img.startsWith('data:') && 
        !img.startsWith('PLACEHOLDER:') &&
        !img.startsWith('blob:') &&
        !img.startsWith('http://') &&
        !img.startsWith('https://')
      );
      if (images.length === 0) {
        console.warn(`⚠️ No data URLs found, creating blob URLs from files`);
        images = haccpImageFiles.map(file => URL.createObjectURL(file));
      } else {
        console.log(`📦 Loaded ${images.length} offline image data URLs from draft`);
      }
    } else {
      images = haccpImageFiles.map(file => URL.createObjectURL(file));
      console.log(`📦 Created ${images.length} blob URLs from offline image files`);
    }
    
    return { images, files };
  }

  private createPlaceholdersFromImageNames(imageNames: string[], deletedFilenames: string[]): string[] {
    const placeholders: string[] = [];
    for (const imageName of imageNames) {
      if (imageName && !deletedFilenames.includes(imageName)) {
        placeholders.push(`PLACEHOLDER:${imageName}`);
      }
    }
    return placeholders;
  }

  private async loadDraftIfAny(): Promise<void> {
    try {
      const draftData = await this.offlineStorage.getDraft(this.id);
      
      if (!draftData || typeof draftData !== 'object') {
        await this.loadEmptyImagePlaceholdersIfNeeded();
        return;
      }
      
      this.hasDraft = true;
      const draft = draftData as {
        loadNumber?: string; dateTime?: string; variety?: string; vendorName?: string; vendorNumber?: string; 
        apnName?: string; haulier?: string; destination?: string; origin?: string; loadingDateTime?: string; 
        bulkerNumber?: string; haccpCheck?: 'Yes' | 'No'; haccpImages?: string[]; haccpImageFiles?: File[]; 
        haccpImageCount?: number; haccpImageNames?: string[];
        authorisedBy?: string; deletedImageFilenames?: string[]; plannedDateTime?: string; dueDateTime?: string;
      };
      const {
        loadNumber, dateTime, variety, vendorName, vendorNumber, apnName, haulier, destination,
        origin, loadingDateTime, bulkerNumber, haccpCheck, haccpImages,
        haccpImageFiles, haccpImageCount,
        authorisedBy, deletedImageFilenames, plannedDateTime, dueDateTime
      } = draft;
      
      const { images: imagesToDisplay, files: filesToStore } = await this.processDraftImages(
        haccpImageFiles,
        haccpImages,
        draft.haccpImageNames,
        haccpImageCount,
        deletedImageFilenames
      );
      
      // Preserve existing imageCount and haccpImageLinks from list component
      const existingData = this.store.get(this.id);
      this.store.update(this.id, {
        id: this.id,
        loadNumber, dateTime, variety, vendorName, vendorNumber, apnName, haulier, destination,
        origin, loadingDateTime, bulkerNumber, haccpCheck,
        haccpImages: imagesToDisplay,
        haccpImageFiles: filesToStore,
        authorisedBy, deletedImageFilenames, plannedDateTime, dueDateTime,
        // Preserve critical fields from list component for HACCP computation and image loading
        imageCount: existingData?.imageCount ?? 0,
        haccpImageLinks: existingData?.haccpImageLinks ?? []
      });
      console.log('✅ Loaded draft from IndexedDB');
    } catch (error) {
      console.warn('Error loading draft:', error);
      if (navigator.onLine === false) {
        await this.loadEmptyImagePlaceholdersIfNeeded();
      }
    }
  }

  /**
   * Process draft images and return images to display and files to store
   * Extracted from loadDraftIfAny to reduce cognitive complexity
   */
  private async processDraftImages(
    haccpImageFiles: File[] | undefined,
    haccpImages: string[] | undefined,
    haccpImageNames: string[] | undefined,
    haccpImageCount: number | undefined,
    deletedImageFilenames: string[] | undefined
  ): Promise<{ images: string[]; files: File[] }> {
    let imagesToDisplay: string[] = [];
    let filesToStore: File[] = [];
    const deletedFilenames = deletedImageFilenames || [];

    if (haccpImageFiles && haccpImageFiles.length > 0) {
      return this.processDraftImagesWithFiles(haccpImageFiles, haccpImages, haccpImageNames, deletedFilenames);
    }
    
    if (haccpImages !== undefined && Array.isArray(haccpImages)) {
      return this.processDraftImagesWithoutFiles(haccpImages, haccpImageNames, deletedFilenames);
    }
    
    return this.processDraftImagesFallback(haccpImageNames, haccpImageCount, deletedImageFilenames);
  }

  /**
   * Process draft images when haccpImageFiles exist
   */
  private processDraftImagesWithFiles(
    haccpImageFiles: File[],
    haccpImages: string[] | undefined,
    haccpImageNames: string[] | undefined,
    deletedFilenames: string[]
  ): { images: string[]; files: File[] } {
    const result = this.loadOfflineImagesFromDraft(haccpImageFiles, haccpImages || []);
    const imagesToDisplay = result.images;
    const filesToStore = result.files;
    
    const imageNames = haccpImageNames || [];
    if (imageNames.length > 0) {
      const placeholders = this.createPlaceholdersFromImageNames(imageNames, deletedFilenames);
      imagesToDisplay.push(...placeholders);
      console.log(`📦 Added ${placeholders.length} backend image placeholders (${imageNames.length - placeholders.length} were deleted)`);
    }
    
    return { images: imagesToDisplay, files: filesToStore };
  }

  /**
   * Process draft images when only haccpImages exist (no files)
   */
  private processDraftImagesWithoutFiles(
    haccpImages: string[],
    haccpImageNames: string[] | undefined,
    deletedImageFilenames: string[] | undefined
  ): { images: string[]; files: File[] } {
    const deletedFilenames = deletedImageFilenames || [];
    const filteredImages = haccpImages.filter((img: string) => {
      if (img?.startsWith('http://') || img?.startsWith('https://') || img?.startsWith('blob:')) {
        return false;
      }
      if (img?.startsWith('PLACEHOLDER:')) {
        const filename = img.substring('PLACEHOLDER:'.length);
        return !deletedFilenames.includes(filename);
      }
      return true;
    });
    
    const imagesToDisplay = [...filteredImages];
    
    if (navigator.onLine === false && haccpImageNames && haccpImageNames.length > 0) {
      const placeholders = this.createPlaceholdersFromImageNames(haccpImageNames, deletedFilenames);
      const existingPlaceholders = new Set(
        filteredImages
          .filter((img: string) => img?.startsWith('PLACEHOLDER:'))
          .map((img: string) => img.substring('PLACEHOLDER:'.length))
      );
      const newPlaceholders = placeholders.filter((p: string) => {
        const filename = p.substring('PLACEHOLDER:'.length);
        return !existingPlaceholders.has(filename);
      });
      imagesToDisplay.push(...newPlaceholders);
      console.log(`📦 Added ${newPlaceholders.length} backend image placeholders (${haccpImageNames.length - newPlaceholders.length} were deleted or already present)`);
    }
    
    console.log(`📦 Loaded ${filteredImages.length} images from draft saved state (${haccpImages.length} total, backend URLs filtered out)`);

    const dataUrls = imagesToDisplay.filter((img: string) => img.startsWith('data:'));
    const filesToStore = dataUrls.map((dataUrl: string, index: number) =>
      this.dataURLtoFile(dataUrl, `draft_offline_image_${index}.jpg`)
    );
    if (filesToStore.length > 0) {
      console.log(`🔄 Rebuilt ${filesToStore.length} File objects from draft data URLs`);
    }

    return { images: imagesToDisplay, files: filesToStore };
  }

  /**
   * Process draft images fallback logic
   */
  private async processDraftImagesFallback(
    haccpImageNames: string[] | undefined,
    haccpImageCount: number | undefined,
    deletedImageFilenames: string[] | undefined
  ): Promise<{ images: string[]; files: File[] }> {
    const deletedFilenames = deletedImageFilenames || [];
    const imagesToDisplay: string[] = [];
    
    const imageNames = haccpImageNames || [];
    if (imageNames.length > 0) {
      const placeholders = this.createPlaceholdersFromImageNames(imageNames, deletedFilenames);
      imagesToDisplay.push(...placeholders);
      console.log(`📦 Added ${placeholders.length} backend image placeholders to draft (${imageNames.length - placeholders.length} were deleted)`);
      return { images: imagesToDisplay, files: [] };
    }
    
    if (haccpImageCount && haccpImageCount > 0 && navigator.onLine === false && imagesToDisplay.length === 0) {
      const placeholders = this.createPlaceholdersFromImageNames(
        Array.from({ length: haccpImageCount }, (_, i) => `image_${i + 1}.jpg`),
        deletedFilenames
      );
      imagesToDisplay.push(...placeholders);
      return { images: imagesToDisplay, files: [] };
    }
    
    if (navigator.onLine === false && imagesToDisplay.length === 0) {
      const currentData = this.store.get(this.id);
      if (currentData && deletedImageFilenames && deletedImageFilenames.length > 0) {
        this.store.update(this.id, { deletedImageFilenames });
      }
      await this.loadEmptyImagePlaceholdersIfNeeded();
      const updatedData = this.store.get(this.id);
      return { images: updatedData?.haccpImages || [], files: [] };
    }
    
    return { images: imagesToDisplay, files: [] };
  }

  /**
   * Load empty image placeholders based on last received image count
   * This is used when offline and no draft exists, but we need to show image boxes
   * IMPORTANT: Excludes placeholders that are in deletedImageFilenames
   */
  private async loadEmptyImagePlaceholdersIfNeeded(): Promise<void> {
    if (navigator.onLine) {
      return; // Only needed when offline
    }
    
    try {
      const currentData = this.store.get(this.id);
      const lastReceivedImageLinks = currentData?.haccpImageLinks || [];
      const deletedImageFilenames = currentData?.deletedImageFilenames || [];
      
      if (lastReceivedImageLinks.length > 0) {
        // Create placeholders with image names embedded (format: "PLACEHOLDER:filename.jpg")
        // BUT exclude any that are in deletedImageFilenames
        const emptyPlaceholders: string[] = [];
        for (let i = 0; i < lastReceivedImageLinks.length; i++) {
          const url = lastReceivedImageLinks[i];
          let imageName = `image_${i + 1}.jpg`;
          try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            imageName = pathParts.at(-1) || imageName;
          } catch {
            // Use default name if URL parsing fails
          }
          
          // Only add placeholder if it's not in deletedImageFilenames
          if (!deletedImageFilenames.includes(imageName)) {
            emptyPlaceholders.push(`PLACEHOLDER:${imageName}`);
          }
        }
        this.store.update(this.id, {
          haccpImages: emptyPlaceholders,
          haccpImageFiles: []
        });
        console.log(`📦 Created ${emptyPlaceholders.length} empty image placeholders from last received data (${lastReceivedImageLinks.length - emptyPlaceholders.length} were deleted)`);
      }
    } catch (error) {
      console.warn('Failed to load empty image placeholders:', error);
    }
  }

  get shouldMarkRequired(): boolean {
    return this.mode === 'edit' && (!this.canSubmit) && this.hasDraft;
  }

  get missingOrigin(): boolean {
    // Origin is now optional - "No Origin Field" is a valid selection
    // Always return false as origin is never considered "missing"
    return false;
  }

  get missingLoading(): boolean {
    const s = this.store.get(this.id);
    return (s?.loadingDateTime?.length ?? 0) < 16;
  }

  get missingBulker(): boolean {
    const s = this.store.get(this.id);
    return !(s?.bulkerNumber?.trim());
  }

  get missingAuthor(): boolean {
    const s = this.store.get(this.id);
    return !(s?.authorisedBy?.trim());
  }

  get missingHaccp(): boolean {
    // HACCP is missing/invalid if computedHaccpCheck is not 'Yes' (i.e., no images attached)
    // "No" is not a valid entry for submission - images must be attached
    return this.computedHaccpCheck !== 'Yes';
  }

  private loadSubmittedIfAny(): void {
    try {
      const raw = localStorage.getItem(this.submittedKey());
      if (!raw) return;
      const parsed = JSON.parse(raw || '{}');
      const {
        loadNumber, dateTime, variety, vendorName, vendorNumber, apnName, haulier, destination,
        origin, loadingDateTime, bulkerNumber, haccpCheck, haccpImages, authorisedBy
      } = parsed || {};
      // Preserve existing imageCount and haccpImageLinks from list component
      const existingData = this.store.get(this.id);
      this.store.update(this.id, {
        id: this.id,
        loadNumber, dateTime, variety, vendorName, vendorNumber, apnName, haulier, destination,
        origin, loadingDateTime, bulkerNumber, haccpCheck, haccpImages, authorisedBy,
        // Preserve critical fields from list component for HACCP computation and image loading
        imageCount: existingData?.imageCount ?? 0,
        haccpImageLinks: existingData?.haccpImageLinks ?? []
      });

      // Image caching removed: do not hydrate images from snapshot links
    } catch {
      // Ignore errors loading snapshot - non-critical feature
    }
  }

  private saveSubmittedSnapshot(): void {
    const data = this.store.get(this.id);
    if (!data) return;
    try {
      const snapshot = { ...data, savedAt: Date.now(), selectedShuntingYard: (this.selectedShuntingYard || '') };
      localStorage.setItem(this.submittedKey(), JSON.stringify(snapshot));
    } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
  }

  private async clearDraft(): Promise<void> {
    try {
      await this.offlineStorage.deleteDraft(this.id);
      console.log('✅ Draft cleared from IndexedDB');
    } catch (error) {
      console.warn('Error clearing draft:', error);
    }
  }

  private async revertUnsavedImageChanges(): Promise<void> {
    const current = this.store.get(this.id);
    if (!current) return;

    let savedDraft: any = null;
    try {
      savedDraft = await this.offlineStorage.getDraft(this.id);
    } catch (err) {
      console.warn('Failed to get draft for reverting images', err);
    }

    // Identify blob URLs we are currently holding
    const currentBlobUrls = (current.haccpImages || []).filter((img: string) => img?.startsWith('blob:'));

    // We will discard all current blob URLs because they represent either:
    // 1. Proxy backend images (which will be refetched by loadImagesIfNeeded anyway)
    // 2. Local File blobs (which we are dropping if unsaved, or will be recreated from draft Files)
    for (const img of currentBlobUrls) {
      URL.revokeObjectURL(img);
      this.proxyUrlMap.delete(img);
      this.imageBlobCache.delete(img);
    }

    if (savedDraft && typeof savedDraft === 'object') {
      const draft = savedDraft;
      
      // Filter out any stale session blob URLs that might have been saved in the draft
      const draftImages = (draft.haccpImages || []).filter((img: string) => 
        !img?.startsWith('blob:') && !img?.startsWith('http://') && !img?.startsWith('https://')
      );
      
      this.store.update(this.id, {
        haccpImages: draftImages,
        haccpImageFiles: draft.haccpImageFiles || [],
        deletedImageFilenames: draft.deletedImageFilenames || []
      });
      // Draft on disk already has the correct state; no re-write needed.
      return;
    }

    // No saved draft: strip unsaved local additions, revert deletions
    const sanitized = (current.haccpImages || [])
      .filter((img: string) => !img.startsWith('data:') && !img.startsWith('blob:'));
      
    this.store.update(this.id, {
      haccpImages: sanitized,
      haccpImageFiles: [],
      deletedImageFilenames: []
    });
  }

  /**
   * Helper to create passport header for navigation
   */
  private createPassportHeader() {
    const s = this.store.get(this.id);
    return {
      loadNumber: s?.loadNumber || this.id,
      variety: s?.variety,
      haulier: s?.haulier,
      bulkerNumber: s?.bulkerNumber,
      scheduledText: this.getScheduledText(s),
      origin: s?.origin,
      destination: s?.destination,
      apnName: s?.apnName,
      vendorName: s?.vendorName,
      vendorNumber: s?.vendorNumber,
      isGatehouse: this.isGatehouse,
      isScheduler: this.isScheduler,
      selectedShuntingYard: this.selectedShuntingYard || '',
      shuntingYardsDisplay: this.shuntingYardsDisplay || []
    };
  }

  async onCommentsClick(): Promise<void> {
    const header = this.createPassportHeader();
    const readOnly = this.isAudit;
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open comments panel for desktop
      this.commentsPanelService.openCommentsPanel(this.id, header, readOnly);
    } else {
      // Save flag and component-local state so returning via back button preserves edits
      sessionStorage.setItem(this.getMobilePanelNavKey(), 'true');
      this.saveMobilePanelState();
      // Navigate to comments page for mobile
      await this.router.navigate(['/comments'], { state: { passportId: this.id, header, readOnly } });
    }
  }

  async onHistoryClick(): Promise<void> {
    const header = this.createPassportHeader();
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open history panel for desktop
      this.historyPanelService.openHistoryPanel(this.id, header);
    } else {
      // Save flag and component-local state so returning via back button preserves edits
      sessionStorage.setItem(this.getMobilePanelNavKey(), 'true');
      this.saveMobilePanelState();
      // Navigate to history page for mobile
      await this.router.navigate(['/history'], { state: { passportId: this.id, header } });
    }
  }

  private getScheduledText(s: any): string | undefined {
    const iso = s?.dueDateTime || s?.loadingDateTime || s?.dateTime;
    return this.dateTimeHelper.formatScheduledText(iso);
  }

  async onPersonaChange(value: string): Promise<void> {
    // 'Scheduler' option: load Grower data in view mode (user clicks Edit to enter edit mode)
    if (value === 'Scheduler') {
      this.persona = 'Scheduler';
      this.reasonText = '';

      const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
      const isSchedulerUser = currentPersona.includes('schedule');
      const isAudit = currentPersona.includes('audit');

      if (isSchedulerUser || isAudit) {
        await this.loadPassportDataForPersona('Grower');
        await this.loadOriginOptions();
        if (this.isTransferredLoad) {
          await this.loadAuthorisedByOptions();
        }
      }
      return;
    }

    if (value === 'Grower' || value === 'Transloader') {
      this.persona = value;
      
      // Clear reason field when persona changes
      this.reasonText = '';
      
      // Check if current user is Scheduler or Audit (needs role-specific API call for images)
      const currentPersona = (localStorage.getItem('persona') || '').toLowerCase();
      const isScheduler = currentPersona.includes('schedule');
      const isAudit = currentPersona.includes('audit');
      
      if (isScheduler || isAudit) {
        // Scheduler/Audit switching personas: need to fetch role-specific data (especially images)
        await this.loadPassportDataForPersona(value);
        
        // Reload authorizers for Transloader (station-level) and Grower (account-level)
        if (value === 'Transloader' || value === 'Grower' || value === 'Scheduler') {
          await this.loadAuthorisedByOptions();
        }
        
        // Reload origin options for the new persona
        // This ensures "No Origin Field" is available when switching to Grower
        await this.loadOriginOptions();
      } else {
        // Regular Grower/Transloader user: data already in store from homepage API
        // Just reload images (they should already be correct for their role)
        await this.loadImagesIfNeeded();
      }
    }
  }

  /**
   * Checks whether the API response contains meaningful Transloader-specific field values.
   */
  private hasTransloaderFields(response: any): boolean {
    const hasLoadingDateTime = response.loadingDateTime && response.loadingDateTime.trim().length > 0;
    const hasBulkerNumber = response.trailerNumber && response.trailerNumber.trim().length > 0;
    const hasAuthoriser = response.authorizerName && response.authorizerName.trim().length > 0;
    const hasHaccpData = response.haccpCheck !== undefined && response.haccpCheck !== null;

    const hasData = hasLoadingDateTime || hasBulkerNumber || hasAuthoriser || hasHaccpData;

    console.log(`Transloader data check: loadingDateTime=${hasLoadingDateTime}, bulker=${hasBulkerNumber}, authoriser=${hasAuthoriser}, haccp=${hasHaccpData} => hasData=${hasData}`);
    return hasData;
  }

  /**
   * Check if Transloader data exists for this passport and load it if available.
   * Returns true if meaningful Transloader data was found, false otherwise.
   */
  private async checkAndLoadTransloaderData(): Promise<boolean> {
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      
      if (!token) {
        console.warn('No authentication token found');
        return false;
      }

      const roleId = 3;
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/${this.id}/role/${roleId}`;
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('Checking for Transloader data...');
      const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));

      if (this.hasTransloaderFields(response)) {
        await this.loadPassportDataForPersona('Transloader');
        return true;
      }
      
      return false;
    } catch (error: any) {
      if (error?.status === 403) {
        this.accessDenied.set(false);
        console.log('User not authorized as Transloader for this passport, defaulting to Grower');
        return false;
      }
      console.warn('Failed to check Transloader data, defaulting to Grower:', error);
      return false;
    }
  }

  private async loadPassportDataForPersona(
    persona: string,
    skipImages = false,
    suppressAccessDeniedHandling = false
  ): Promise<void> {
    this.isLoading.set(true);
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      
      if (!token) {
        console.warn('No authentication token found');
        return;
      }

      // Determine role ID based on persona (default: Grower = 2)
      const roleId = PassportDetailsPage.PERSONA_ROLE_MAP[persona] ?? 2;
      
      // Call the API to get persona-specific data
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/${this.id}/role/${roleId}`;
      const headers = {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      };

      console.log(`Loading passport data for ${persona} (roleId: ${roleId})`);
      
      const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));
      
      console.log(`✅ Loaded ${persona} data:`, response);

      // Preserve existing editing state when switching personas (for scheduler)
      const existingData = this.store.get(this.id);
      const existingDeletedImages = existingData?.deletedImageFilenames || [];
      const existingImageFiles = existingData?.haccpImageFiles || [];

      // Preserve the Scheduler/Gatehouse shunting yard choice across persona switches.
      // The shunting yard is a Scheduler/Gatehouse-level field, not Grower/Transloader-specific,
      // so it must not be overwritten when the Scheduler toggles between Grower and Transloader.
      const preserveShuntingYard = this.selectedShuntingYard;
      const preserveShuntingYardId = this.selectedShuntingYardId;
      const preserveIsShuntingRequired = this.isShuntingRequired;

      // Update the store with the persona-specific data
      this.store.upsert({
        id: this.id,
        loadNumber: response.loadNumber || this.id,
        vendorName: response.vendorName || '',
        vendorNumber: response.vendorNumber || '',
        apn: response.apn || '',
        apnName: response.apnName || '',
        variety: response.variety || '',
        destination: response.destination || '',
        destinationWeighbridge: response.destinationWeighbridge || '',
        origin: (response.transferFlag && response.originName)
          ? response.originName
          : this.formatOriginDisplayName(response.originName || '', response.rawSourceType || ''),
        bulkerNumber: response.trailerNumber || '',
        bulkerMain: response.trailerMain || '',
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(response.dueDateTime || response.scheduledDateTime || ''),
        haulier: response.haulierName || '',
        plantID: this.getPlantID(response),
        statusCode: response.statusCode || '',
        haccpCheck: response.haccpCheck ? 'Yes' : 'No',
        authorisedBy: response.authorizerName || '',
        authorisedByUserID: response.authorisedByUserID || response.authorizerId || undefined, // Store authorizer ID from API
        ownerUserID: response.userID || response.userId || undefined, // Store owner ID from API
        loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(response.loadingDateTime || ''),
        plannedDateTime: response.plannedDateTime ? this.dateTimeHelper.ensureUtcTimestamp(response.plannedDateTime) : undefined,
        haccpImageLinks: response.haccpImageLinks || [], // Store persona-specific image links
        haccpImages: [], // Will be populated by loadImagesIfNeeded
        haccpImageFiles: existingImageFiles, // Preserve newly added images when switching personas
        deletedImageFilenames: existingDeletedImages, // Preserve deleted images list when switching personas
        imageCount: response.imageCount || 0,  // Store image count from backend
        isShuntingRequired: response.isShuntingRequired ?? true, // Default to true if not provided
        shuntingYardID: response.shuntingYardID ?? null,
        shuntingYardName: response.shuntingYardName || '',
        isPassportCompleted: response.isPassportCompleted || false,
        isReceivingCompleted: response.isReceivingCompleted === true,
        transloadingStationID: response.transloadingStationID ?? null,
        growerAccountID: response.growerAccountID ?? null,
        transferFlag: response.transferFlag || existingData?.transferFlag || false
      });

      this.restoreShuntingYardState(
        response,
        preserveShuntingYard,
        preserveShuntingYardId,
        preserveIsShuntingRequired
      );

      if (!skipImages) {
        await this.loadImagesIfNeeded();
        this.checkImageLoadStatus(response);
      }
      
      await this.loadAuthorizerOptionsIfNeeded(response, existingData, persona);
      
      console.log(`✅ Successfully loaded ${persona} passport data and images`);
    } catch (error: any) {
      this.handleLoadPassportError(error, persona, suppressAccessDeniedHandling);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadAuthorizerOptionsIfNeeded(
    response: any,
    existingData: any,
    persona: string
  ): Promise<void> {
    if (!response.userID || !this.id) {
      return;
    }
    const isTransferredSchedulerPersona =
      (response.transferFlag || existingData?.transferFlag || this.isTransferredTab) &&
      (localStorage.getItem('persona') || '').toLowerCase().includes('schedule') &&
      this.persona === 'Scheduler';
    if (isTransferredSchedulerPersona) {
      return;
    }
    const passportIdNum = Number.parseInt(this.id, 10);
    if (persona === 'Grower' || persona === 'Scheduler') {
      await this.loadAuthorisedByOptionsForPassport(passportIdNum, undefined, 2);
    } else {
      await this.loadAuthorisedByOptionsForUser(response.userID);
    }
  }

  private handleLoadPassportError(
    error: any,
    persona: string,
    suppressAccessDeniedHandling: boolean
  ): never {
    this.accessDenied.set(true);

    if (error?.status === 403) {
      console.warn(`Access denied for passport ${this.id} as ${persona}`);
      if (!suppressAccessDeniedHandling) {
        this.toastService.error('You do not have access to this passport.', 5000);
        this.navigateBackToOrigin();
      }
      throw error;
    }
    if (error?.status === 404) {
      console.warn(`Passport ${this.id} not found`);
      this.toastService.error('This passport record does not exist or is no longer available.', 5000);
      this.navigateBackToOrigin();
      throw error;
    }
    console.warn(`Failed to load passport data for ${persona}:`, error);
    this.toastService.error('Failed to load passport details. Please try again.', 5000);
    this.navigateBackToOrigin();
    throw error;
  }

  private restoreShuntingYardState(
    response: any,
    preserveShuntingYard: string,
    preserveShuntingYardId: number | null,
    preserveIsShuntingRequired: boolean
  ): void {
    const currentUserPersona = (localStorage.getItem('persona') || '').toLowerCase();
    const isSchedulerOrGatehouse = currentUserPersona.includes('schedule') || currentUserPersona.includes('gate');
    if (isSchedulerOrGatehouse && preserveShuntingYard) {
      this.selectedShuntingYardId = preserveShuntingYardId;
      this.selectedShuntingYard = preserveShuntingYard;
      this.isShuntingRequired = preserveIsShuntingRequired;
    } else {
      this.selectedShuntingYardId = (response.shuntingYardID ?? null);
      this.selectedShuntingYard = (response.shuntingYardName || (this.selectedShuntingYardId ? '' : 'No Shunting Required'));
      this.isShuntingRequired = response.isShuntingRequired ?? true;
    }
  }

  /**
   * Check if backend expects images but returned incomplete links.
   * Actual fetch failures are handled by loadImagesIfNeeded and onHaccpImageError.
   */
  private checkImageLoadStatus(response: any): void {
    if (!navigator.onLine || response.imageCount <= 0) return;

    const imageLinks = response.haccpImageLinks || [];
    const validImageLinks = imageLinks.filter((link: string) => link && link.trim().length > 0);

    if (validImageLinks.length === 0) {
      console.warn(`⚠️ Backend has ${response.imageCount} images but no valid image links returned`);
      this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, 0);
    } else if (validImageLinks.length < response.imageCount) {
      console.warn(`⚠️ Backend has ${response.imageCount} images but only ${validImageLinks.length} links returned`);
      this.imageLoadHelper.checkAndShowImageLoadFailure(this.id, validImageLinks.length);
    }
  }

  setShuntingYard(value: string): void {
    this.selectedShuntingYard = value;
    // Find the corresponding ID from the shuntingYards array (prefer exact match)
    let yard = this.shuntingYards.find(y => y.name === value);
    // Fallback to case-insensitive match if not found
    if (!yard) {
      const lower = value.toLowerCase();
      yard = this.shuntingYards.find(y => (y.name || '').toLowerCase() === lower);
    }
    this.selectedShuntingYardId = yard ? yard.id : null;
    // Auto-close the dropdown after selection
    this.yardOpen = false;
    console.log(`Selected shunting yard: ${value} (ID: ${this.selectedShuntingYardId})`);
  }

  async onAdvancePassport(): Promise<void> {
    this.isLoading.set(true);
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      
      if (!token) {
        alert('Authentication token not found. Please login again.');
        return;
      }

      // For Gatehouse: Always submit shunting yard selection (including "No Shunting Required" = null)
      if (this.isGatehouse) {
        await this.submitGatehouseAdvance(tokenType, token);
        return;
      }

      const persona = localStorage.getItem('persona') || '';
      const roleId = this.getPersonaId(persona);

      // Advance the passport status (for non-Gatehouse personas)
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/status/advance/${this.id}/role/${roleId}`;
      
      const response = await firstValueFrom(
        this.http.post<any>(apiUrl, {}, {
          headers: {
            'Authorization': `${tokenType} ${token}`
          }
        })
      );

      console.log('✅ Passport advanced:', response);

      if (response?.workflowTrigger?.triggered && !response?.workflowTrigger?.success) {
        this.toastService.warning(
          'Passport status was updated, but the processing service could not be reached. Data may take longer to sync.',
          8000
        );
      }
      
      // Navigate back to appropriate persona page
      const targetRoute = this.getAdvanceTargetRoute();
      this.router.navigateByUrl(targetRoute);
      
    } catch (error: unknown) {
      console.warn('❌ Advance passport failed:', error);
      const errorMsg = this.getAdvancePassportErrorMessage(error);
      alert(`Error: ${errorMsg}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async submitGatehouseAdvance(tokenType: string, token: string): Promise<void> {
    const submitDraftUrl = `${environment.apiBaseUrl}/api/v1/passports/submit/${this.id}/role/7/submittedBy/7`;
    const formData = this.buildGatehouseFormData();

    try {
      const submitResponse = await firstValueFrom(
        this.http.post<any>(submitDraftUrl, formData, {
          headers: {
            'Authorization': `${tokenType} ${token}`
          },
          withCredentials: true
        })
      );

      console.log('✅ Draft submitted:', submitResponse);

      // Navigate back to gatehouse page after submit - no advance API needed
      this.updateGatehousePostSubmitState();
      await this.router.navigateByUrl('/gatehouse');
    } catch (error: unknown) {
      if (this.isStaleInvoicedGatehouseError(error)) {
        const message = error.error?.message ??
          'This load has been invoiced. Refresh the page to load the latest data; your changes were not saved.';
        this.toastService.warning(message, 7000);
        return;
      }
      throw error;
    }
  }

  private updateGatehousePostSubmitState(): void {
    const details = this.store.get(this.id);
    const shuntingYardId = this.selectedShuntingYardId ?? details?.shuntingYardID ?? null;
    const shuntingYardName = this.selectedShuntingYard.trim().length > 0
      ? this.selectedShuntingYard
      : (details?.shuntingYardName ?? '');
    if (details) {
      this.store.update(this.id, {
        shuntingYardID: shuntingYardId,
        shuntingYardName,
      });
    }

    const record = this.passports.getById(this.id);
    const tabCategory: GatehouseSchedulerTab = this.helper.mapPassportToTabCategory(
      details?.statusCode ?? record?.statusCode,
      shuntingYardId,
      details?.transloadingStationID ?? null,
      undefined,
      record?.transferFlag ?? details?.transferFlag
    );
    this.ui.setSchedulerTabSelected(tabCategory);
    this.updateGatehouseReturnTab(tabCategory);

    this.passports.upsert({
      id: this.id,
      status: record?.status ?? 'pending',
      isSynced: record?.isSynced ?? true,
      tabCategory,
      statusCode: details?.statusCode ?? record?.statusCode,
      transferFlag: record?.transferFlag ?? details?.transferFlag,
    });
  }

  private updateGatehouseReturnTab(target: GatehouseSchedulerTab): void {
    const snapshot = parseGatehouseListSnapshot();
    if (!snapshot) {
      return;
    }
    saveGatehouseListSnapshot({ ...snapshot, tab: target });
  }

  private buildGatehouseFormData(): FormData {
    const formData = new FormData();
    const shuntingYardId = this.selectedShuntingYardId;

    // If shunting yard is selected, send the ID; if "No Shunting Required", send empty string to clear it
    if (shuntingYardId !== null && shuntingYardId !== undefined) {
      formData.append('Request.ShuntingYardID', shuntingYardId.toString());
      console.log('📤 Gatehouse submitting with shunting yard ID:', shuntingYardId);
    } else {
      // Send empty string to indicate clearing the shunting yard (No Shunting Required)
      formData.append('Request.ShuntingYardID', '');
      console.log('📤 Gatehouse submitting with No Shunting Required (clearing shunting yard)');
    }

    return formData;
  }

  private isStaleInvoicedGatehouseError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse
      && error.status === 400
      && error.error?.error === 'stale_invoiced_load';
  }

  private getAdvanceTargetRoute(): string {
    if (this.isShunting) {
      return '/shunting';
    }
    return '/profile';
  }

  private getAdvancePassportErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message ?? error.message ?? 'Failed to advance passport';
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Failed to advance passport';
  }

  ngOnDestroy(): void {
    // Clean up timer to prevent memory leaks
    if (this.haccpErrorTimer) {
      clearTimeout(this.haccpErrorTimer);
      this.haccpErrorTimer = undefined;
    }
    // Restore body scroll if preview is open
    if (this.haccpImagePreviewVisible) {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.handlePreviewKeydown);
    }
    if (this.previewImageSrc?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewImageSrc);
    }
    this.previewImageSrc = null;
    // Revoke all active blob URLs to free browser memory
    const currentImages = this.store.get(this.id)?.haccpImages || [];
    for (const img of currentImages) {
      if (img?.startsWith('blob:')) {
        URL.revokeObjectURL(img);
      }
    }
    // Clear display URLs from the store so the next visit reloads from backend links only.
    // Skip when navigating to mobile comments/history — user returns with in-progress edits.
    const isReturningFromMobilePanel = !!sessionStorage.getItem(this.getMobilePanelNavKey());
    if (!isReturningFromMobilePanel && currentImages.length > 0) {
      this.store.update(this.id, { haccpImages: [] });
    }
    this.proxyUrlMap.clear();
    this.imageBlobCache.clear();
    this.lastLoadedLinksKey = '';
    this.imageLoadInProgress = false;
    // Clear original snapshot for offline history to free memory
    // BUT skip if we're navigating to mobile comments/history (will return to this passport)
    if (this.id && !sessionStorage.getItem(this.getMobilePanelNavKey())) {
      this.offlineHistoryService.clearOriginalSnapshot(this.id);
    }
  }

  trackBySrc(_: number, src: string): string { return src; }

  /**
   * Get locked fields for the top section (always visible)
   */
  get topLockedFields() {
    return [
      { label: 'Load Number', value: this.store.get(this.id)?.loadNumber || '' },
      { label: 'Scheduled Date & Time', value: (this.store.get(this.id)?.dueDateTime || this.store.get(this.id)?.dateTime) ? (this.formatDate(this.store.get(this.id)?.dueDateTime || this.store.get(this.id)?.dateTime)) : '-' },
      { label: 'Variety', value: this.store.get(this.id)?.variety || '-' },
      { label: 'Vendor Name', value: this.store.get(this.id)?.vendorName || '' },
      { label: 'Vendor Number', value: this.store.get(this.id)?.vendorNumber || '' },
      { label: 'APN/Name', value: this.formattedApn },
      { label: 'Haulier', value: this.store.get(this.id)?.haulier || '' },
      { label: 'Destination', value: this.store.get(this.id)?.destinationWeighbridge || this.store.get(this.id)?.destination || '' }
    ];
  }

  /**
   * Bulker for read-only display — actual Grower/Transloader users: detail value; others: trailerMain preferred.
   */
  get displayBulkerNumber(): string {
    return this.helper.getDisplayBulker(this.store.get(this.id), !this.isActualGrowerOrTransloaderUser);
  }

  /**
   * Get locked fields for Gatehouse persona in edit mode
   */
  get gatehouseViewFields() {
    return [
      { label: 'Loading Date and Time', value: this.loadingFormatted },
      { label: 'Bulker Number', value: this.displayBulkerNumber },
      { label: 'HACCP Check', value: this.computedHaccpCheck },
      { label: 'Authorised By', value: this.store.get(this.id)?.authorisedBy || '' }
    ];
  }

  /**
   * Get locked fields for view-only mode
   */
  get viewOnlyFields() {
    return [
      { label: 'Origin', value: this.store.get(this.id)?.origin || this.NO_ORIGIN_FIELD_TEXT },
      { label: 'Loading Date and Time', value: this.loadingFormatted },
      { label: 'Bulker Number', value: this.displayBulkerNumber },
      { label: 'HACCP Check', value: this.computedHaccpCheck },
      { label: 'Authorised By', value: this.store.get(this.id)?.authorisedBy || '' }
    ];
  }

  /**
   * Format date for display (converts UTC to local time)
   */
  private formatDate(dateStr: string | undefined): string {
    return this.dateTimeHelper.formatDateForDisplay(dateStr);
  }

  trackByLabel(_: number, field: { label: string; value: string }): string {
    return field.label;
  }
}




